import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatQuantity, formatQuantityParts } from '../utils/unitDisplay';
import { FiSearch, FiPlus, FiMinus, FiX, FiUser, FiCamera } from 'react-icons/fi';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Check } from 'lucide-react';
import { io } from 'socket.io-client';
import './StockOutForm-module.css';
import { uploadWastagePhotos } from '../services/wastageService';
import wastageReasonEventService from '../services/wastageReasonEventService';
import { API_BASE_URL } from '../services/index';
import { createSubmissionLedger } from '../utils/submissionLedger';
import http from '../services/httpClient';
import { postWithLockedRetry } from '../utils/ccsLockedRetry';
import UnitMappingService from '../services/unitMappingService';
import RecipesService from '../services/recipesService';
import Usage from './Usage';
import { StandardSearch, StandardScrollStyles, smartSearch, highlightMatch, defaultTextSizes, standardScrollStyles } from '../styles/standardStyles';
import Counter from './Counter';
import WastageDatePicker from './WastageDatePicker';
import { isWastageDateDisabled } from '../utils/wastageDateBounds';
import { resolveWastageSeedDate, shouldAdoptSuggestedDate } from '../utils/wastageDateSeed';
import { buildRawWastageItem, resolveRowUnitId, NON_CONSUMABLE_CATEGORY } from '../utils/wastageItemMapping';
import { pieceWastageItemIds, piecePackLines, packetsSentence } from '../utils/piecePackReadback';
// Banquet Wastage — the pure rules live in utils/banquetWastageForm.js.
import BanquetEventPicker from './BanquetEventPicker';
import { fetchWastageEvents } from '../utils/banquetApi';
import {
    isBanquetReason, findBanquetEvent, eventWindow, banquetItemFields, banquetRefusalMessage,
    isBanquetRefusalCode, sumEstimatedCost, banquetSuccessMessage, banquetWarningMessages,
    formatShortDay, BANQUET_MESSAGES,
    functionItemKeys, keysOutsideFunction, functionDishesNote, emptyFunctionMessage,
    removedItemsNotice, PICK_FUNCTION_FIRST,
} from '../utils/banquetWastageForm';
// The client half of the shortage identity contract — see that module for why a
// confirmation echoes the server's own match_keys instead of a rebuilt label.
import { shortageMatchTokens, buildShortageSelectionKey, buildShortageItemScope } from '../utils/shortageIdentity';
import { useTranslate } from '../translations/useTranslate';
import { useAuth } from '../contexts/AuthContext';


/**
 * Today as an IST calendar day, 'YYYY-MM-DD'.
 *
 * Module scope on purpose: it is referenced by a useState initialiser and by effects,
 * and a function redefined every render would either have to be a dependency or be
 * silently stale. The business day is IST everywhere in this codebase.
 */
const istToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

/** The banquet-event dropdown before (or without) a load. One frozen object, so resetting to it twice is a no-op. */
const NO_BANQUET_EVENTS = Object.freeze({ status: 'idle', events: [], range: null, error: null });

// Canonical, app-wide display: full names (Gram/Kilogram/Millilitre/Litre/Pcs), auto-scaled.
const formatValueWithUnit = (val, originalUnit) => {
  const parts = formatQuantityParts(Number(val), originalUnit);
  return { qty: parts.qty, unit: parts.unit };
};

// Searchable Select Component (same as TSR)
const SearchableSelect = ({ value, onChange, options = [], placeholder = 'Search unit...', searchPlaceholder, disabled = false, className = '', style = {} }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [openUpward, setOpenUpward] = useState(false);
  const wrapRef = useRef(null);
  const controlRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (open && controlRef.current) {
      const rect = controlRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 250; // approximate height with search + options
      
      setOpenUpward(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
    }
  }, [open]);

  const selected = options.find(o => o.value === value);
  const filtered = options.filter(o =>
    (o.label || o.value || '').toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div ref={wrapRef} className={`substitute-searchable-select ${className}`} style={style}>
      <div
        ref={controlRef}
        className="substitute-searchable-control"
        onClick={() => !disabled && setOpen(!open)}
      >
        <span>{selected ? (selected.label || selected.value) : placeholder}</span>
        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
      {open && (
        <div className={`substitute-searchable-menu ${openUpward ? 'upward' : ''}`}>
          <input
            type="text"
            placeholder={searchPlaceholder || placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="substitute-searchable-input"
            autoFocus
          />
          <div className="substitute-searchable-options">
            {filtered.map((opt) => (
              <div
                key={opt.value}
                className={`substitute-searchable-option ${opt.value === value ? 'selected' : ''}`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                  setQuery('');
                }}
              >
                {opt.label || opt.value}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="substitute-searchable-option disabled">No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Utility function to convert unit names to short forms
const getShortUnit = (unit) => UnitMappingService.normalizeUnitSymbol(unit);

const formatShortQty = (value, fallback = '0') => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    const rounded = Math.round(parsed * 100) / 100;
    // Never collapse a real, non-zero quantity to "0". A tiny value like 0.004
    // (e.g. an ingredient stored in kg that is really a few grams) rounds to 0
    // at 2 decimals and misleadingly reads as "Required: 0". Fall back to the
    // first two significant figures so the value stays visible (0.004, 0.0012…).
    if (rounded === 0 && parsed !== 0) {
        return String(Number(parsed.toPrecision(2)));
    }
    const formatted = rounded.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
    return formatted;
};

// Using centralized API_BASE_URL for display only; requests go through http
// Regular Modal for selection lists
const Modal = ({ children, onClose, contentStyle = {}, contentClassName = '', overlayClassName = '' }) => (
    <div className={`stock-out-modal-overlay ${overlayClassName}`.trim()} onClick={onClose}>
        <div
            className={`stock-out-modal-content ${contentClassName}`.trim()}
            onClick={e => e.stopPropagation()}
            style={{ margin: '20px', ...contentStyle }}
        >
            {children}
        </div>
    </div>
);

// Success Modal with animation
const SuccessModal = ({ show, message, onClose }) => {
    if (!show) return null;

    return (
        <div className="stock-out-modal-overlay" onClick={onClose}>
            <div className="stock-out-success-modal-box" onClick={e => e.stopPropagation()}>
                <FontAwesomeIcon icon={faCheck} className="checkmark green" />
                <p>{message}</p>
                <button onClick={onClose} className="ok-btn">OK</button>
            </div>
        </div>
    );
};

// Ingredient Shortage Modal - Professional Error Display with TSR-style functionality
const IngredientShortageModal = ({ show, data, onClose, onAddStock, onRemoveFromRecipe, selectedItems, onToggleItem }) => {
    if (!show) return null;

    // Safely extract data with fallbacks
    const itemName = data?.itemName || 'Unknown Item';
    const missingIngredients = Array.isArray(data?.missingIngredients) 
        ? data.missingIngredients 
        : [];
    
    // Debug logging
    console.log('🎨 Modal rendering with data:', {
        show,
        itemName,
        missingIngredients,
        fullData: data,
        isArray: Array.isArray(missingIngredients)
    });

    const buildSelectionKey = (ingredient) =>
        buildShortageSelectionKey(ingredient, { itemName, menuItemId: data?.menuItemId });

    const missingKeys = new Set(missingIngredients.map(buildSelectionKey));
    const selectedCount = Object.keys(selectedItems || {})
        .filter(k => selectedItems[k] && missingKeys.has(k)).length;
    const totalCount = missingIngredients?.length || 0;
    const allSelected = totalCount > 0 && selectedCount === totalCount;

    return (
        <div className="stock-out-modal-overlay" onClick={onClose}>
            <div className="ingredient-shortage-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="shortage-modal-header">
                    <div className="shortage-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h2>Cannot Waste Item</h2>
                    <p className="item-name-subtitle">Insufficient ingredients for <strong>{itemName}</strong></p>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>

                {/* Missing Ingredients List */}
                <div className="shortage-modal-body">
                    <button 
                        className="select-all-btn"
                        onClick={() => {
                            if (allSelected) {
                                // Deselect all
                                missingIngredients?.forEach(ing => {
                                    const key = buildSelectionKey(ing);
                                    if (selectedItems && selectedItems[key]) {
                                        onToggleItem && onToggleItem(key);
                                    }
                                });
                            } else {
                                // Select all
                                missingIngredients?.forEach(ing => {
                                    const key = buildSelectionKey(ing);
                                    if (!selectedItems || !selectedItems[key]) {
                                        onToggleItem && onToggleItem(key);
                                    }
                                });
                            }
                        }}
                    >
                        {allSelected ? 'Deselect All' : 'Select All'}
                    </button>
                    <div className="ingredients-list">
                        {missingIngredients && missingIngredients.length > 0 ? (
                            missingIngredients.map((ing, index) => {
                                console.log(` Ingredient ${index}:`, ing);
                                
                                // Backend returns objects with: {name, required, available, shortage, unit}
                                const name = ing.name || 'Unknown Ingredient';
                                const required = formatShortQty(ing.required);
                                const available = formatShortQty(ing.available);
                                const shortage = formatShortQty(ing.shortage);
                                const unit = getShortUnit(ing.unit || '');
                                const key = buildSelectionKey(ing);
                                
                                return (
                                    <div key={index} className="missing-ingredient-row">
                                        <div className="ingredient-left">
                                            <span className="ingredient-name">{name}</span>
                                            <span className="ingredient-details">
                                                Required: {required} {unit} | Available: {available} {unit}
                                            </span>
                                        </div>
                                        <div className="ingredient-right">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(selectedItems && selectedItems[key])}
                                                onChange={() => onToggleItem && onToggleItem(key)}
                                                className="ingredient-checkbox"
                                            />
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="no-ingredients">
                                <p>No ingredient details available</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="shortage-modal-footer modal-footer-stacked">
                    <button 
                        className="modal-btn modal-btn-negative" 
                        onClick={onAddStock}
                        disabled={selectedCount === 0}
                    >
                        Confirm & Create Negative Stock
                    </button>

                    <button 
                        className="modal-btn modal-btn-remove"
                        onClick={onRemoveFromRecipe}
                        disabled={selectedCount === 0}
                    >
                        Remove and Update Recipe
                    </button>
                </div>
            </div>
        </div>
    );
};

// TSR Validation Error Modal - Enhanced to display multiple items
const TSRValidationErrorModal = ({ show, data, onClose }) => {
    if (!show || !data) return null;

    const { message, details, title, items, allItems } = data;

    const formatNoTsrMessage = (rawMessage) => {
        if (!rawMessage) {
            return 'No TSR (Total Sales Report) entries were found for the selected date. Bill Error can be used only after sales are recorded for that day.';
        }

        return rawMessage
            .replace('No TSR (Total Sales Report) entries found for', 'No TSR (Total Sales Report) entries were found for')
            .replace('Bill Error can only be used when sales have been recorded for that day.', 'Bill Error can be used only after sales are recorded for that day.');
    };
    
    // Support both single item (details) and multiple items (items/allItems)
    const errorItems = items || allItems || (details ? [details] : []);
    const hasMultipleItems = errorItems.length > 1;
    const singleDetails = errorItems.length === 1 ? errorItems[0] : details;
    
    // Determine reason codes from first item or details
    const reasonCode = singleDetails?.reason_code || details?.reason_code;
    const isNoTSR = reasonCode === 'TSR_NOT_FOUND';
    const isItemNotInTSR = reasonCode === 'ITEM_NOT_IN_TSR';
    const isQuantityExceeds = reasonCode === 'QUANTITY_EXCEEDS_TSR';

    return (
        <div className="stock-out-modal-overlay" onClick={onClose}>
            <div className="tsr-error-modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <button className="modal-close" onClick={onClose}>✕</button>
                
                <div className="tsr-error-header">
                    <h2>{title || 'Bill Error Not Allowed'}</h2>
                </div>

                <div className="tsr-error-body">
                    {/* Display NO TSR message if applicable */}
                    {isNoTSR && (
                        <div className="tsr-error-details">
                            <p style={{ padding: '12px', backgroundColor: 'transparent', borderRadius: '8px', color: '#000000' }}>
                                {formatNoTsrMessage(message)}
                            </p>
                        </div>
                    )}
                    
                    {/* Display all affected items */}
                    {!isNoTSR && errorItems.length > 0 && (
                        <div className="tsr-error-details">
                            {errorItems.map((item, index) => (
                                <div key={index} >
                                    {/* Item Name */}
                                    {item.item_name && (
                                        <div className="tsr-detail-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                            <div className="tsr-detail-label">Item Name</div>
                                            <div className="tsr-detail-value">{item.item_name}</div>
                                        </div>
                                    )}

                                    {/* Quantity comparison for QUANTITY_EXCEEDS_TSR */}
                                    {(isQuantityExceeds || item.reason_code === 'QUANTITY_EXCEEDS_TSR') && (
                                        <div className="tsr-detail-card" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                            <div className="tsr-detail-content" style={{ flex: 1 }}>
                                                <div className="tsr-detail-label">Wastage Qty</div>
                                                <div className="tsr-detail-value">{item.wastage_quantity}</div>
                                            </div>
                                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ef4444' }}>{'>'}</div>
                                            <div className="tsr-detail-content" style={{ flex: 1 }}>
                                                <div className="tsr-detail-label">Sold Qty (TSR)</div>
                                                <div className="tsr-detail-value">{item.sold_quantity}</div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Show reason for ITEM_NOT_IN_TSR */}
                                    {(isItemNotInTSR || item.reason_code === 'ITEM_NOT_IN_TSR') && !isQuantityExceeds && (
                                        <p style={{ fontSize: '13px', color: '#dc2626', marginTop: '4px' }}>
                                            Not sold on selected date
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {/* Fallback for legacy single item display */}
                    {!isNoTSR && errorItems.length === 0 && details && (
                        <div className="tsr-error-details">
                            {(isItemNotInTSR || isQuantityExceeds) && details.item_name && (
                                <div className="tsr-detail-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div className="tsr-detail-label">Item Name</div>
                                    <div className="tsr-detail-value">{details.item_name}</div>
                                </div>
                            )}

                            {isQuantityExceeds && (
                                <div className="tsr-detail-card" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <div className="tsr-detail-content" style={{ flex: 1 }}>
                                        <div className="tsr-detail-label">Wastage Qty</div>
                                        <div className="tsr-detail-value">{details.wastage_quantity}</div>
                                    </div>
                                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ef4444' }}>{'>'}</div>
                                    <div className="tsr-detail-content" style={{ flex: 1 }}>
                                        <div className="tsr-detail-label">Sold Qty (TSR)</div>
                                        <div className="tsr-detail-value">{details.sold_quantity}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="tsr-error-footer" style={{ display: 'flex', gap: '12px' }}>
                    <button className="tsr-error-btn-secondary" onClick={onClose} style={{ flex: 1 }}>
                        Back
                    </button>
                    <button className="tsr-error-btn-primary" onClick={onClose} style={{ flex: 1 }}>
                        Got It
                    </button>
                </div>
            </div>
        </div>
    );
};

// Image component with multiple fallback handling
const ImageWithFallback = ({ src, alt, fallback, placeholder, ...props }) => {
    const [imgSrc, setImgSrc] = useState(src);
    const [errorCount, setErrorCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    const handleError = (e) => {
        console.log(`🚫 Image error for ${alt}:`, {
            currentSrc: imgSrc,
            errorCount,
            naturalWidth: e.target.naturalWidth,
            naturalHeight: e.target.naturalHeight
        });

        if (errorCount === 0 && fallback && fallback !== imgSrc) {
            console.log(`🔄 Trying fallback for ${alt}: ${fallback}`);
            setImgSrc(fallback);
            setErrorCount(1);
        } else if (errorCount === 1 && placeholder && placeholder !== imgSrc) {
            console.log(`🔄 Trying placeholder for ${alt}: ${placeholder}`);
            setImgSrc(placeholder);
            setErrorCount(2);
        } else {
            console.log(`❌ All fallbacks failed for ${alt}, creating canvas placeholder`);
            // Last-resort placeholder: plain white, no text — owner preference
            // (2026-08-26). Imageless items render as a clean white tile
            // everywhere; the dish-name label under the card carries identity.
            const canvas = document.createElement('canvas');
            canvas.width = 300;
            canvas.height = 200;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, 300, 200);
            setImgSrc(canvas.toDataURL());
            setErrorCount(3);
        }
        setIsLoading(false);
    };

    const handleLoad = (e) => {
        setIsLoading(false);
        console.log(`✅ Image loaded successfully for ${alt}:`, {
            src: imgSrc,
            naturalWidth: e.target.naturalWidth,
            naturalHeight: e.target.naturalHeight
        });
    };

    useEffect(() => {
        console.log(`🔄 ImageWithFallback useEffect for ${alt}:`, { src, fallback, placeholder });
        setImgSrc(src);
        setErrorCount(0);
        setIsLoading(true);
    }, [src, alt]);

    return (
        <img
            src={imgSrc}
            alt={alt}
            onError={handleError}
            onLoad={handleLoad}
            {...props}
            style={{
                ...props.style,
                backgroundColor: isLoading ? '#f8f9fa' : 'transparent',
                opacity: 1,
                transition: 'opacity 0.3s ease'
            }}
        />
    );
};

const StockOutForm = ({ initialSubTab, goTo, onLockStateChange, recoveryDate, suggestedDate = null, openReasonModal = false, onReasonModalOpened = null }) => {
    // Get current authenticated user and businessId from AuthContext
    const { user: currentUser, businessId } = useAuth();

    // Determine if the logged-in user is Owner/Admin
    // If they have a specific role (Manager, Chef, Waiter) and their own credentials,
    // we auto-assign them as the staff and skip the "Select Staff" modal.
    const isCurrentUserOwner = useMemo(() => {
        if (!currentUser) return true; // Default to owner behavior if no user
        // Check isOwner flag from login response
        if (currentUser.isOwner === true) return true;
        // Fallback: check roleName
        const roleName = (currentUser.roleName || currentUser.role_name || '').toLowerCase();
        if (roleName.includes('owner') || roleName.includes('admin')) return true;
        return false;
    }, [currentUser]);

    // Check localStorage for initial sub-tab (from Chef Dashboard wastage button)
    const getInitialSubTab = () => {
        const storedSubTab = localStorage.getItem('stockOutInitialSubTab');
        console.log('🗑️ StockOutForm: Reading initial sub-tab from localStorage:', storedSubTab);
        if (storedSubTab) {
            localStorage.removeItem('stockOutInitialSubTab'); // Clear it after reading
            console.log('🗑️ StockOutForm: Using stored sub-tab:', storedSubTab);
            return storedSubTab;
        }
        const finalTab = initialSubTab || 'Usage'; // Changed default from 'Waste' to 'Usage'
        console.log('🗑️ StockOutForm: Using default sub-tab:', finalTab);
        return finalTab;
    };
    
    // Translation hook
    const { t, language } = useTranslate();
    
    // Helper function to translate category names
    const translateCategory = (category) => {
        if (category === 'All') return t('stockOut.all');
        if (category === 'Raw Items') return t('stockOut.raw_items');
        if (category === 'Main Course') return t('stockOut.main_course');
        if (category === 'Preparations') { const v = t('stockOut.preparations'); return (v && v !== 'stockOut.preparations') ? v : 'Preparations'; }
        // For other categories from database, return as-is for now
        return category;
    };
    
    // Helper function to translate waste reason labels
    const translateWasteReason = (reasonLabel) => {
        if (!reasonLabel) return '';
        // Try to get translation from wasteReasons object
        const translationKey = `wasteReasons.${reasonLabel}`;
        const translated = t(translationKey);
        // If translation exists and is different from the key, return it
        if (translated && translated !== translationKey) {
            return translated;
        }
        // Otherwise return the original label
        return reasonLabel;
    };
    
    // Helper function to translate waste category/description
    const translateWasteCategory = (category) => {
        if (!category) return '';
        const translationKey = `wasteReasons.${category}`;
        const translated = t(translationKey);
        if (translated && translated !== translationKey) {
            return translated;
        }
        return category;
    };
    
    // State for UI
    const [activeSubTab, setActiveSubTab] = useState(getInitialSubTab());
    const [searchTerm, setSearchTerm] = useState('');

    // ── The day this wastage is being recorded FOR ───────────────────────────
    //
    // Seeded from `recoveryDate` when the TSR-lock or TSR-page recovery flow sent one,
    // otherwise from the server's clock-free filing date, otherwise today.
    //
    // The middle rung is the fix for wastage landing on the NEXT day. `recoveryDate`
    // is fed from the lock's date, which is null for the whole 00:00–02:00 grace
    // window — exactly when a late-closing restaurant files — so the form used to
    // open on the new calendar day and stamp last night's wastage / NC on tomorrow
    // (282 of 318 after-midnight records, measured live). See utils/wastageDateSeed.js.
    const [operationDate, setOperationDate] = useState(
        () => resolveWastageSeedDate({ recoveryDate, suggestedDate, today: istToday() }).date
    );
    // True once the owner has chosen a day themselves. A deliberate choice is never
    // overridden by a suggestion that arrives late.
    const userPickedDateRef = useRef(false);
    // The suggestion is adopted at most once; a later today-status poll must not
    // yank the date around underneath somebody mid-entry.
    const suggestionAppliedRef = useRef(false);
    // Set when the form opened on (or moved to) a BACKLOG day on its own, so the
    // screen can say so. A silent default is how today's wastage gets filed
    // against yesterday without anyone noticing.
    const [autoBacklogDate, setAutoBacklogDate] = useState(
        () => {
            const seed = resolveWastageSeedDate({ recoveryDate, suggestedDate, today: istToday() });
            return seed.source === 'suggested' ? seed.date : null;
        }
    );

    /** The picker's onChange: a choice made by a person, not by the form. */
    const handleUserPickedDate = (next) => {
        userPickedDateRef.current = true;
        setAutoBacklogDate(null);
        setOperationDate(next);
    };
    // How far back the ledger is closed. null until /wastage/date-bounds answers, and
    // null means NO floor — never today. See utils/wastageDateBounds.js.
    const [reconciledThrough, setReconciledThrough] = useState(null);
    // In a recovery flow the date is the whole point of the navigation, so it is fixed.
    const dateIsFixed = Boolean(recoveryDate);

    // The recovery flows navigate INTO this screen with a date; honour a change of prop.
    useEffect(() => {
        if (recoveryDate) {
            setOperationDate(recoveryDate);
            setAutoBacklogDate(null);
        }
    }, [recoveryDate]);

    // Adopt the server's filing date when it arrives. App.js fetches it from
    // today-status, which usually resolves AFTER this form has mounted on today —
    // the same late arrival the TSR screen handles, with the same guards: one shot,
    // never over a deliberate choice, never in a recovery flow, never into a closed
    // period.
    useEffect(() => {
        const adopt = shouldAdoptSuggestedDate({
            suggestedDate,
            today: istToday(),
            currentDate: operationDate,
            recoveryDate,
            userPicked: userPickedDateRef.current,
            alreadyApplied: suggestionAppliedRef.current,
            reconciledThrough,
        });
        // Mark it applied as soon as a suggestion has been SEEN, adopted or not —
        // "one shot" means one look, not one success.
        if (suggestedDate) suggestionAppliedRef.current = true;
        if (!adopt) return;
        setOperationDate(suggestedDate);
        setAutoBacklogDate(suggestedDate !== istToday() ? suggestedDate : null);
        // operationDate / reconciledThrough are read, not watched: this must fire
        // when the SUGGESTION changes, not every time the date does.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestedDate]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await http.get('/wastage/date-bounds');
                if (cancelled || !res?.success) return;
                const watermark = res.data?.reconciled_through || null;
                setReconciledThrough(watermark);

                // If the seeded date turns out to sit inside a closed period, move to
                // today rather than leaving a date the server will refuse at submit.
                // Only when the user has not been sent here FOR that date.
                if (!recoveryDate) {
                    setOperationDate((current) => {
                        const parts = String(current).split('-').map(Number);
                        if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) {
                            return istToday();
                        }
                        const seeded = new Date(parts[0], parts[1] - 1, parts[2]);
                        if (isWastageDateDisabled(seeded, { reconciledThrough: watermark })) {
                            // The backlog day we opened on turned out to be closed, so
                            // the hint announcing it would now describe a date the form
                            // is no longer on.
                            setAutoBacklogDate(null);
                            return istToday();
                        }
                        return current;
                    });
                }
            } catch (e) {
                // Non-fatal: the server enforces the same rule at submit. A failed
                // bounds fetch must not stop somebody recording today's wastage.
                console.warn('Could not load wastage date bounds:', e?.message || e);
            }
        })();
        return () => { cancelled = true; };
    }, [recoveryDate]);
    const searchInputRef = useRef(null);
    const [activeCategory, setActiveCategory] = useState('All');
    const [isItemGridScrolling, setIsItemGridScrolling] = useState(false);
    const [itemGridThumb, setItemGridThumb] = useState({ top: 0, height: 0, hasOverflow: false });
    const itemGridRef = useRef(null);
    const itemGridScrollTimeoutRef = useRef(null);
    const [wastedItems, setWastedItems] = useState({});
    const [notes, setNotes] = useState('');
    const [photos, setPhotos] = useState([]);
    const fileInputRef = useRef(null);

    // State for API data
    const [menuItems, setMenuItems] = useState([]);
    const [menuItemsWithRecipeCost, setMenuItemsWithRecipeCost] = useState([]);
    const [availablePlates, setAvailablePlates] = useState({});
    const [wasteReasons, setWasteReasons] = useState([]);
    const [staffMembers, setStaffMembers] = useState([]);
    const [categories, setCategories] = useState(['All']);
    const [dishCategories, setDishCategories] = useState([]);
    const [extraCategories, setExtraCategories] = useState([]);
    const [useMasterCategories, setUseMasterCategories] = useState(() => {
        const saved = localStorage.getItem('useMasterCategories');
        return saved !== null ? JSON.parse(saved) : true;
    });

    // 2-line overflow detection and View all toggle
    const categoryChipsRef = useRef(null);
    const categoryMeasureRef = useRef(null);
    const [showAllCategoryChips, setShowAllCategoryChips] = useState(false);
    const [hasCategoryChipOverflow, setHasCategoryChipOverflow] = useState(false);
    const [collapsedDishCount, setCollapsedDishCount] = useState(0);

    const isCategoryCollapsed = hasCategoryChipOverflow && !showAllCategoryChips;
    const visibleDishOptions = isCategoryCollapsed
        ? dishCategories.slice(0, collapsedDishCount)
        : dishCategories;

    useEffect(() => {
        setShowAllCategoryChips(false);
    }, [dishCategories, extraCategories]);

    useEffect(() => {
        const measureCategoryRows = () => {
            const measureContainer = categoryMeasureRef.current;
            if (!measureContainer) return;

            const allChip = measureContainer.querySelector('[data-measure-all="true"]');
            const dishChips = Array.from(measureContainer.querySelectorAll('[data-measure-dish="true"]'));
            const extraChips = Array.from(measureContainer.querySelectorAll('[data-measure-extra="true"]'));
            const viewAllChip = measureContainer.querySelector('[data-measure-view-all="true"]');

            if (!allChip || !dishChips.length || !viewAllChip) {
                setHasCategoryChipOverflow(false);
                setCollapsedDishCount(dishCategories.length);
                return;
            }

            const getRowCount = (elements) => {
                const rowTops = [];
                elements.forEach((el) => {
                    if (el.style.display === 'none') return;
                    const top = Math.round(el.offsetTop);
                    if (!rowTops.some((existingTop) => Math.abs(existingTop - top) <= 2)) {
                        rowTops.push(top);
                    }
                });
                return rowTops.length;
            };

            allChip.style.display = '';
            dishChips.forEach((chip) => { chip.style.display = ''; });
            extraChips.forEach((chip) => { chip.style.display = ''; });
            viewAllChip.style.display = 'none';

            const totalRowsWithoutToggle = getRowCount([allChip, ...dishChips, ...extraChips]);
            const overflows = totalRowsWithoutToggle > 2;
            setHasCategoryChipOverflow(overflows);

            if (!overflows) {
                setCollapsedDishCount(dishChips.length);
                return;
            }

            let maxVisible = 0;
            viewAllChip.style.display = '';
            for (let count = dishChips.length; count >= 0; count -= 1) {
                dishChips.forEach((chip, idx) => {
                    chip.style.display = idx < count ? '' : 'none';
                });

                const visibleElements = [
                    allChip,
                    ...dishChips.filter((_, idx) => idx < count),
                    ...extraChips,
                    viewAllChip
                ];
                const rowsWithToggle = getRowCount(visibleElements);
                if (rowsWithToggle <= 2) {
                    maxVisible = count;
                    break;
                }
            }

            setCollapsedDishCount(maxVisible);

            dishChips.forEach((chip) => { chip.style.display = ''; });
            viewAllChip.style.display = 'none';
        };

        const frameId = window.requestAnimationFrame(measureCategoryRows);
        const timeoutId = window.setTimeout(measureCategoryRows, 80);
        window.addEventListener('resize', measureCategoryRows);
        let resizeObserver;
        if (window.ResizeObserver && categoryMeasureRef.current) {
            resizeObserver = new window.ResizeObserver(measureCategoryRows);
            resizeObserver.observe(categoryMeasureRef.current);
        }

        return () => {
            window.cancelAnimationFrame(frameId);
            window.clearTimeout(timeoutId);
            window.removeEventListener('resize', measureCategoryRows);
            if (resizeObserver) resizeObserver.disconnect();
        };
    }, [dishCategories.join('|'), extraCategories.join('|')]);

    useEffect(() => {
        if (showAllCategoryChips || !hasCategoryChipOverflow) return;

        const frameId = window.requestAnimationFrame(() => {
            const visibleContainer = categoryChipsRef.current;
            if (!visibleContainer) return;

            const visibleButtons = Array.from(visibleContainer.querySelectorAll('button'));
            const rowTops = [];
            visibleButtons.forEach((el) => {
                const top = Math.round(el.offsetTop);
                if (!rowTops.some((existingTop) => Math.abs(existingTop - top) <= 2)) {
                    rowTops.push(top);
                }
            });

            if (rowTops.length > 2 && collapsedDishCount > 0) {
                setCollapsedDishCount((prev) => Math.max(prev - 1, 0));
            }
        });

        return () => window.cancelAnimationFrame(frameId);
    }, [visibleDishOptions.length, hasCategoryChipOverflow, showAllCategoryChips, collapsedDishCount, extraCategories.length]);

    // Sync with localStorage on mount, window focus, or storage event
    useEffect(() => {
        const checkPref = () => {
            const saved = localStorage.getItem('useMasterCategories');
            const val = saved !== null ? JSON.parse(saved) : true;
            setUseMasterCategories(val);
        };
        checkPref();
        window.addEventListener('storage', checkPref);
        window.addEventListener('focus', checkPref);
        return () => {
            window.removeEventListener('storage', checkPref);
            window.removeEventListener('focus', checkPref);
        };
    }, []);
    const [rawItems, setRawItems] = useState([]);
    const [rawItemsLoaded, setRawItemsLoaded] = useState(false);
    const [preparationItems, setPreparationItems] = useState([]);
    const [preparationItemsLoaded, setPreparationItemsLoaded] = useState(false);
    const [inventoryOverview, setInventoryOverview] = useState([]);
    const [isItemsLoading, setIsItemsLoading] = useState(false);

    // State for dynamic threshold checking
    const [thresholdCheckResults, setThresholdCheckResults] = useState({});
    const [isCheckingThresholds, setIsCheckingThresholds] = useState(false);

    const updateItemGridThumb = useCallback(() => {
        const el = itemGridRef.current;
        if (!el) return;

        const { scrollTop, scrollHeight, clientHeight } = el;
        if (scrollHeight <= clientHeight) {
            setItemGridThumb({ top: 0, height: 0, hasOverflow: false });
            return;
        }

        const minThumbHeight = 24;
        const thumbHeight = Math.max((clientHeight / scrollHeight) * clientHeight, minThumbHeight);
        const maxThumbTop = clientHeight - thumbHeight;
        const scrollRange = Math.max(scrollHeight - clientHeight, 1);
        const thumbTop = (scrollTop / scrollRange) * maxThumbTop;

        setItemGridThumb({ top: thumbTop, height: thumbHeight, hasOverflow: true });
    }, []);

    const handleItemGridScroll = useCallback(() => {
        updateItemGridThumb();
        setIsItemGridScrolling(true);

        if (itemGridScrollTimeoutRef.current) {
            clearTimeout(itemGridScrollTimeoutRef.current);
        }

        // Keep scrollbar visible briefly after scroll stops.
        itemGridScrollTimeoutRef.current = setTimeout(() => {
            setIsItemGridScrolling(false);
        }, 700);
    }, [updateItemGridThumb]);

    useEffect(() => {
        return () => {
            if (itemGridScrollTimeoutRef.current) {
                clearTimeout(itemGridScrollTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const onResize = () => updateItemGridThumb();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [updateItemGridThumb]);

    // Debug: Log when availablePlates changes
    useEffect(() => {
        console.log('🔍 [PLATES STATE] ====== availablePlates state changed ======');
        console.log('🔍 [PLATES STATE] availablePlates:', availablePlates);
        console.log('🔍 [PLATES STATE] Number of items with plate info:', Object.keys(availablePlates || {}).length);
        console.log('🔍 [PLATES STATE] Keys:', Object.keys(availablePlates || {}));
    }, [availablePlates]);

    // Check thresholds dynamically based on wastage threshold table
    useEffect(() => {
        const checkAllThresholds = async () => {
            if (Object.keys(wastedItems || {}).length === 0) {
                setThresholdCheckResults({});
                return;
            }

            setIsCheckingThresholds(true);
            const results = {};

            try {
                // Check each wasted menu item
                for (const [id, qty] of Object.entries(wastedItems || {})) {
                    const item = menuItemsWithRecipeCost.find(i => i.id?.toString() === id?.toString());
                    if (!item) {
                        continue; // Skip raw items for now
                    }

                    try {
                        // Use http client instead of fetch to properly handle API base URL
                        const data = await http.post('/wastage/check-threshold', {
                            menu_item_id: id,
                            quantity: qty
                        });
                        
                        if (data.success) {
                            results[id] = {
                                photoRequired: data.photoRequired,
                                thresholdQuantity: data.thresholdQuantity,
                                enteredQuantity: data.enteredQuantity,
                                thresholdExists: data.thresholdExists
                            };
                            
                            // Only log when photo is actually required
                            if (data.photoRequired) {
                                console.log(`⚠️ Evidence photo required: ${item.name} (${qty} exceeds threshold of ${data.thresholdQuantity})`);
                            }
                        }
                    } catch (error) {
                        console.error(`❌ Error checking threshold for item ${id}:`, error.message || error);
                    }
                }

                setThresholdCheckResults(results);
            } catch (error) {
                console.error('❌ Error checking thresholds:', error);
            } finally {
                setIsCheckingThresholds(false);
            }
        };

        // Debounce threshold checking (wait 500ms after last change)
        const timeoutId = setTimeout(checkAllThresholds, 500);
        return () => clearTimeout(timeoutId);
    }, [wastedItems, menuItemsWithRecipeCost]);

    // Determine if ANY item requires evidence photo
    const requiresEvidencePhoto = useMemo(() => {
        const required = Object.values(thresholdCheckResults).some(result => result.photoRequired === true);
        // Only log if photo is actually required to reduce console noise
        if (required && Object.keys(thresholdCheckResults).length > 0) {
            console.log('📸 Evidence photo required for wastage threshold');
        }
        return required;
    }, [thresholdCheckResults]);

    // Identify the first item that requires evidence
    const evidenceTargetMenuItemId = useMemo(() => {
        for (const [id, result] of Object.entries(thresholdCheckResults)) {
            if (result.photoRequired) {
                return id;
            }
        }
        return null;
    }, [thresholdCheckResults]);

    // State for selections
    const [wasteReason, setWasteReason] = useState(null);
    const [recordedBy, setRecordedBy] = useState(null);

    // Auto-assign staff for non-owner users:
    // When a staff member (Manager/Chef/Waiter) logs in with their own credentials,
    // automatically set them as the recordedBy user so the "Select Staff" modal is skipped.
    // For Owner/Admin users, also auto-assign themselves so they don't need the staff modal.
    // We build the recordedBy object directly from currentUser (login response data)
    // instead of depending on the staffMembers list (which requires view_users permission).
    useEffect(() => {
        if (currentUser && !recordedBy) {
            const userId = currentUser.id || currentUser.user_id || currentUser.userId;
            const userName = currentUser.name || currentUser.email || 'Staff';
            const userRole = currentUser.roleName || currentUser.role_name || (isCurrentUserOwner ? 'Owner/Admin' : 'Staff');
            
            console.log(`✅ Auto-assigning staff from login context: ${userName} (${userRole})`);
            setRecordedBy({
                user_id: userId,
                id: userId,
                name: userName,
                role_name: userRole,
                email: currentUser.email
            });
        }
    }, [isCurrentUserOwner, currentUser, recordedBy]);

    // State for modals
    const [isReasonModalOpen, setReasonModalOpen] = useState(false);
    const [isStaffModalOpen, setStaffModalOpen] = useState(false);
    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
    // PIECE GUARD on the way out (utils/piecePackReadback.js): the Stock In answer per
    // `pcs` item ({ [itemId]: profile | null }), and the packet read-back shown after the
    // final Confirm — null when it is not showing.
    const [piecePackProfiles, setPiecePackProfiles] = useState({});
    const [piecePackReview, setPiecePackReview] = useState(null);
    const [piecePackChecking, setPiecePackChecking] = useState(false);
    const piecePackCheckingRef = useRef(false);
    // Every open and every close of the confirmation modal is a new session. A check
    // that awaited the answer goes on only in the session it started in: a modal
    // cancelled meanwhile — even one opened again since — must not then submit.
    const confirmSessionRef = useRef({ open: false, id: 0 });
    if (confirmSessionRef.current.open !== isConfirmModalOpen) {
        confirmSessionRef.current = { open: isConfirmModalOpen, id: confirmSessionRef.current.id + 1 };
    }
    const [showPhotoNudge, setShowPhotoNudge] = useState(false);
    const [plateDetailModal, setPlateDetailModal] = useState({
        open: false,
        loading: false,
        item: null,
        platesInfo: null,
        summary: null,
        breakdown: [],
        error: null
    });

    // Preparation quantity modal state
    const [prepQuantityModal, setPrepQuantityModal] = useState({ open: false, item: null, quantity: '', selectedUnit: '' });
    const [prepUnitDropdownOpen, setPrepUnitDropdownOpen] = useState(false);
    // Track which display unit was last chosen per preparation item (for card display)
    const [prepDisplayUnits, setPrepDisplayUnits] = useState({});

    // Unit conversion helpers for preparations & raw materials.
    // Weight (g/kg) and volume (ml/L) each form a family. Whatever an item's
    // standard unit is, the modal lets the user enter the quantity in either
    // member of its family and converts back to the item's standard (base)
    // unit before storing it in `wastedItems`, so the backend is unaffected.
    // `factor` = how many of the family's small base unit (g / ml) one of
    // this unit equals (e.g. 1 kg = 1000 g).
    const PREP_UNIT_FAMILIES = {
        g:  { family: 'weight', factor: 1 },
        kg: { family: 'weight', factor: 1000 },
        ml: { family: 'volume', factor: 1 },
        L:  { family: 'volume', factor: 1000 },
    };
    // Normalize any spelling/casing to a canonical symbol we understand.
    // Unknown / count units (e.g. pcs) are returned untouched (no dropdown).
    const normalizePrepUnit = (symbol) => {
        if (!symbol) return symbol;
        const s = String(symbol).trim().toLowerCase();
        if (['g', 'gm', 'gms', 'gram', 'grams'].includes(s)) return 'g';
        if (['kg', 'kgs', 'kilo', 'kilos', 'kilogram', 'kilograms'].includes(s)) return 'kg';
        if (['ml', 'milliliter', 'millilitre', 'milliliters', 'millilitres'].includes(s)) return 'ml';
        if (['l', 'ltr', 'litre', 'liter', 'litres', 'liters'].includes(s)) return 'L';
        return symbol;
    };
    // All selectable units for an item's standard unit = its whole family, small-first.
    const getAvailableUnits = (baseUnitSymbol) => {
        const info = PREP_UNIT_FAMILIES[normalizePrepUnit(baseUnitSymbol)];
        if (!info) return [baseUnitSymbol];
        return Object.keys(PREP_UNIT_FAMILIES).filter(u => PREP_UNIT_FAMILIES[u].family === info.family);
    };
    // Convert an entered value (in `fromUnit`) into the item's standard/base unit for storage.
    const convertToBaseUnit = (value, fromUnit, baseUnitSymbol) => {
        const v = parseFloat(value);
        if (isNaN(v)) return value;
        const from = PREP_UNIT_FAMILIES[normalizePrepUnit(fromUnit)];
        const base = PREP_UNIT_FAMILIES[normalizePrepUnit(baseUnitSymbol)];
        if (!from || !base || from.family !== base.family) return v; // not convertible → treat as already base
        return (v * from.factor) / base.factor;
    };
    // Convert a value already in the base unit back out to `toUnit` for display.
    const convertFromBaseUnit = (value, toUnit, baseUnitSymbol) => {
        const v = parseFloat(value);
        if (isNaN(v)) return value;
        const to = PREP_UNIT_FAMILIES[normalizePrepUnit(toUnit)];
        const base = PREP_UNIT_FAMILIES[normalizePrepUnit(baseUnitSymbol)];
        if (!to || !base || to.family !== base.family) return v;
        return (v * base.factor) / to.factor;
    };
    // Unit shown when the modal first opens = the item's own standard unit.
    const getDefaultInputUnit = (baseUnitSymbol) => {
        const norm = normalizePrepUnit(baseUnitSymbol);
        return PREP_UNIT_FAMILIES[norm] ? norm : baseUnitSymbol;
    };

    const checkPrepShortages = useCallback((prepItem, qty) => {
        if (!prepItem || !prepItem.components || prepItem.components.length === 0 || !qty || parseFloat(qty) <= 0) return [];
        
        const shortages = [];
        const scaleFactor = parseFloat(qty) / (prepItem.totalWeight || 1);
        
        const stockMap = {};
        inventoryOverview.forEach(inv => {
            if (inv.item_id != null) {
                stockMap[inv.item_id] = parseFloat(inv.quantity || inv.current_stock || 0);
            }
        });
        
        prepItem.components.forEach(comp => {
            const compId = comp.atomic_item_id;
            const required = (parseFloat(comp.weight || 0) * scaleFactor);
            const available = stockMap[compId] || 0;
            const unit = getShortUnit(comp.unit_name || comp.unitSymbol || 'g');
            
            if (required > available + 0.0001) {
                shortages.push({
                    name: comp.item_name || 'Ingredient',
                    required,
                    available,
                    shortage: required - available,
                    unit
                });
            }
        });
        
        return shortages;
    }, [inventoryOverview]);

    // Cleanup lock interval on unmount
    useEffect(() => {
        return () => {
            if (lockIntervalRef.current) {
                clearInterval(lockIntervalRef.current);
            }
        };
    }, []);

    // Ensure no residual scroll locks when arriving on this page
    useEffect(() => {
        // Clear any leftover locks from previous pages/modals
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        // Ensure viewport starts in a sane place
        window.scrollTo({ top: 0, behavior: 'auto' });

        return () => {
            // Clean up on unmount just in case
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        };
    }, []);

    // Prevent background scrolling when modal is open
    useEffect(() => {
        if (isReasonModalOpen || isStaffModalOpen || isConfirmModalOpen || piecePackReview || showPhotoNudge || prepQuantityModal.open) {
            // Save current scroll position
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
            
            return () => {
                // Restore scroll position
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.body.style.overflow = '';
                document.documentElement.style.overflow = '';
                window.scrollTo(0, scrollY);
            };
        }
    }, [isReasonModalOpen, isStaffModalOpen, isConfirmModalOpen, piecePackReview, showPhotoNudge, prepQuantityModal.open]);

    // State for loading and errors
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    // T1.9 — what the success modal is actually reporting. 'recorded' = a confirmed
    // submission (stock moved; the daily wastage checkpoint may be ticked). 'draft' = a
    // saved draft (nothing recorded; the checkpoint must NOT be ticked).
    const [successModalKind, setSuccessModalKind] = useState('recorded');
    const [messageModal, setMessageModal] = useState({ open: false, title: '', message: '', onOk: null });
    const [processing, setProcessing] = useState(false);
    const [processingProgress, setProcessingProgress] = useState(0);
    const [processingMessage, setProcessingMessage] = useState('');
    // Confirmation modal derived items snapshot
    const [confirmItems, setConfirmItems] = useState([]);
    // Track if Photo Required button has been clicked
    const [photoButtonClicked, setPhotoButtonClicked] = useState(false);

    // 10-second submission lock state (locks entire page when active in Usage tab)
    const [submissionLock, setSubmissionLock] = useState({
        canSubmit: true,
        canSwitchTab: true,
        secondsRemaining: 0,
        isActive: false
    });
    const lockIntervalRef = useRef(null);

    // Function to start the 10-second lock (called from Usage component when item is selected)
    const startSubmissionLock = useCallback((selectedShift = null) => {
        // Check if lock was already triggered today
        const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // YYYY-MM-DD
        const userId = currentUser?.userId || currentUser?.user_id || currentUser?.id || 'guest';
        const bizId = businessId || UnitMappingService.getBusinessId() || localStorage.getItem('businessId') || 'default';
        const shiftType = selectedShift?.shift_type || (selectedShift?.shift || selectedShift?.name || '').replace(' Shift', '').trim() || 'Morning';
        const scopedLockKey = `usageLockDate_${userId}_${bizId}_${shiftType}`;
        const lastLockDate = localStorage.getItem(scopedLockKey);
        
        console.log('🔒 startSubmissionLock called - Today:', today, 'Last lock:', lastLockDate);
        
        if (lastLockDate === today) {
            // Lock already triggered today, skip
            console.log('✅ Submission lock already triggered today, skipping');
            return;
        }
        
        console.log('⏰ Starting 10-second submission lock');
        
        // Store today's date to prevent re-triggering
        localStorage.setItem(scopedLockKey, today);
        
        // Initialize lock on server-side
        http.post('/usage/init-lock', {
            session_date: today,
            shift_type: shiftType,
            duration_seconds: 10
        }).catch(err => console.error('Failed to initialize lock on server:', err));
        
        const newLockState = {
            canSubmit: false,
            canSwitchTab: false, // Block tab switching
            secondsRemaining: 10,
            isActive: true
        };
        setSubmissionLock(newLockState);
        
        // Notify parent component about lock state
        if (onLockStateChange) {
            console.log('📤 Notifying parent about lock state:', newLockState.isActive);
            onLockStateChange(newLockState);
        }

        // Clear any existing interval
        if (lockIntervalRef.current) {
            clearInterval(lockIntervalRef.current);
        }

        // Start countdown
        let remaining = 10;
        lockIntervalRef.current = setInterval(() => {
            remaining -= 1;
            if (remaining <= 0) {
                const unlockedState = {
                    canSubmit: true,
                    canSwitchTab: true,
                    secondsRemaining: 0,
                    isActive: false
                };
                setSubmissionLock(unlockedState);
                
                // Notify parent component that lock is released
                if (onLockStateChange) {
                    onLockStateChange(unlockedState);
                }
                
                clearInterval(lockIntervalRef.current);
            } else {
                setSubmissionLock(prev => ({
                    ...prev,
                    secondsRemaining: remaining
                }));
            }
        }, 1000);
    }, [onLockStateChange, currentUser, businessId]);

    // Robust lookup for item name by id across known collections
    const getItemNameById = (searchId) => {
        const wid = `${searchId}`;
        const tryName = (obj) => (obj?.name || obj?.display_name || obj?.menu_name || obj?.alias || null);
        let pools = [];
        try {
            pools = [
                ...(Array.isArray(menuItemsWithRecipeCost) ? menuItemsWithRecipeCost : []),
                ...(Array.isArray(rawItems) ? rawItems : []),
                ...(Array.isArray(preparationItems) ? preparationItems : []),
            ];
        } catch (err) {}
        const found = pools.find(i => `${i?.id}` === wid);
        return tryName(found) || null;
    };
    
    // State for ingredient shortage modal
    const [showShortageModal, setShowShortageModal] = useState(false);
    const [shortageData, setShortageData] = useState(null);
    const [showTSRErrorModal, setShowTSRErrorModal] = useState(false);
    const [tsrErrorData, setTSRErrorData] = useState(null);
    const [selectedMissingItems, setSelectedMissingItems] = useState({});
    const [resolvedIngredients, setResolvedIngredients] = useState(new Set()); // Track resolved ingredients
    // Removed manual refresh state (auto background refresh every 10s now)

    // Restore wastage data when returning from itemmap
    useEffect(() => {
        const pendingWastageData = localStorage.getItem('pendingWastageData');
        const cameFromWastage = localStorage.getItem('cameFromWastage');
        const itemMappingComplete = localStorage.getItem('itemMappingComplete');
        
        // 🔄 Refresh menu items if user completed item mapping
        if (itemMappingComplete === 'true') {
            console.log('🔄 Item mapping completed, refreshing menu items...');
            const refreshMenuItems = async () => {
                try {
                    const businessId = UnitMappingService.getBusinessId();
                    // ✅ Include inactive items for wastage - user may need to waste disabled items
                    const result = await http.get(`/menu/items?businessId=${businessId}&language=${language}&includeInactive=true`);
                    if (result.success && result.data) {
                        console.log(`✅ Refreshed menu items: ${result.data.length} items found (including inactive)`);
                        setMenuItems(result.data.map(item => ({
                            ...item,
                            id: item.id ?? item.menu_item_id,
                            menu_item_id: item.menu_item_id ?? item.id,
                            price: parseFloat(item.price) || 0
                        })));
                    }
                } catch (error) {
                    console.error('❌ Failed to refresh menu items:', error);
                }
            };
            refreshMenuItems();
            localStorage.removeItem('itemMappingComplete');
        }
        
        if (pendingWastageData && cameFromWastage) {
            try {
                const data = JSON.parse(pendingWastageData);
                console.log('🔄 Restoring wastage data from itemmap:', data);
                
                // Restore state
                if (data.wastedItems) setWastedItems(data.wastedItems);
                if (data.wasteReason) setWasteReason(data.wasteReason);
                if (data.recordedBy) setRecordedBy(data.recordedBy);
                if (data.notes) setNotes(data.notes);
                if (data.photos) setPhotos(data.photos);
                
                // If ingredients were being removed, mark them as resolved and show updated modal
                if (data.ingredientsToRemove && data.ingredientsToRemove.length > 0 && data.shortageData) {
                    console.log('✅ User returned from itemmap after removing ingredients:', data.ingredientsToRemove);
                    // Entries are `{ name, match_keys }`; a payload written before that
                    // shape existed is a plain string, and its name is then the only
                    // identity available — the same fallback shortageMatchTokens makes.
                    const removedEntries = data.ingredientsToRemove.map((ing) => (
                        typeof ing === 'string' ? { name: ing } : (ing || {})
                    ));
                    const removedNames = new Set(
                        removedEntries.map((ing) => String(ing.name || '').toLowerCase().trim()).filter(Boolean)
                    );
                    const removedResolvedKeys = new Set(
                        removedEntries.flatMap((ing) => shortageMatchTokens(ing))
                    );

                    // Add to resolved ingredients
                    setResolvedIngredients(prev => {
                        const updated = new Set([...prev, ...removedResolvedKeys]);
                        console.log('🗑️ Total resolved ingredients:', Array.from(updated));
                        return updated;
                    });

                    // Filter out removed ingredients from missing list
                    const remainingMissing = data.shortageData.missingIngredients.filter((ing) => {
                        const isRemoved = removedNames.has(String(ing.name || '').toLowerCase().trim());
                        if (isRemoved) {
                            console.log(`✅ Removing "${ing.name}" from missing list (removed from recipe)`);
                        }
                        return !isRemoved;
                    });
                    
                    console.log(`📊 Remaining missing ingredients: ${remainingMissing.length}`);
                    
                    // Update shortage data with remaining ingredients
                    setShortageData({
                        ...data.shortageData,
                        missingIngredients: remainingMissing
                    });
                    
                    // If no more missing ingredients, retry wastage submission
                    if (remainingMissing.length === 0) {
                        console.log('✅ All ingredients resolved! Retrying wastage submission...');
                        // Delay to let state settle
                        setTimeout(() => handleSubmit(removedResolvedKeys), 500);
                    } else {
                        // Show updated modal with remaining ingredients
                        console.log('⚠️ Still have missing ingredients, showing updated modal');
                        setShowShortageModal(true);
                    }
                }
                
                // Clear the flags
                localStorage.removeItem('pendingWastageData');
                localStorage.removeItem('cameFromWastage');
                
                console.log('✅ Wastage data restored successfully');
            } catch (error) {
                console.error('❌ Failed to restore wastage data:', error);
            }
        }
    }, [language]);

    // ✅ Single-item calculateRecipeCost removed — replaced by bulk path in loadMenuItemsWithRecipeCosts
    // The single-item endpoint /manufacturing-costs/menu-item/:id still exists for other consumers.

    // Load inventory overview data with real-time costs
    const loadInventoryOverview = useCallback(async (forceRefresh = false) => {
        try {
            // Include complimentary items for comprehensive cost calculation
            const businessId = UnitMappingService.getBusinessId();
            const result = await http.get(`/stock-in/v2/inventory/overview?businessId=${businessId}&includeComplimentary=true&realTime=true&language=${language}`);
            if (result.success && result.data) {
                setInventoryOverview(result.data);
                
                // If this is a forced refresh, also refresh menu item costs
                if (forceRefresh && menuItems.length > 0) {
                    await loadMenuItemsWithRecipeCosts(true);
                }
            }
        } catch (error) {
            console.error('❌ Error loading inventory overview:', error);
        }
    // NOTE: intentionally NOT depending on menuItems to avoid re-creating the function
    // which was triggering the initial load effect repeatedly (causing perpetual loading state).
    }, [language]);

    // Load menu items with manufacturing costs from backend API
    // ✅ BULK: Single API call instead of N individual calls
    // ✅ OPTIMIZED: Show items immediately with placeholder costs, update async
    const lastBulkRefreshRef = useRef(0); // timestamp of last successful refresh
    const loadMenuItemsWithRecipeCosts = useCallback(async (forceRealTime = false, silent = false) => {
        if (menuItems.length === 0) {
            // skip silently
            return;
        }

        // ✅ Show items immediately with zero costs (no loading card blocker)
        if (!silent && menuItemsWithRecipeCost.length === 0) {
            const placeholder = menuItems.map(item => ({
                ...item,
                originalPrice: parseFloat(item.price) || 0,
                price: 0,
                hasRecipe: false,
                isComplete: false,
                insufficientStock: false,
                isRecipeCostCalculated: false,
                lastUpdated: new Date().toISOString()
            }));
            setMenuItemsWithRecipeCost(placeholder);
            setIsItemsLoading(false);
        }

        // ✅ Minimum gap guard: skip if last refresh was <30s ago (unless forced)
        const now = Date.now();
        if (silent && lastBulkRefreshRef.current && (now - lastBulkRefreshRef.current) < 30000) {
            // skip — too soon
            return;
        }

        try {
            // fetching bulk costs
            const businessId = UnitMappingService.getBusinessId();
            const result = await http.get(`/manufacturing-costs/bulk?businessId=${businessId}&includeInactive=true`);

            if (result.success && result.data) {
                // Build a map from the bulk response
                const costMap = {};
                result.data.forEach(item => {
                    costMap[item.menu_item_id] = item;
                });

                const itemsWithRecipeCosts = menuItems.map(item => {
                    const costLookupId = item.menu_item_id ?? item.id;
                    const costData = costMap[costLookupId] || {};
                    const manufacturingCost = parseFloat(costData.manufacturing_cost) || 0;
                    const hasRecipe = costData.has_recipe || false;
                    const isIncomplete = costData.is_incomplete || false;
                    const insufficientStock = costData.insufficient_stock || false;

                    let cost = 0;
                    let isComplete = false;
                    // Display manufacturing cost only when ALL ingredients have sufficient stock.
                    // If any ingredient is missing (insufficientStock=true), the dish cannot be
                    // made, so the realizable manufacturing cost is ₹0.
                    if (manufacturingCost > 0 && hasRecipe && !insufficientStock) {
                        cost = manufacturingCost;
                        isComplete = true;
                    }

                    return {
                        ...item,
                        originalPrice: parseFloat(item.price) || 0,
                        price: cost,
                        hasRecipe,
                        isComplete,
                        insufficientStock,
                        isRecipeCostCalculated: hasRecipe && cost > 0,
                        lastUpdated: new Date().toISOString()
                    };
                });

                // bulk costs received

                if (silent) {
                    // Shallow diff: only update if something changed
                    let changed = itemsWithRecipeCosts.length !== menuItemsWithRecipeCost.length;
                    if (!changed) {
                        for (let i = 0; i < itemsWithRecipeCosts.length; i++) {
                            const a = itemsWithRecipeCosts[i];
                            const b = menuItemsWithRecipeCost[i];
                            if (!b || a.id !== b.id || a.price !== b.price) {
                                changed = true;
                                break;
                            }
                        }
                    }
                    if (changed) {
                        setMenuItemsWithRecipeCost(itemsWithRecipeCosts);
                    }
                } else {
                    setMenuItemsWithRecipeCost(itemsWithRecipeCosts);
                }

                lastBulkRefreshRef.current = Date.now();
            }
        } catch (error) {
            console.error('❌ Error loading bulk manufacturing costs:', error);
            // On error, ensure items still show with zero costs
            if (menuItemsWithRecipeCost.length === 0) {
                const itemsWithoutRecipes = menuItems.map(item => ({
                    ...item,
                    originalPrice: parseFloat(item.price) || 0,
                    price: 0,
                    hasRecipe: false,
                    isRecipeCostCalculated: false,
                    lastUpdated: new Date().toISOString()
                }));
                setMenuItemsWithRecipeCost(itemsWithoutRecipes);
            }
        } finally {
            setIsItemsLoading(false);
        }
    }, [menuItems, menuItemsWithRecipeCost]);

    // Load available plates for menu items based on current stock
    const loadAvailablePlates = useCallback(async () => {
        console.log('🔍 [PLATES] ====== loadAvailablePlates CALLED ======');
        try {
            const businessId = UnitMappingService.getBusinessId();
            console.log('🔍 [PLATES] Loading available plates for businessId:', businessId);
            const result = await http.get(`/wastage/available-plates?business_id=${businessId}`);
            
            console.log('🔍 [PLATES] API Response:', result);
            
            if (result.success && result.data) {
                // Convert array to map for quick lookup
                const platesMap = {};
                result.data.forEach(item => {
                    platesMap[item.menu_item_id] = {
                        available_plates: item.available_plates,
                        limiting_ingredient: item.limiting_ingredient
                    };
                });
                console.log('🔍 [PLATES] Plates map:', platesMap);
                setAvailablePlates(platesMap);
                console.log(`✅ Loaded available plates for ${result.data.length} menu items`);
            } else {
                console.warn('⚠️ [PLATES] No data returned or unsuccessful:', result);
            }
        } catch (error) {
            console.error('❌ Error loading available plates:', error);
            console.error('❌ Error details:', error.message, error.stack);
        }
    }, []);

    const closePlateDetailModal = useCallback(() => {
        setPlateDetailModal({
            open: false,
            loading: false,
            item: null,
            platesInfo: null,
            summary: null,
            breakdown: [],
            error: null
        });
    }, []);

    const openPlateDetailModal = useCallback(async (item) => {
        if (!item?.id) return;

        const platesInfo = availablePlates[item.id] || null;
        setPlateDetailModal({
            open: true,
            loading: true,
            item,
            platesInfo,
            summary: null,
            breakdown: [],
            error: null
        });

        try {
            const result = await http.get(`/manufacturing-costs/menu-item/${item.id}`);
            const data = result?.data || {};
            setPlateDetailModal({
                open: true,
                loading: false,
                item,
                platesInfo,
                summary: data,
                breakdown: Array.isArray(data?.breakdown) ? data.breakdown : [],
                error: null
            });
        } catch (error) {
            setPlateDetailModal({
                open: true,
                loading: false,
                item,
                platesInfo,
                summary: null,
                breakdown: [],
                error: error.message || 'Failed to load plate details'
            });
        }
    }, [availablePlates]);

    // Load available plates when menu items with costs are ready
    useEffect(() => {
        console.log('🔍 [PLATES TRIGGER] useEffect fired, menuItemsWithRecipeCost.length:', menuItemsWithRecipeCost.length);
        if (menuItemsWithRecipeCost && menuItemsWithRecipeCost.length > 0) {
            console.log('🔍 [PLATES TRIGGER] Menu items loaded, triggering loadAvailablePlates...');
            loadAvailablePlates().then(() => {
                console.log('🔍 [PLATES TRIGGER] loadAvailablePlates promise resolved');
            }).catch(err => {
                console.error('❌ [PLATES TRIGGER] loadAvailablePlates failed:', err);
            });
        } else {
            console.log('🔍 [PLATES TRIGGER] Skipping - no menu items yet');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuItemsWithRecipeCost.length]); // Only depend on length, loadAvailablePlates is stable via useCallback

    // Once availablePlates loads, correct the displayed price:
    // if an item has 0 available_plates it means at least one ingredient is out of stock,
    // so the real manufacturing cost that will be incurred is ₹0.
    useEffect(() => {
        if (!availablePlates || Object.keys(availablePlates).length === 0) return;
        if (!menuItemsWithRecipeCost || menuItemsWithRecipeCost.length === 0) return;

        let changed = false;
        const corrected = menuItemsWithRecipeCost.map(item => {
            const platesInfo = availablePlates[item.id];
            if (platesInfo && Number(platesInfo.available_plates || 0) === 0 && item.price > 0) {
                changed = true;
                return { ...item, price: 0 };
            }
            return item;
        });

        if (changed) {
            setMenuItemsWithRecipeCost(corrected);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availablePlates]);

    const fetchData = useCallback(async (path, setter, errorMessage) => {
        try {
            const json = await http.get(path);

            if (json.success) {
                let data = json.data || [];
                if (path === '/wastage/reasons' || path.startsWith('/wastage/reasons')) {
                    // Remove duplicate reasons (if any)
                    data = data.filter((item, index, self) =>
                        index === self.findIndex(t => (t.reason_id || t.id) === (item.reason_id || item.id))
                    );
                    
                    // ✅ FIXED: Only use database reasons - NO fallback reasons
                    // If no reasons from database, show error to user
                    if (data.length === 0) {
                        console.warn('⚠️ No wastage reasons found in database!');
                        setError('No wastage reasons configured. Please add wastage reasons in Settings first.');
                    } else {
                        console.log(`✅ Loaded ${data.length} wastage reasons from database`);
                    }
                }

                // Ensure menu items have numeric prices and populated id
                if (path.startsWith('/menu/items') && data.length > 0) {
                    data = data.map(item => ({
                        ...item,
                        id: item.id ?? item.menu_item_id,
                        menu_item_id: item.menu_item_id ?? item.id,
                        price: parseFloat(item.price) || 0
                    }));
                }

                setter(data);
            } else {
                setError(`${errorMessage}: ${json.error || 'Unknown error'}`);
            }
        } catch (e) {
            if (e && e.name === 'TypeError' && typeof e.message === 'string' && e.message.includes('fetch')) {
                setError(`${errorMessage}: Network connection failed`);
            } else {
                setError(`${errorMessage}: ${e.message}`);
            }
        }
    }, []);

    // Load all data on component mount
    useEffect(() => {
        console.log('🔍 [MOUNT] useEffect triggered for data loading, language:', language);
        // Run only once on mount to prevent infinite re-fetch loops caused by
        // function identity changes in dependencies.
        let cancelled = false;
        const loadData = async () => {
            console.log('🔍 [MOUNT] Starting loadData function...');
            setIsLoading(true);
            setError(null);
            try {
                console.log('🔍 [MOUNT] Checking health...');
                await http.get('/health');
                console.log('🔍 [MOUNT] Health check passed');
            } catch (healthError) {
                console.error('❌ [MOUNT] Health check failed:', healthError);
                if (!cancelled) {
                    setError(`Cannot connect to server at ${API_BASE_URL}. Please check if the backend is running.`);
                    setIsLoading(false);
                }
                return;
            }
            try {
                const businessId = UnitMappingService.getBusinessId();
                const fetchPromises = [
                    // ✅ Include inactive items for wastage - user may need to waste disabled items
                    fetchData(`/menu/items?businessId=${businessId}&language=${language}&includeInactive=true`, setMenuItems, 'Failed to load menu items'),
                    // ?date= arms the server's Bill Error gating: it marks bill-error
                    // reasons is_disabled on a day with no TSR. The client never sent a
                    // date, so the gating had never fired once — a Bill Error on a day
                    // with no sales failed at submit with a 400 instead of being greyed out.
                    fetchData(`/wastage/reasons?date=${encodeURIComponent(operationDate)}`, setWasteReasons, 'Failed to load waste reasons'),
                    loadInventoryOverview(),
                    loadAvailablePlates()
                ];
                // Only fetch staff members for owners (they need the staff selection modal).
                // Non-owner staff are auto-assigned from their login context, and may not have view_users permission.
                if (isCurrentUserOwner) {
                    fetchPromises.push(
                        fetchData(`/users?business_id=${UnitMappingService.getBusinessId()}`, setStaffMembers, 'Failed to load staff members')
                    );
                }
                await Promise.all(fetchPromises);
                if (!cancelled) {
                    setIsLoading(false);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(`Unexpected error while loading data: ${e.message}`);
                    setIsLoading(false);
                }
            }
        };
        loadData();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    // 🔔 Subscribe to wastage reason events (real-time sync with Settings)
    useEffect(() => {
        console.log('📢 StockOutForm: Subscribing to wastage reason events');
        
        const unsubscribe = wastageReasonEventService.subscribe((event) => {
            console.log('📢 StockOutForm: Received wastage reason event:', event);
            
            if (event.type === 'REASON_ADDED' || event.type === 'REASON_DELETED' || event.type === 'REASONS_REFRESHED') {
                // Refresh wastage reasons from API (business_id from session)
                console.log('🔄 StockOutForm: Refreshing wastage reasons due to event:', event.type);
                fetchData(`/wastage/reasons?date=${encodeURIComponent(operationDate)}`, setWasteReasons, 'Failed to load waste reasons');
            }
        });
        
        return () => {
            console.log('📢 StockOutForm: Unsubscribing from wastage reason events');
            unsubscribe();
        };
        // `operationDate` is a dependency because the handler bakes it into the refetch
        // URL. Re-subscribing on a date change is cheap; a stale closure would refetch
        // the reasons for the day the user has just moved away from.
    }, [fetchData, operationDate]);

    // Bill Error is only legal on a day that has a TSR, and the server decides that per
    // date. Changing the date changes the answer, so the list is refetched — otherwise
    // the user picks a Bill Error the server will refuse, and only finds out at submit.
    useEffect(() => {
        if (!operationDate) return;
        fetchData(
            `/wastage/reasons?date=${encodeURIComponent(operationDate)}`,
            setWasteReasons,
            'Failed to load waste reasons'
        );
    }, [operationDate, fetchData]);

    // A reason selected for the previous date may be disabled on the new one. Watching
    // the LIST rather than the date is what makes this correct: `wasteReason` holds the
    // object as it read when it was picked, so its own `is_disabled` is always stale —
    // the freshly fetched row is the only one that knows.
    useEffect(() => {
        if (!wasteReason) return;
        const fresh = wasteReasons.find((r) => r.reason_id === wasteReason.reason_id);
        if (fresh && fresh.is_disabled) {
            console.log(`🚫 "${fresh.reason_label}" is not available on ${operationDate}; clearing it.`);
            setWasteReason(null);
        }
    }, [wasteReasons, wasteReason, operationDate]);

    // ── Banquet Wastage ──────────────────────────────────────────────────────
    //
    // The banquet reason is identified by system_code / is_banquet, never by its
    // label. Read from the FRESH row when there is one — a reason object restored
    // from somewhere older may predate those fields.
    const banquetReasonSelected = useMemo(() => {
        if (!wasteReason) return false;
        const fresh = wasteReasons.find((r) => r.reason_id === wasteReason.reason_id);
        return isBanquetReason(fresh || wasteReason);
    }, [wasteReason, wasteReasons]);
    // The "Banquet event" dropdown for the wastage date, and the chosen event.
    const [banquetEvents, setBanquetEvents] = useState(NO_BANQUET_EVENTS);
    const [banquetEventId, setBanquetEventId] = useState(null);
    const [banquetEventsReload, setBanquetEventsReload] = useState(0); // bumped to force a reload
    const chosenBanquetEvent = banquetReasonSelected ? findBanquetEvent(banquetEvents.events, banquetEventId) : null;
    // While an event is chosen the date may only be event day … min(event + 3, today).
    const banquetDateWindow = eventWindow(chosenBanquetEvent, istToday(), reconciledThrough);
    // What the success modal reports for a banquet submission: { message, warnings }.
    const [banquetOutcome, setBanquetOutcome] = useState(null);
    // Banquet wastage names only what the function SERVED: its dishes, and the
    // raw items and preparations inside them. The server sends that scope with
    // each listed function and refuses anything outside it, so the grid offers
    // exactly that set — and nothing at all until a function is picked.
    const banquetScopeKeys = useMemo(
        () => (banquetReasonSelected && chosenBanquetEvent ? functionItemKeys(chosenBanquetEvent) : null),
        [banquetReasonSelected, chosenBanquetEvent]
    );
    const banquetAwaitingFunction = banquetReasonSelected && !chosenBanquetEvent;
    // "Removed N items not served at …" after a function was picked or changed.
    const [banquetRemovedNotice, setBanquetRemovedNotice] = useState(null);

    // Load the events a wastage on this date may belong to (held date−3 … date).
    // Reloaded when the date changes; a chosen event that is no longer listed —
    // reverted, or outside the new date's range — is unchosen.
    useEffect(() => {
        if (!banquetReasonSelected || !operationDate) {
            setBanquetEvents(NO_BANQUET_EVENTS);
            setBanquetEventId(null);
            return undefined;
        }
        let cancelled = false;
        setBanquetEvents((prev) => ({ ...prev, status: 'loading', error: null }));
        Promise.resolve(fetchWastageEvents(operationDate)).then((res) => {
            if (cancelled) return;
            if (!res || !res.ok) {
                setBanquetEvents({
                    status: 'error', events: [], range: null,
                    error: banquetRefusalMessage(res) || res?.error || 'Could not load the banquets.',
                });
                return;
            }
            const events = Array.isArray(res.events) ? res.events : [];
            setBanquetEvents({ status: 'ready', events, range: res.range || null, error: null });
            setBanquetEventId((prev) => (prev !== null && !findBanquetEvent(events, prev) ? null : prev));
        }).catch(() => {
            if (!cancelled) setBanquetEvents({ status: 'error', events: [], range: null, error: 'Could not load the banquets.' });
        });
        return () => { cancelled = true; };
    }, [banquetReasonSelected, operationDate, banquetEventsReload]);

    // Choosing an event SNAPS the date to the event day — through the user-pick
    // handler, so a late suggested date can never override it. In a recovery flow
    // the date is fixed and is left alone: every listed event already admits it.
    const handleBanquetEventChange = (eventId) => {
        setBanquetEventId(eventId);
        const event = findBanquetEvent(banquetEvents.events, eventId);
        if (event && !dateIsFixed) handleUserPickedDate(event.eventDate);
    };

    /**
     * The raw-ingredient side of the wastage screen.
     *
     * Declared HERE, above the refresh effects, rather than further down where it used to
     * sit: those effects now call it, and a dependency array is evaluated during render,
     * so a `const` declared below them would be read before it exists.
     */
    const fetchRawItems = useCallback(async () => {
        try {
            const businessId = UnitMappingService.getBusinessId();
            const result = await http.get(`/unit-mapping/inventory-items/${businessId}?language=${language}`);

            if (result.success && result.data) {
                let detailedItems = [];
                try {
                    // Fetch with real-time + complimentary flags to ensure dynamic costs
                    const detailedResult = await http.get(`/stock-in/v2/inventory/overview?businessId=${businessId}&includeComplimentary=true&realTime=true&language=${language}`);
                    if (detailedResult.success && detailedResult.data) {
                        detailedItems = detailedResult.data;
                    }
                } catch (detailError) {
                    console.warn('Could not fetch detailed inventory data:', detailError);
                }

                const processedRawItems = result.data.map(item =>
                    // Extracted to utils/wastageItemMapping.js. The version that used to
                    // live here copied the unit LABEL and dropped standard_unit_id, so
                    // every raw-ingredient wastage row was stored with unit_id = NULL.
                    buildRawWastageItem(item, detailedItems.find(d => d.item_id === item.id))
                );

                setRawItems(processedRawItems);
            }
        } catch (error) {
            console.error('Error fetching raw items:', error);
        } finally {
            setRawItemsLoaded(true);
        }
    }, [language]);

    useEffect(() => {
        fetchRawItems();
    }, [fetchRawItems]);

    /**
     * The Stock In answer ("how many pieces in one packet?") for these items.
     *
     * No vendor on purpose: wastage is not from a vendor, and a new answer is born
     * item-level — the same row Settings › Piece Guard edits. Resolves to the fetched
     * slice, or null when the answer could not be read; an unreadable answer is not
     * cached, so the next Confirm asks again rather than staying silent for the session.
     */
    const loadPiecePackProfiles = useCallback(async (itemIds) => {
        const businessId = UnitMappingService.getBusinessId();
        if (!businessId || itemIds.length === 0) return {};
        const res = await http.get(`/stock-in/v2/piece-profiles?businessId=${businessId}&itemIds=${itemIds.join(',')}`);
        if (!res || !res.success || res.unavailable) return null;
        const fetched = {};
        // An explicit null for "no answer yet", so the item is not re-requested.
        itemIds.forEach(id => { fetched[id] = (res.profiles && res.profiles[id]) || null; });
        setPiecePackProfiles(prev => ({ ...prev, ...fetched }));
        return fetched;
    }, []);

    // Fetched while the list is being built, so the final Confirm does not wait on it.
    useEffect(() => {
        const wanted = pieceWastageItemIds(wastedItems, rawItems).filter(id => !(id in piecePackProfiles));
        if (wanted.length === 0) return undefined;
        const timer = setTimeout(() => {
            loadPiecePackProfiles(wanted).catch(e => console.debug('Piece packet read-back unavailable:', e.message));
        }, 400);
        return () => clearTimeout(timer);
    }, [wastedItems, rawItems, piecePackProfiles, loadPiecePackProfiles]);

    // Set categories when menu items are loaded or when itemmap master category toggle preference changes
    useEffect(() => {
        if (menuItems.length > 0) {
            const key = useMasterCategories ? 'master_category' : 'category';
            const uniqueCats = Array.from(new Set(menuItems.map(item => item[key] || (useMasterCategories ? 'Uncategorized' : '')).filter(Boolean))).sort();
            const extras = ['Raw Items'];
            if (preparationItems.length > 0) {
                extras.push('Preparations');
            }
            // Packaging, cutlery, carry bags, tissues. The chip appears on its own the
            // moment the business owns one such item — which is the moment a stock-in
            // files an uncategorized row onto the non-consumable shelf. Nobody has to
            // create the category here or move anything into it; the server already
            // said what these items are (`category_type`), and this reads that.
            if (rawItems.some(item => item.isNonConsumable)) {
                extras.push(NON_CONSUMABLE_CATEGORY);
            }
            setDishCategories(uniqueCats);
            setExtraCategories(extras);
            setCategories(['All', ...uniqueCats, ...extras]);
        }
    }, [menuItems, preparationItems, rawItems, useMasterCategories]);

    // Load manufacturing costs from backend API when menu items are loaded
    useEffect(() => {
        if (menuItems.length > 0) {
            loadMenuItemsWithRecipeCosts();
        }
    }, [menuItems, loadMenuItemsWithRecipeCosts]);

    // ✅ OPTIMIZED: Polling every 60s (was 5s) — WS handles instant updates
    useEffect(() => {
        let active = true;
        const doRefresh = async () => {
            if (!active || menuItems.length === 0) return;
            // 30s gap guard: skip if a WS-triggered refresh just happened
            if (lastBulkRefreshRef.current && (Date.now() - lastBulkRefreshRef.current) < 30000) return;
            try {
                await loadInventoryOverview(true);
                if (active) {
                    await loadMenuItemsWithRecipeCosts(true, true); // silent diff update
                }
            } catch (_) { /* silent */ }
        };
        // Fallback polling is OFF. A WebSocket push already triggers doRefresh,
        // and the visibility handler below re-syncs the moment this tab is looked
        // at again — both with the same 30s gap guard. The timer only added cost:
        // it ran overnight on tablets nobody was watching, and anything under
        // Neon's 5-minute autosuspend keeps the compute billed. Set a delay here
        // again only if the WebSocket proves unreliable.
        return () => { active = false; };
    }, [menuItems.length, loadInventoryOverview, loadMenuItemsWithRecipeCosts]);

    // ✅ REFRESH ON PAGE VISIBILITY with 30s gap guard
    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (!document.hidden && menuItems.length > 0) {
                // 30s gap guard: skip if a recent refresh happened
                if (lastBulkRefreshRef.current && (Date.now() - lastBulkRefreshRef.current) < 30000) {
                    // skip — recent data available
                    return;
                }
                // refreshing on visibility change
                await new Promise(resolve => setTimeout(resolve, 500));
                try {
                    await loadInventoryOverview(true);
                    await loadMenuItemsWithRecipeCosts(true, false);
                    // The raw list too. Without this the screen refreshed the COSTS of the
                    // items it already knew about and never learned about a new one — so an
                    // ingredient stocked in a minute ago, in the other tab, could not be
                    // wasted until the whole page was reloaded. That is the exact sequence a
                    // non-consumable goes through: bought, then thrown away.
                    await fetchRawItems();
                } catch (error) {
                    console.error('Error refreshing costs on visibility change:', error);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [menuItems.length, loadInventoryOverview, loadMenuItemsWithRecipeCosts, fetchRawItems]);

    // ✅ WebSocket: listen for inventory:updated → instant refresh
    useEffect(() => {
        const businessId = UnitMappingService.getBusinessId();
        if (!businessId || menuItems.length === 0) return;

        // Derive socket URL: main API backend (port 5000), not customer-menu (5001)
        const socketUrl = (() => {
            if (process.env.REACT_APP_API_URL) {
                return process.env.REACT_APP_API_URL.replace('/api', '').replace(/\/$/, '');
            }
            // In dev mode with proxy, connect to same origin
            const host = window.location.hostname;
            if (host === 'localhost' || host === '127.0.0.1') {
                return `http://${host}:5000`;
            }
            // Production: same origin (nginx routes /socket.io to backend)
            return window.location.origin;
        })();

        // connecting WebSocket
        const socket = io(socketUrl, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 3000,
            reconnectionAttempts: 10,
            timeout: 10000,
        });

        socket.on('connect', () => {
            // WS connected
            socket.emit('owner:register', { businessId });
        });

        socket.on('inventory:updated', async (data) => {
            // WS inventory update received
            // Small delay to let DB settle
            await new Promise(r => setTimeout(r, 800));
            try {
                await loadInventoryOverview(true);
                await loadMenuItemsWithRecipeCosts(true, false);
                // 🔥 FIX: Refresh plate counts after wastage/inventory changes
                await loadAvailablePlates();
                // A stock-in is what fires this event, and a stock-in is how a new
                // ingredient — or a new non-consumable — comes into existence. Refreshing
                // everything except the list that would contain it defeats the push.
                await fetchRawItems();
            } catch (err) {
                console.error('Error refreshing after WS event:', err);
            }
        });

        socket.on('connect_error', (err) => {
            // WS connect error (non-critical)
        });

        return () => {
            // disconnecting WS
            socket.disconnect();
        };
    }, [menuItems.length, loadInventoryOverview, loadMenuItemsWithRecipeCosts, loadAvailablePlates, fetchRawItems]);


    // Helper: compute cost-per-base-unit for a preparation from its components and the inventory overview.
    // costPerBaseUnit = sum(component.weight * avgUnitCost[component.atomic_item_id]) / prep.totalWeight
    const computePrepCostPerUnit = useCallback((prep, overviewData) => {
        if (!prep.components || prep.components.length === 0 || !prep.totalWeight || prep.totalWeight <= 0) return 0;
        // Build a cost map from inventoryOverview: item_id -> unit_cost
        const costMap = {};
        (overviewData || []).forEach(item => {
            if (item.item_id != null) {
                costMap[item.item_id] = parseFloat(item.unit_cost || 0);
            }
        });
        // Sum component costs
        let totalComponentCost = 0;
        for (const comp of prep.components) {
            const atomicId = comp.atomic_item_id;
            const weight = parseFloat(comp.weight || 0);
            const unitCost = costMap[atomicId] || 0;
            totalComponentCost += weight * unitCost;
        }
        // Price per 1 base-unit of this preparation
        return totalComponentCost / prep.totalWeight;
    }, []);

    // Fetch saved preparation items from decomposition templates
    const fetchPreparationItems = useCallback(async () => {
        try {
            const businessId = UnitMappingService.getBusinessId();
            const result = await http.get(`/medium-features/decomposition/templates?businessId=${businessId}`);
            if (result && result.personalized && result.personalized.length > 0) {
                const processedPreps = result.personalized.map(prep => ({
                    id: `prep_${prep.id}`,
                    originalId: prep.id,
                    name: prep.composite_name,
                    category: 'Preparations',
                    price: 0, // Will be updated by the inventoryOverview effect below
                    unit: prep.total_weight_unit_name || 'grams',
                    unitSymbol: getShortUnit(prep.total_weight_unit_name || 'grams'),
                    totalWeight: parseFloat(prep.total_weight) || 0,
                    components: prep.components || [],
                    isPreparation: true
                }));
                setPreparationItems(processedPreps);
            } else {
                setPreparationItems([]);
            }
        } catch (error) {
            console.error('Error fetching preparation items:', error);
        } finally {
            setPreparationItemsLoaded(true);
        }
    }, []);

    useEffect(() => {
        fetchPreparationItems();
    }, [fetchPreparationItems]);

    // Re-compute preparation item cost-per-unit whenever inventoryOverview updates.
    // This ensures Estimated Waste Cost reflects real ingredient prices dynamically.
    useEffect(() => {
        if (!inventoryOverview || inventoryOverview.length === 0) return;
        if (!preparationItems || preparationItems.length === 0) return;

        const updated = preparationItems.map(prep => {
            const costPerUnit = computePrepCostPerUnit(prep, inventoryOverview);
            if (Math.abs(costPerUnit - (prep.price || 0)) < 0.0001) return prep; // no change
            return { ...prep, price: costPerUnit };
        });

        // Only update state if something actually changed (avoid infinite loop)
        const hasChange = updated.some((p, i) => p !== preparationItems[i]);
        if (hasChange) {
            console.log('💰 [PREP COST] Updated preparation item costs from inventory overview');
            setPreparationItems(updated);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inventoryOverview, computePrepCostPerUnit]);

    // Set default waste reason when reasons are loaded
    useEffect(() => {
        if (wasteReasons.length > 0) {
            const normalizeLabel = (label) => (label || '').toLowerCase();
            
            // Check if current wasteReason is valid (exists in loaded wasteReasons)
            const isValidReason = wasteReason && wasteReasons.some(r => r.reason_id === wasteReason.reason_id);
            
            // Only set default if no valid reason is selected
            if (!isValidReason) {
                // Exclude "No Wastage Recorded" and "Bill Error" from default selection
                const excludedReasons = ['no wastage recorded', 'bill errors', 'bill error'];
                
                // Find the best default reason (prefer common wastage reasons)
                const spoilageReason = wasteReasons.find(r => normalizeLabel(r.reason_label) === 'spoilage');
                const expiredReason = wasteReasons.find(r => normalizeLabel(r.reason_label) === 'expired');
                const overcookingReason = wasteReasons.find(r => normalizeLabel(r.reason_label) === 'overcooking');
                const qualityReason = wasteReasons.find(r => normalizeLabel(r.reason_label) === 'quality issue');
                
                // Get first valid reason that's not excluded
                const validReasons = wasteReasons.filter(r => !excludedReasons.includes(normalizeLabel(r.reason_label)));
                
                // Priority order: Spoilage > Expired > Overcooking > Quality Issue > Any other valid reason
                const defaultReason = spoilageReason || expiredReason || overcookingReason || qualityReason || (validReasons.length > 0 ? validReasons[0] : wasteReasons[0]);
                
                console.log('🔧 Setting default waste reason:', defaultReason?.reason_label);
                setWasteReason(defaultReason);
            } else {
                console.log('✅ Valid waste reason already set:', wasteReason?.reason_label);
            }
        }
    }, [wasteReasons]);

    // Auto-open reason modal when redirected from TSR warning
    useEffect(() => {
        if (openReasonModal && wasteReasons.length > 0) {
            setActiveSubTab('Waste');
            setReasonModalOpen(true);
            if (onReasonModalOpened) {
                onReasonModalOpened();
            }
        }
    }, [openReasonModal, wasteReasons, onReasonModalOpened]);

    // Draft functionality (manual save - though auto-save is already active)
    // Helper function to delete draft from database
    const clearDraftDatabase = async () => {
        try {
            // Scoped to the day being worked on. Without the date this cleared every
            // draft the business had, whatever day it was for.
            await http.delete(`/wastage/draft?date=${encodeURIComponent(operationDate)}`);
            console.log('✅ Draft deleted from database');
        } catch (err) {
            console.warn('Failed to delete draft from database:', err);
        }
    };

    // Draft functionality (manual save to database)
    const handleSaveDraft = async () => {
        // Banquet wastage is never a draft (the button is hidden; the server refuses it too).
        if (banquetReasonSelected) {
            setError(BANQUET_MESSAGES.BANQUET_NO_DRAFT);
            return;
        }
        try {
            // Build the items array
            const items = Object.entries(wastedItems)
                .filter(([itemId, qty]) => !isNaN(parseFloat(qty)) && parseFloat(qty) > 0)
                .map(([itemId, qty]) => ({
                    menu_item_id: itemId,
                    quantity: parseFloat(qty)
                }));

            // T1.9 — POST /wastage/draft replaces the stored draft for its day, so
            // saving an empty form silently destroyed a draft the user had already
            // saved. The button is disabled when the list is empty (see the
            // Save-as-Draft button); this is the belt-and-braces guard for the
            // programmatic path.
            if (items.length === 0) {
                setError('Nothing to save as draft — add at least one item first.');
                return;
            }

            const payload = {
                items,
                notes,
                waste_reason_id: wasteReason?.reason_id || null,
                activeSubTab,
                deducted_by_user_id: recordedBy?.user_id || recordedBy?.id || null,
                // Drafts are scoped by day server-side, so this decides which draft is
                // replaced — without it, saving for the 16th would clear the 15th's.
                deducted_date: operationDate || undefined
            };

            console.log('💾 Saving draft to database:', payload);
            const response = await http.post('/wastage/draft', payload);
            if (response && response.success) {
                console.log('✅ Draft saved to database');
                
                // Keep local storage synced as fallback
                const draftData = {
                    wasteReason,
                    wastedItems,
                    notes,
                    recordedBy,
                    photos: photos || [],
                    savedAt: new Date().toISOString(),
                    activeSubTab
                };
                const businessId = localStorage.getItem('businessId') || 'unknown';
                localStorage.setItem(`stockOutDraft_${businessId}`, JSON.stringify(draftData));

                // T1.9 — the success modal used to be shared verbatim with the confirmed
                // submission: it read "Waste Recorded Successfully" and its OK button
                // POSTed /operations/record-wastage, ticking off the day's wastage
                // checkpoint. A DRAFT is not a recording — no stock has moved and no
                // StockOutRecord is Confirmed — so the checkpoint was being satisfied by
                // work that had not happened. `successModalKind` keeps the two apart.
                setSuccessModalKind('draft');
                setSuccessMessage('Draft saved — not yet recorded');
                setShowSuccessModal(true);
            } else {
                throw new Error(response?.error || 'Failed to save draft');
            }
        } catch (err) {
            console.error('❌ Failed to save draft to database:', err);
            setError(err?.data?.code === 'BANQUET_NO_DRAFT'
                ? BANQUET_MESSAGES.BANQUET_NO_DRAFT
                : (err.message || 'Failed to save draft to database'));
        }
    };

    // Auto-save draft to local storage only as temporary fallback cache
    useEffect(() => {
        const hasData = Object.keys(wastedItems || {}).length > 0 || 
                        (notes && notes.trim().length > 0) || 
                        (photos && photos.length > 0) ||
                        wasteReason ||
                        recordedBy;
        
        if (hasData) {
            const draftData = {
                wasteReason,
                wastedItems,
                notes,
                recordedBy,
                photos: photos || [],
                savedAt: new Date().toISOString(),
                activeSubTab
            };
            const businessId = localStorage.getItem('businessId') || 'unknown';
            localStorage.setItem(`stockOutDraft_${businessId}`, JSON.stringify(draftData));
        }
    }, [wastedItems, notes, photos, wasteReason, recordedBy, activeSubTab]);

    // Load draft on component mount (runs only once) - Auto-restore from database
    useEffect(() => {
        const loadDraft = async () => {
            try {
                const response = await http.get(`/wastage/draft?date=${encodeURIComponent(operationDate)}`);
                if (response && response.success && response.data) {
                    const draftData = response.data;
                    console.log('📂 Found saved draft in database:', draftData);
                    
                    const hasItems = Object.keys(draftData.wastedItems || {}).length > 0;
                    if (hasItems) {
                        setWastedItems(draftData.wastedItems || {});
                        setNotes(draftData.notes || '');
                        if (draftData.recordedBy) setRecordedBy(draftData.recordedBy);
                        // Never the banquet reason: it would come back with no event.
                        if (draftData.wasteReason && !isBanquetReason(draftData.wasteReason)) setWasteReason(draftData.wasteReason);
                        if (draftData.activeSubTab) setActiveSubTab(draftData.activeSubTab);
                        console.log('✅ Draft restored automatically from database');
                        return;
                    }
                }
            } catch (err) {
                console.error('❌ Error loading draft from database:', err);
            }

            // Fallback to local storage draft if database draft is not found/empty
            const businessId = localStorage.getItem('businessId') || 'unknown';
            const savedDraftString = localStorage.getItem(`stockOutDraft_${businessId}`);
            if (savedDraftString) {
                try {
                    const draftData = JSON.parse(savedDraftString);
                    const hasItems = Object.keys(draftData.wastedItems || {}).length > 0;
                    if (hasItems) {
                        setWastedItems(draftData.wastedItems || {});
                        setNotes(draftData.notes || '');
                        if (draftData.recordedBy) setRecordedBy(draftData.recordedBy);
                        // Never the banquet reason: it would come back with no event.
                        if (draftData.wasteReason && !isBanquetReason(draftData.wasteReason)) setWasteReason(draftData.wasteReason);
                        if (draftData.activeSubTab) setActiveSubTab(draftData.activeSubTab);
                        console.log('✅ Draft restored automatically from local storage fallback');
                    }
                } catch (e) {
                    console.error('❌ Error parsing local storage draft:', e);
                    localStorage.removeItem(`stockOutDraft_${businessId}`);
                }
            }
        };

        loadDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run only once on mount

    // Validate and clean up wastedItems when menu items are loaded
    useEffect(() => {
        if (!rawItemsLoaded || !preparationItemsLoaded || menuItemsWithRecipeCost.length === 0 || Object.keys(wastedItems).length === 0) return;
        
        // Check if any wasted items no longer exist in the menu
        const validItemIds = new Set([
            ...menuItemsWithRecipeCost.map(item => item.id?.toString()),
            ...rawItems.map(item => item.id?.toString()),
            ...preparationItems.map(item => item.id?.toString())
        ]);
        
        let hasInvalidItems = false;
        const cleanedWastedItems = {};
        
        for (const [itemId, quantity] of Object.entries(wastedItems)) {
            // Extract the actual ID (remove 'raw_' prefix if present)
            const actualId = itemId.toString().startsWith('raw_') 
                ? itemId.toString().replace('raw_', '')
                : itemId.toString();
            
            if (validItemIds.has(actualId) || validItemIds.has(itemId.toString())) {
                cleanedWastedItems[itemId] = quantity;
            } else {
                hasInvalidItems = true;
                console.warn(`⚠️ Removing invalid item from waste list: ID ${itemId} (not found in menu)`);
            }
        }
        
        if (hasInvalidItems) {
            setWastedItems(cleanedWastedItems);
            setError('Some items were removed from your waste list because they no longer exist. Please review and re-submit.');
            
            // Update the draft
            const businessId = localStorage.getItem('businessId') || 'unknown';
            const savedDraft = localStorage.getItem(`stockOutDraft_${businessId}`);
            if (savedDraft) {
                try {
                    const draftData = JSON.parse(savedDraft);
                    draftData.wastedItems = cleanedWastedItems;
                    localStorage.setItem(`stockOutDraft_${businessId}`, JSON.stringify(draftData));
                } catch (e) {
                    console.error('Error updating draft:', e);
                }
            }
        }
    }, [menuItemsWithRecipeCost, rawItems, preparationItems, wastedItems, rawItemsLoaded, preparationItemsLoaded]);

    // Search handlers
    const clearSearch = () => {
        setSearchTerm('');
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    // A raw ingredient counted in pieces is stepped like a dish — +1 / −1 on the card, or
    // typed in the box — instead of opening the quantity modal, which for pieces has no
    // other unit to offer. Same unit test the modal itself uses (unitSymbol below).
    const isPieceSteppedItem = (item) =>
        item?.category === 'Raw Items' && getShortUnit(item.unitSymbol || item.unit) === 'pcs';

    const handleQuantityChange = (itemId, amount) => {
        // For preparations and raw items, open quantity modal instead of direct stepper
        if (itemId.toString().startsWith('prep_') || itemId.toString().startsWith('raw_')) {
            const item = preparationItems.find(p => p.id === itemId) || rawItems.find(r => r.id === itemId);
            if (item && !isPieceSteppedItem(item)) {
                const currentQty = wastedItems[itemId] || 0;
                // If clicking minus to remove, just remove directly
                if (amount < 0 && currentQty + amount <= 0) {
                    setWastedItems(prev => {
                        const { [itemId]: _, ...rest } = prev;
                        return rest;
                    });
                    return;
                }
                const unitSymbol = item.unitSymbol || getShortUnit(item.unit) || 'g';
                setPrepQuantityModal({
                    open: true,
                    item: {
                        ...item,
                        unitSymbol: unitSymbol
                    },
                    quantity: (() => {
                        const displayUnit = prepDisplayUnits[itemId] || getDefaultInputUnit(unitSymbol);
                        const displayQty = currentQty > 0 ? convertFromBaseUnit(currentQty, displayUnit, unitSymbol) : 0;
                        return displayQty > 0 ? String(parseFloat(displayQty.toFixed(4))) : '';
                    })(),
                    selectedUnit: prepDisplayUnits[itemId] || getDefaultInputUnit(unitSymbol)
                });
                return;
            }
        }
        setWastedItems(prev => {
            const currentCount = prev[itemId] || 0;
            const newCount = currentCount + amount;

            if (newCount <= 0) {
                const { [itemId]: _, ...rest } = prev;
                return rest;
            }

            // No submission lock in Wastage tab (lock only applies to Usage component)

            return { ...prev, [itemId]: newCount };
        });
    };

    // Handle preparation quantity modal confirm
    const handlePrepQuantityConfirm = () => {
        const { item, quantity, selectedUnit } = prepQuantityModal;
        const qty = parseFloat(quantity);
        if (!item || isNaN(qty) || qty <= 0) {
            setPrepQuantityModal({ open: false, item: null, quantity: '', selectedUnit: '' });
            return;
        }
        // Convert entered quantity to base unit for storage
        const baseQty = convertToBaseUnit(qty, selectedUnit, item.unitSymbol);
        setWastedItems(prev => ({ ...prev, [item.id]: parseFloat(baseQty.toFixed(6)) }));
        // Remember which display unit the user chose
        setPrepDisplayUnits(prev => ({ ...prev, [item.id]: selectedUnit }));
        setPrepQuantityModal({ open: false, item: null, quantity: '', selectedUnit: '' });
        // Auto-scroll to submit button after a short delay for the modal to close
        setTimeout(() => {
            const el = document.querySelector('.form-actions');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
    };

    // Open preparation quantity modal from card tap or input focus
    const openPrepQuantityModal = (item) => {
        const unitSymbol = item.unitSymbol || getShortUnit(item.unit) || 'g';
        const itemWithSymbol = {
            ...item,
            unitSymbol: unitSymbol
        };
        const currentBaseQty = wastedItems[item.id] || 0;
        // Use the last-chosen unit, or default to the sub-unit (ml for L, g for kg)
        const displayUnit = prepDisplayUnits[item.id] || getDefaultInputUnit(unitSymbol);
        const displayQty = currentBaseQty > 0 ? convertFromBaseUnit(currentBaseQty, displayUnit, unitSymbol) : 0;
        setPrepQuantityModal({
            open: true,
            item: itemWithSymbol,
            quantity: displayQty > 0 ? String(parseFloat(displayQty.toFixed(4))) : '',
            selectedUnit: displayUnit
        });
    };

    // Helper to format preparation quantity for card display
    const formatPrepDisplayQty = (item) => {
        const baseQty = wastedItems[item.id];
        if (!baseQty) return '0';
        const unitSymbol = item.unitSymbol || getShortUnit(item.unit) || 'g';
        const displayUnit = prepDisplayUnits[item.id] || getDefaultInputUnit(unitSymbol);
        const displayQty = convertFromBaseUnit(baseQty, displayUnit, unitSymbol);
        // Show clean number (no trailing zeros)
        const formatted = parseFloat(displayQty.toFixed(4));
        return `${formatted} ${displayUnit}`;
    };

    // Calculate totals
    const { totalItems, totalCost } = useMemo(() => {
        // For No Wastage Recorded, cost is always 0 (no actual wastage)
        const isNonBillingError = wasteReason?.reason_label === "No Wastage Recorded";
        
        return Object.entries(wastedItems).reduce((acc, [id, count]) => {
            // Check both menu items, raw items, and preparation items
            let item = menuItemsWithRecipeCost.find(i => i.id.toString() === id);
            if (!item) {
                item = rawItems.find(i => i.id.toString() === id);
            }
            if (!item) {
                item = preparationItems.find(i => i.id.toString() === id);
            }

            if (item) {
                // For preparations and raw items: count is fractional/raw qty; count as 1 item each for display
                acc.totalItems += (item.isPreparation || item.category === 'Raw Items') ? 1 : count;
                // item.price for preparations = costPerBaseUnit (computed from component ingredient costs)
                // item.price for raw items = avg unit_cost from inventory batches
                // item.price for menu items = manufacturing cost (recipe COGS)
                // No Wastage Recorded items have zero cost (billing mistakes, not actual waste)
                acc.totalCost += isNonBillingError ? 0 : (item.price * count);
            }
            return acc;
        }, { totalItems: 0, totalCost: 0 });
    }, [wastedItems, menuItemsWithRecipeCost, rawItems, preparationItems, wasteReason]);

    // ── Banquet wastage: the chosen function's scope, applied to the grid ──
    //
    // Its dishes, and the raw items and preparations inside their recipes. With
    // no function scope these are the full lists, so every other reason is
    // exactly what it was.
    const scopedDishes = useMemo(
        () => (banquetScopeKeys ? menuItemsWithRecipeCost.filter((i) => banquetScopeKeys.has(String(i.id))) : menuItemsWithRecipeCost),
        [banquetScopeKeys, menuItemsWithRecipeCost]
    );
    const scopedRawItems = useMemo(
        () => (banquetScopeKeys ? rawItems.filter((i) => banquetScopeKeys.has(String(i.id))) : rawItems),
        [banquetScopeKeys, rawItems]
    );
    const scopedPreparationItems = useMemo(
        () => (banquetScopeKeys ? preparationItems.filter((i) => banquetScopeKeys.has(String(i.id))) : preparationItems),
        [banquetScopeKeys, preparationItems]
    );
    // The category chips, narrowed the same way: a dish category only when the
    // function served a dish in it; Raw Items / Preparations only when any are in scope.
    const chipDishOptions = useMemo(() => {
        if (!banquetScopeKeys) return visibleDishOptions;
        const key = useMasterCategories ? 'master_category' : 'category';
        const present = new Set(scopedDishes.map((i) => i[key] || (useMasterCategories ? 'Uncategorized' : '')));
        return dishCategories.filter((c) => present.has(c));
    }, [banquetScopeKeys, visibleDishOptions, dishCategories, scopedDishes, useMasterCategories]);
    const chipExtraCategories = useMemo(() => {
        if (!banquetScopeKeys) return extraCategories;
        return extraCategories.filter((c) => (
            c === 'Raw Items' ? scopedRawItems.length > 0
                : c === 'Preparations' ? scopedPreparationItems.length > 0
                : c === NON_CONSUMABLE_CATEGORY ? scopedRawItems.some((i) => i.isNonConsumable)
                : false
        ));
    }, [banquetScopeKeys, extraCategories, scopedRawItems, scopedPreparationItems]);

    // A category the function does not offer falls back to All.
    useEffect(() => {
        if (!banquetScopeKeys) return;
        if (activeCategory !== 'All' && !chipDishOptions.includes(activeCategory) && !chipExtraCategories.includes(activeCategory)) {
            setActiveCategory('All');
        }
    }, [banquetScopeKeys, activeCategory, chipDishOptions, chipExtraCategories]);

    // A new function (or leaving banquet) starts without the old notice.
    useEffect(() => {
        setBanquetRemovedNotice(null);
    }, [banquetEventId, banquetReasonSelected]);

    // Entries the chosen function did not serve — tapped before it was picked,
    // left over from a different function, or restored from elsewhere — are
    // removed, and the owner is told which. The server refuses them anyway
    // (BANQUET_ITEM_NOT_IN_FUNCTION); this keeps the sheet honest before submit.
    useEffect(() => {
        if (!banquetScopeKeys || !chosenBanquetEvent) return;
        const drop = keysOutsideFunction(wastedItems, chosenBanquetEvent);
        if (drop.length === 0) return;
        const everything = [...menuItemsWithRecipeCost, ...rawItems, ...preparationItems];
        const nameOf = (key) => (everything.find((i) => String(i.id) === String(key)) || {}).name || null;
        setWastedItems((prev) => {
            const next = { ...prev };
            drop.forEach((k) => { delete next[k]; });
            return next;
        });
        setBanquetRemovedNotice(removedItemsNotice(drop.map(nameOf), chosenBanquetEvent.functionName));
    }, [banquetScopeKeys, chosenBanquetEvent, wastedItems, menuItemsWithRecipeCost, rawItems, preparationItems]);

    // Filter displayed items
    const displayedItems = useMemo(() => {
        console.log('🔍 [WASTAGE displayedItems] Computing displayed items...');
        console.log('   menuItemsWithRecipeCost:', menuItemsWithRecipeCost.length, 'items');
        console.log('   rawItems:', rawItems.length, 'items');
        console.log('   activeCategory:', activeCategory);
        console.log('   searchTerm:', searchTerm);
        
        let items = [];

        if (banquetAwaitingFunction) {
            // Banquet wastage: nothing can be picked until the function is.
            items = [];
        } else if (banquetScopeKeys) {
            // Only what the chosen function served. "All" is the whole scope:
            // its dishes, and the raw items and preparations inside them.
            if (activeCategory === 'Raw Items') {
                items = scopedRawItems;
            } else if (activeCategory === NON_CONSUMABLE_CATEGORY) {
                items = scopedRawItems.filter(item => item.isNonConsumable);
            } else if (activeCategory === 'Preparations') {
                items = scopedPreparationItems;
            } else if (activeCategory === 'All') {
                items = [...scopedDishes, ...scopedRawItems, ...scopedPreparationItems];
            } else {
                const key = useMasterCategories ? 'master_category' : 'category';
                items = scopedDishes.filter(item => (item[key] || (useMasterCategories ? 'Uncategorized' : '')) === activeCategory);
            }
        } else if (activeCategory === 'Raw Items') {
            items = rawItems;
        } else if (activeCategory === NON_CONSUMABLE_CATEGORY) {
            // The same raw-item objects, narrowed — so a non-consumable wastes through
            // exactly the path a raw ingredient does (item_type InventoryItem, the item's
            // own unit id, its real unit_cost). No second identity, no duplicate row.
            items = rawItems.filter(item => item.isNonConsumable);
            console.log('   Non Consumable category: raw items =', items.length);
        } else if (activeCategory === 'Preparations') {
            items = preparationItems;
        } else if (activeCategory === 'All') {
            const hasSearch = Boolean(searchTerm && searchTerm.trim());
            if (hasSearch) {
                // When searching in "All", search across ALL item types:
                // menu dishes, raw items (including non-consumable), and preparations
                items = [...menuItemsWithRecipeCost, ...rawItems, ...preparationItems];
            } else {
                // When browsing without search, show menu items AND complimentary raw items
                const complimentaryRaw = rawItems.filter(r => r.isComplimentary);
                items = [...menuItemsWithRecipeCost, ...complimentaryRaw];
            }
            console.log('   All category: candidate items =', items.length);
        } else {
            const key = useMasterCategories ? 'master_category' : 'category';
            items = menuItemsWithRecipeCost.filter(item => {
                const itemCat = item[key] || (useMasterCategories ? 'Uncategorized' : '');
                return itemCat === activeCategory;
            });
            console.log('   Category filter:', activeCategory, '-> found', items.length, 'items');
        }

        // Sort items alphabetically by name first so smartSearch preserves alphabetical order within priority buckets
        let sorted = [...items].sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });

        if (searchTerm && searchTerm.trim()) {
            const beforeSearch = sorted.length;
            sorted = smartSearch(sorted, searchTerm, ['name']);
            console.log('   Search filter:', searchTerm, '-> reduced from', beforeSearch, 'to', sorted.length);
        }
        
        console.log('   Final displayedItems:', sorted.length, 'items');
        sorted.forEach((item, idx) => {
            console.log(`     ${idx + 1}. ${item.name} - Category: ${item.category}`);
        });
        
        return sorted;
    }, [menuItemsWithRecipeCost, rawItems, preparationItems, activeCategory, searchTerm, useMasterCategories,
        banquetAwaitingFunction, banquetScopeKeys, scopedDishes, scopedRawItems, scopedPreparationItems]);

    useEffect(() => {
        // Recompute thumb when item count/layout changes.
        const id = window.requestAnimationFrame(updateItemGridThumb);
        return () => window.cancelAnimationFrame(id);
    }, [displayedItems, updateItemGridThumb]);

    const handleCameraClick = () => {
        fileInputRef.current.click();
    };

    const handleFileSelect = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
            try {
                // Import the image compression utility
                const { autoCompressImage } = await import('../utils/imageCompression');

                const processedPhotos = await Promise.all(files.map(async file => {
                    try {
                        const compressedFile = await autoCompressImage(file);
                        return {
                            file: compressedFile,
                            url: URL.createObjectURL(compressedFile),
                            originalFile: file
                        };
                    } catch (error) {
                        return {
                            file: file,
                            url: URL.createObjectURL(file),
                            compressionFailed: true
                        };
                    }
                }));

                setPhotos(prev => [...prev, ...processedPhotos]);
            } catch (error) {
                const newPhotos = files.map(file => ({
                    file: file,
                    url: URL.createObjectURL(file)
                }));
                setPhotos(prev => [...prev, ...newPhotos]);
            }
        }
        event.target.value = null;
    };

    const handleRemovePhoto = (index) => {
        setPhotos(photos.filter((_, i) => i !== index));
    };

    // Handler functions for missing ingredients modal.
    //
    // A selection key scopes a CHECKBOX; it is never sent to the server. The
    // builder that rebuilt one of these into a resolution payload is gone — see
    // utils/shortageIdentity.js for the loop that caused and why a confirmation
    // now echoes the server's own match_keys instead.
    const parseSelectionKey = (key) => {
        const parts = String(key || '').split('|');
        const itemScope = parts[0] || '';
        const ingredientName = parts.slice(1).join('|') || '';

        let menuItemId = null;
        let itemName = null;
        if (itemScope.startsWith('id:')) {
            menuItemId = itemScope.slice(3);
        } else if (itemScope.startsWith('name:')) {
            itemName = itemScope.slice(5);
        } else {
            itemName = itemScope;
        }

        return { itemScope, menuItemId, itemName, ingredientName };
    };

    const getCurrentScopedSelections = () => {
        // Derived through the SAME builder the modal keyed the checkbox with, so a
        // missing item name cannot leave the scope reading `name:undefined` here
        // while the tick was stored under `name:Unknown Item`. Every selection
        // would then be filtered out and the button would insist nothing was
        // selected when three rows were.
        const currentScope = buildShortageItemScope({
            itemName: shortageData?.itemName,
            menuItemId: shortageData?.menuItemId,
        });

        return Object.keys(selectedMissingItems)
            .filter(k => selectedMissingItems[k])
            .filter(key => {
                const parsed = parseSelectionKey(key);
                return parsed.itemScope === currentScope;
            });
    };

    const handleToggleMissingItem = (selectionKey) => {
        const key = String(selectionKey || '');
        if (!key) return;
        setSelectedMissingItems(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const showMessageModal = (message, { title = 'Notice', onOk = null } = {}) => {
        setMessageModal({ open: true, title, message, onOk });
    };

    const closeMessageModal = () => {
        setMessageModal({ open: false, title: '', message: '', onOk: null });
    };

    const handleConfirmNegativeStock = async () => {
        // Mark selected ingredients as resolved
        const selected = getCurrentScopedSelections();
        
        if (selected.length === 0) {
            showMessageModal('Please select at least one ingredient to confirm', { title: 'Select Ingredients' });
            return;
        }
        
        // A tick is a UI key; the server is answered with the ingredient's OWN
        // published identity. Resolve one to the other here — the same builder the
        // modal ticked with, so a row cannot be looked up under a different key.
        const selectedKeys = new Set(selected);
        const shortageScope = {
            itemName: shortageData?.itemName,
            menuItemId: shortageData?.menuItemId
        };
        const confirmedIngredients = (shortageData?.missingIngredients || []).filter(
            (ing) => selectedKeys.has(buildShortageSelectionKey(ing, shortageScope))
        );

        const confirmedResolvedKeys = new Set();
        confirmedIngredients.forEach((ing) => {
            shortageMatchTokens(ing).forEach((token) => confirmedResolvedKeys.add(token));
        });

        if (confirmedResolvedKeys.size === 0) {
            // No identity to send means the retry would be re-blocked by the same
            // shortage and the user would be staring at this modal again. Say so
            // instead of looping.
            showMessageModal(
                'These ingredients could not be identified for confirmation. Please refresh and try again.',
                { title: 'Cannot Confirm' }
            );
            return;
        }

        const updatedResolvedIngredients = new Set([...resolvedIngredients, ...confirmedResolvedKeys]);

        // Add to resolved ingredients set
        setResolvedIngredients(() => {
            const updated = new Set(updatedResolvedIngredients);
            console.log('🗑️ Total resolved ingredients:', Array.from(updated));
            return updated;
        });

        console.log('🗑️ Filtering out confirmed ingredients:', Array.from(confirmedResolvedKeys));

        // Filter out confirmed ingredients from missing list
        const confirmedSelectionKeys = new Set(
            confirmedIngredients.map((ing) => buildShortageSelectionKey(ing, shortageScope))
        );
        const remainingMissing = (shortageData?.missingIngredients || []).filter((ing) => {
            const isConfirmed = confirmedSelectionKeys.has(buildShortageSelectionKey(ing, shortageScope));
            if (isConfirmed) {
                console.log(`✅ Removing "${ing.name}" from missing list (confirmed negative stock)`);
            }
            return !isConfirmed;
        });

        console.log(`📊 Remaining missing ingredients: ${remainingMissing.length}`);
        
        // Update shortage data with remaining ingredients
        setShortageData(prev => ({
            ...prev,
            missingIngredients: remainingMissing
        }));
        
        setSelectedMissingItems({});
        
        // If no more missing ingredients, retry wastage submission
        if (remainingMissing.length === 0) {
            console.log('✅ All ingredients resolved! Retrying wastage submission...');
            setShowShortageModal(false);
            showMessageModal('Negative stock will be created. Please ensure you add stock soon!', {
                title: 'Notice',
                onOk: () => handleSubmit(updatedResolvedIngredients)
            });
        } else {
            // Show updated modal with remaining ingredients
            console.log('⚠️ Still have missing ingredients, showing updated modal');
            showMessageModal('Negative stock will be created for selected ingredients.', { title: 'Notice' });
            setShowShortageModal(true);
        }
    };

    const handleRemoveFromRecipe = () => {
        // Navigate to itemmap for recipe editing
        const selected = getCurrentScopedSelections();
        
        if (selected.length === 0) {
            showMessageModal('Please select at least one ingredient to remove', { title: 'Select Ingredients' });
            return;
        }

        // Track ingredients being removed. The published identity travels with the
        // name: the return trip marks these resolved too, and a re-derived key would
        // fail to match for exactly the reason described at shortageMatchTokens.
        const selectedKeys = new Set(selected);
        const shortageScope = {
            itemName: shortageData?.itemName,
            menuItemId: shortageData?.menuItemId
        };
        const ingredientsToRemove = (shortageData?.missingIngredients || [])
            .filter((ing) => selectedKeys.has(buildShortageSelectionKey(ing, shortageScope)))
            .map((ing) => ({ name: ing.name, match_keys: shortageMatchTokens(ing) }));

        // Get the menu item from the wasted items
        const menuItemId = shortageData?.menuItemId;
        const menuItemName = shortageData?.itemName;
        
        if (!menuItemId) {
            showMessageModal('Cannot identify menu item to edit', { title: 'Error' });
            return;
        }

        console.log('📍 Navigating to itemmap for:', { menuItemId, menuItemName, ingredientsToRemove });

        // Store wastage state for return navigation
        localStorage.setItem('pendingWastageData', JSON.stringify({
            wastedItems,
            wasteReason,
            recordedBy,
            notes,
            photos,
            ingredientsToRemove, // Track which ingredients user is removing
            shortageData // Keep shortage data to restore modal state
        }));
        
        // Store the recipe ID to auto-select in itemmap
        localStorage.setItem('autoSelectRecipeId', menuItemId.toString());
        localStorage.setItem('cameFromWastage', 'true');

        // Navigate to itemmap
        if (goTo) {
            goTo('itemmap');
        } else {
            window.location.href = '/itemmap';
        }
    };



    // ── T1.8: double-submit protection and resumable retries ──────────────────────
    //
    // Three separate paths can call handleSubmit: the Confirm button, the shortage
    // modal's "Confirm & Create Negative Stock" (~:2351), and the return trip from the
    // recipe editor (~:1131).
    //
    // The submission is now a SINGLE POST to /wastage/batch — one transaction on the
    // server, all items or none (STEP B.7). Two guards remain, for two different races:
    //
    //   · The re-entry guard below stops a fast double-click from starting a second
    //     submission at all. `isSubmitting` is React state and does not update in time;
    //     a ref does. This one is permanent.
    //   · submissionKeyRef stops a RETRY from recording twice when the first attempt
    //     may already have committed — the server replays the original result.
    //
    // The resume ledger's original job (skipping items an earlier pass of the serial
    // loop had already recorded) is gone with the loop: there is no longer any state to
    // resume from, because a failed submission records nothing.
    // Held in a ref so it survives re-renders untouched. See utils/submissionLedger.js.
    const submissionRef = useRef(null);
    if (submissionRef.current === null) {
        submissionRef.current = createSubmissionLedger();
    }
    const submitAbortRef = useRef(null);

    // STEP B.7 — one idempotency key per submission SET.
    //
    // Created when a submission starts and reused by every retry of that same
    // set: the shortage modal's "confirm and go negative", the recipe-editor
    // return trip, and a plain second press of Confirm. That is what makes a
    // retry safe when the FIRST attempt actually committed but its response was
    // lost — the server replays the original result instead of recording again
    // (I3). Cleared only on success or an explicit form reset, so it can never
    // suppress a genuinely new submission.
    const submissionKeyRef = useRef(null);

    const resetSubmissionLedger = useCallback(() => {
        submissionRef.current.reset();
        submissionKeyRef.current = null;
        // A negative-stock confirmation belongs to ONE submission set, exactly as
        // the idempotency key does, and dies with it. It used to outlive the form
        // because it never worked: the keys the client sent matched nothing on the
        // server, so leaving them behind was harmless. Now that they are honoured,
        // keeping them would mean a user who confirmed once silently overdrew every
        // wastage they recorded afterwards, without ever being asked again.
        setResolvedIngredients(new Set());
    }, []);

    // A change of reason or of the chosen banquet event starts a NEW submission set:
    // otherwise a retry after switching events would replay the earlier attempt under
    // its key. Never mid-flight — an in-flight key may already have committed.
    useEffect(() => {
        if (submissionRef.current.isInFlight()) return;
        submissionKeyRef.current = null;
    }, [wasteReason?.reason_id, banquetEventId]);

    /**
     * A v4 UUID. crypto.randomUUID needs a secure context and is missing from
     * some of the Android WebViews this form runs inside, so it falls back to
     * getRandomValues, and then to Math.random. A weaker key is still vastly
     * better than none: its only job is to be unique per submission set.
     */
    const newIdempotencyKey = () => {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
            const bytes = crypto.getRandomValues(new Uint8Array(16));
            bytes[6] = (bytes[6] & 0x0f) | 0x40;
            bytes[8] = (bytes[8] & 0x3f) | 0x80;
            const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
            return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
    };

    // Handle form submission

    const handleSubmit = async (resolvedIngredientsOverride = null) => {
        // T1.8a — re-entry guard. Must be the very first statement: everything below
        // this point mutates stock, and `isSubmitting` (React state) does not update in
        // time to stop a fast double-click.
        if (!submissionRef.current.tryBegin()) {
            console.warn('⛔ [SUBMIT] Ignored — a submission is already in flight');
            return;
        }
        try {
            await runSubmit(resolvedIngredientsOverride);
        } finally {
            submissionRef.current.end();
        }
    };

    /**
     * The confirmation modal's Confirm. A `pcs` ingredient whose Stock In answer is a
     * packet is read back as packets first (utils/piecePackReadback.js); with none in the
     * list this is exactly the old `handleSubmit()`, in the same tick.
     *
     * Only this door asks. The shortage and recipe-editor retries call handleSubmit
     * directly, because they resume a submission the person has already confirmed.
     */
    const handleConfirmWithPiecePacks = async () => {
        if (piecePackCheckingRef.current) return;
        const session = confirmSessionRef.current.id;
        let lines = [];
        const ids = pieceWastageItemIds(wastedItems, rawItems);
        if (ids.length > 0) {
            piecePackCheckingRef.current = true;
            setPiecePackChecking(true);
            try {
                let profiles = piecePackProfiles;
                const missing = ids.filter(id => !(id in profiles));
                if (missing.length > 0) {
                    profiles = { ...profiles, ...((await loadPiecePackProfiles(missing)) || {}) };
                }
                lines = piecePackLines(wastedItems, rawItems, profiles);
            } catch (e) {
                // A read-back that could not be fetched never stands between a person and
                // their wastage — the same rule onboarding follows for this hint.
                console.debug('Piece packet read-back skipped:', e.message);
                lines = [];
            } finally {
                piecePackCheckingRef.current = false;
                setPiecePackChecking(false);
            }
            // Cancelled while the answer was loading: record nothing.
            if (confirmSessionRef.current.id !== session) return;
        }
        if (lines.length === 0) {
            handleSubmit();
            return;
        }
        setConfirmModalOpen(false);
        setPiecePackReview(lines);
    };

    const runSubmit = async (resolvedIngredientsOverride = null) => {
        console.log('🚀 Starting waste submission...', { 
            wasteReason: wasteReason?.reason_label, 
            recordedBy: recordedBy?.name,
            totalItems,
            requiresEvidencePhoto,
            photosCount: photos.length
        });
        
        setBanquetOutcome(null);
        // Waste reason is only required for Waste tab, not for Usage
        if (activeSubTab === 'Waste' && !wasteReason) {
            setError('Please select a reason.');
            return;
        }
        // Banquet Wastage needs its event (Submit is disabled without one; this is
        // the guard for the programmatic retry paths).
        if (banquetReasonSelected && !chosenBanquetEvent) {
            setError(BANQUET_MESSAGES.BANQUET_EVENT_REQUIRED);
            return;
        }
        if (!recordedBy) {
            setError('Please select a staff member.');
            if (isCurrentUserOwner) {
                setStaffModalOpen(true);
            }
            return;
        }
        
        // Allow empty items for "No Wastage Recorded" and "No wastage recorded"
        const isNonBillingError = wasteReason?.reason_label === "No Wastage Recorded";
        const isNoWastageRecorded = wasteReason?.reason_label === "No wastage recorded";
        if (totalItems === 0 && !isNoWastageRecorded && !isNonBillingError) {
            setError('Please add at least one item (unless "No wastage recorded" or "No Wastage Recorded")');
            return;
        }

        // Enforce evidence requirement based on wastage thresholds
        if (requiresEvidencePhoto && photos.length === 0) {
            // Build a message showing which items require photos
            const itemsRequiringPhotos = [];
            for (const [id, result] of Object.entries(thresholdCheckResults)) {
                if (result.photoRequired) {
                    const item = menuItemsWithRecipeCost.find(i => i.id?.toString() === id?.toString());
                    if (item) {
                        itemsRequiringPhotos.push(`${item.name} (${result.enteredQuantity} exceeds threshold of ${result.thresholdQuantity})`);
                    }
                }
            }
            
            const itemsList = itemsRequiringPhotos.join(', ');
            setError(`📸 Photo evidence required! The following items exceed their wastage thresholds: ${itemsList}. Please upload at least one photo.`);
            return;
        }

        // Close confirmation modal and start processing
        setConfirmModalOpen(false);
        setIsSubmitting(true);
        // ✅ FIXED: Show processing modal IMMEDIATELY when user clicks confirm
        setProcessing(true);
        setProcessingProgress(10);
        setProcessingMessage('Preparing to record waste...');
        setError(null);
        console.log('✅ Validation passed, starting submission...');

        // T1.8c — the old safety timeout only re-enabled the UI after 30 s. It did NOT
        // stop the in-flight POST, so the request kept running against the server while
        // the user was told to "try again": the retry then raced a request that was still
        // writing, and both could record the same wastage.
        //
        // Now the timeout aborts the request. The ledger (T1.8b) survives, so a retry
        // resumes from the first item that has not been confirmed recorded.
        const abortController = new AbortController();
        submitAbortRef.current = abortController;
        let timedOut = false;

        const safetyTimeout = setTimeout(() => {
            timedOut = true;
            console.error('⏱️ TIMEOUT: Submission took too long (30s), aborting in-flight request');
            try { abortController.abort(); } catch (_) {}
            setIsSubmitting(false);
            setProcessing(false);
            setError('Submission timed out and was cancelled. Already-recorded items are kept — press Confirm again to finish the rest.');
        }, 30000); // 30 second timeout

        try {
            const items = Object.entries(wastedItems)
                .filter(([menu_item_id, quantity]) => {
                    const qty = parseFloat(quantity);
                    let isValidId = false;
                    if (menu_item_id.toString().startsWith('raw_')) {
                        const rawId = menu_item_id.toString().replace('raw_', '');
                        isValidId = !isNaN(parseInt(rawId)) && parseInt(rawId) > 0;
                    } else if (menu_item_id.toString().startsWith('prep_')) {
                        const prepId = menu_item_id.toString().replace('prep_', '');
                        isValidId = !isNaN(parseInt(prepId)) && parseInt(prepId) > 0;
                    } else {
                        const id = parseInt(menu_item_id);
                        isValidId = !isNaN(id) && id > 0;
                    }
                    return isValidId && !isNaN(qty) && qty > 0;
                })
                .map(([menu_item_id, quantity]) => ({
                    menu_item_id: menu_item_id,
                    quantity: parseFloat(quantity)
                }));

            // Allow empty items for "No Wastage Recorded" - create administrative record
            const isNonBillingError = wasteReason?.reason_label === "No Wastage Recorded";
            if (items.length === 0 && !isNonBillingError) {
                throw new Error('No valid items to record. Please add items with positive quantities.');
            }

            console.log(`📝 Submitting ${items.length} wastage records...`);

            // Special handling for No Wastage Recorded with no items - use administrative endpoint
            if (isNonBillingError && items.length === 0) {
                console.log('📝 No Wastage Recorded with NO items - creating administrative record...');
                
                // Automatically detect if this is online order wastage from reason label
                // Support multiple formats: "Online Order Wastage", "online order", "Online Order", etc.
                const reasonLabel = (wasteReason?.reason_label || '').toLowerCase();
                const isOnlineOrderWastage = reasonLabel.includes('online') && reasonLabel.includes('order');
                
                console.log('🔍 [WASTAGE] Detecting online order wastage:');
                console.log('   Reason Label:', wasteReason?.reason_label);
                console.log('   Is Online Order:', isOnlineOrderWastage);
                
                const payload = {
                    waste_reason_id: wasteReason?.reason_id,
                    notes: notes ? `${notes} - ${new Date().toLocaleString()}` : `No Wastage Recorded recorded via mobile app - ${new Date().toLocaleString()}`,
                    deducted_by_user_id: recordedBy?.user_id || recordedBy?.id || 1,
                    business_id: UnitMappingService.getBusinessId(),
                    reason_type: 'Waste',
                    is_administrative_record: true,
                    estimated_cost_impact: 0,
                    is_online_order_wastage: isOnlineOrderWastage,
                    deducted_date: operationDate || undefined
                };

                console.log('📤 Sending administrative record:', payload);

                try {
                    setProcessingProgress(50);
                    setProcessingMessage('Recording non-billing error...');
                    
                    const response = await http.post('/wastage/administrative', payload);
                    console.log('📥 Administrative response:', response);
                    
                    if (!response || !response.success) {
                        throw new Error(response?.error || 'Failed to record non-billing error');
                    }

                    setProcessingProgress(90);
                    setProcessingMessage('Non-billing error recorded!');
                    
                    // Upload photos if any
                    // T1.7: arguments were reversed here — uploadWastagePhotos(photos, stockOutId)
                    // is the signature (see services/wastageService.js). The old call passed the id
                    // first, so `photos.forEach` threw and every evidence photo attached to a
                    // "No Wastage Recorded" record was silently dropped.
                    if (photos.length > 0 && response.data?.stock_out_id) {
                        try {
                            await uploadWastagePhotos(photos, response.data.stock_out_id);
                            console.log('✅ Evidence photos uploaded successfully');
                        } catch (uploadErr) {
                            console.error('⚠️ Photo upload failed:', uploadErr);
                            // No longer silent: the record exists but its evidence does not.
                            setError('Wastage was recorded, but the evidence photo failed to upload. Please re-attach it.');
                        }
                    }

                    setProcessingProgress(100);
                    setSuccess('✅ Non-billing error recorded successfully!');

                    // Dispatch custom event to clear daily operations lock immediately
                    window.dispatchEvent(new CustomEvent('daily-operations:refresh'));

                    // Show success modal
                    setSuccessModalKind('recorded'); // T1.9 — a real record was created
                    setShowSuccessModal(true);
                    setSuccessMessage('Non-billing error recorded successfully!');
                    
                    // Reset form
                    resetSubmissionLedger(); // T1.8b — explicit form reset clears the resume ledger
                    setWastedItems({});
                    setSearchTerm('');
                    setNotes('');
                    setPhotos([]);
                    setWasteReason(null);
                    // Only clear recordedBy for owners; staff keep their auto-assignment
                    if (isCurrentUserOwner) {
                        setRecordedBy(null);
                    }

                    // Clear database draft
                    clearDraftDatabase();
                    
                    clearTimeout(safetyTimeout);
                    setIsSubmitting(false);
                    setProcessing(false);
                    return;
                } catch (error) {
                    console.error('❌ Failed to record non-billing error:', error);
                    clearTimeout(safetyTimeout);
                    setIsSubmitting(false);
                    setProcessing(false);
                    throw new Error(error.message || 'Failed to record non-billing error');
                }
            }

            // For No Wastage Recorded WITH items, or any other waste reason, use normal flow
            console.log(`📝 Processing ${items.length} items using normal wastage endpoint...`);

            // ── ONE ATOMIC SUBMISSION ──────────────────────────────────────
            //
            // STEP B.7 of WASTAGE_FINAL_FLOW. This was a SERIAL loop of one POST
            // per item, and every POST that succeeded had already written a
            // StockOutRecord and deducted stock — there was no transaction
            // spanning the loop. A failure on item 4 of 7 left three items
            // recorded, three not, and the user staring at an error with no way
            // to tell which. The submission-resume ledger existed only to make
            // that survivable.
            //
            // It is now a single POST to /wastage/batch: one transaction on the
            // server, all items or none. The idempotency key makes a retry — a
            // double click, a timeout, the shortage modal's "confirm and go
            // negative" — return the ORIGINAL result instead of recording twice.
            const results = [];
            const tsrErrors = [];
            let totalEstimatedCost = 0;
            const effectiveResolvedIngredients = resolvedIngredientsOverride || resolvedIngredients;

            // Automatically detect online order wastage from the reason label.
            // NOTE: still a label test on the client. The server decides for
            // itself; STEP D moves this to WastageReasons.is_online_order.
            const reasonLabel = (wasteReason?.reason_label || '').toLowerCase();
            const isOnlineOrderWastage = reasonLabel.includes('online') && reasonLabel.includes('order');

            // Resolve each row to its real id, type and unit. unit_id used to be
            // hard-coded to 22 (Plate) for EVERY row, including raw materials
            // measured in grams and preparations measured in millilitres, so the
            // stored unit disagreed with the stored quantity. Each type now
            // sends the unit it is actually counted in (STEP B.1).
            const describeRow = (item) => {
                const key = item.menu_item_id.toString();
                if (key.startsWith('raw_')) {
                    const rawItemId = key.replace('raw_', '');
                    const actualItem = rawItems.find(r => r.originalId == rawItemId || r.id === item.menu_item_id);
                    return {
                        actualItem,
                        itemId: actualItem ? actualItem.originalId : parseInt(rawItemId),
                        itemType: 'InventoryItem',
                        unitId: resolveRowUnitId(actualItem, 'InventoryItem'),
                    };
                }
                if (key.startsWith('prep_')) {
                    const prepItemId = key.replace('prep_', '');
                    const actualItem = preparationItems.find(r => r.originalId == prepItemId || r.id === item.menu_item_id);
                    return {
                        actualItem,
                        itemId: actualItem ? actualItem.originalId : parseInt(prepItemId),
                        itemType: 'Preparation',
                        unitId: resolveRowUnitId(actualItem, 'Preparation'),
                    };
                }
                const itemId = parseInt(key);
                const actualItem = menuItemsWithRecipeCost.find(m => m.id == itemId);
                // A plate is genuinely the unit a dish is wasted in.
                return { actualItem, itemId, itemType: 'MenuItem', unitId: resolveRowUnitId(actualItem, 'MenuItem') };
            };

            // Banquet Wastage: `banquet_event_id` on every item. An ordinary reason
            // gets {} here — no key at all — so its payload is unchanged.
            const banquetFields = banquetItemFields(banquetReasonSelected, chosenBanquetEvent);
            const rowsByItemId = new Map();
            const batchItems = items.map((item) => {
                const { actualItem, itemId, itemType, unitId } = describeRow(item);
                rowsByItemId.set(`${itemType}:${itemId}`, { item, actualItem });

                const itemPrice = actualItem ? actualItem.price : 0;
                return {
                    item_id: itemId,
                    item_type: itemType,
                    quantity: item.quantity,
                    unit_id: unitId,
                    waste_reason_id: wasteReason?.reason_id || null,
                    notes: notes
                        ? `${notes} - ${new Date().toLocaleString()}`
                        : `${activeSubTab === 'Usage' ? 'Usage' : 'Waste'} recorded via mobile app - ${new Date().toLocaleString()}`,
                    deducted_by_user_id: recordedBy?.user_id || recordedBy?.id || 1,
                    business_id: UnitMappingService.getBusinessId(),
                    estimated_cost_impact: item.quantity * itemPrice,
                    reason_type: activeSubTab === 'Usage' ? 'Usage' : 'Waste',
                    resolvedIngredients: Array.from(effectiveResolvedIngredients),
                    is_online_order_wastage: isOnlineOrderWastage,
                    deducted_date: operationDate || undefined,
                    ...banquetFields,
                };
            });

            // One key per submission SET, reused on every retry of that set.
            // Created here and cleared only on success or an explicit form reset,
            // so the shortage-modal retry re-posts under the same key: if the
            // first attempt actually committed before the response was lost, the
            // retry returns that result instead of recording a second time (I3).
            if (!submissionKeyRef.current) {
                submissionKeyRef.current = newIdempotencyKey();
            }

            console.log(`📤 Submitting ${batchItems.length} item(s) as ONE batch`, {
                idempotency_key: submissionKeyRef.current,
            });

            let batchResponse;
            try {
                // A Locked month (continuous closing stock) offers a prior-month adjustment.
                batchResponse = await postWithLockedRetry(
                    '/wastage/batch',
                    // Body-level date: the server resolves the submission's day from
                    // here, once, rather than trusting the items to agree with each other.
                    {
                        idempotency_key: submissionKeyRef.current,
                        deducted_date: operationDate || undefined,
                        items: batchItems,
                    },
                    'priorMonthAdjustment',
                    { signal: abortController.signal }
                );
            } catch (batchError) {
                // An aborted request has an UNKNOWN outcome — the server may have
                // committed it. The key is deliberately KEPT so pressing Confirm
                // again replays it rather than recording a second copy.
                if (batchError?.name === 'AbortError' || timedOut) {
                    console.warn('⏹️ [SUBMIT] Aborted mid-batch; the idempotency key is kept for the retry');
                    return;
                }

                const data = batchError?.data;

                // Bill-error / TSR rejections. Nothing was recorded — the batch is
                // atomic — so this reports the item the server stopped on rather
                // than a per-item tally.
                if (data?.details && (
                    data.details.reason_code === 'TSR_NOT_FOUND' ||
                    data.details.reason_code === 'ITEM_NOT_IN_TSR' ||
                    data.details.reason_code === 'QUANTITY_EXCEEDS_TSR'
                )) {
                    const failedIndex = data.failed_item_index ?? 0;
                    const failed = batchItems[failedIndex];
                    const row = failed && rowsByItemId.get(`${failed.item_type}:${failed.item_id}`);
                    tsrErrors.push({
                        item_name: row?.actualItem?.name || data.details.item_name || 'Unknown Item',
                        item_id: failed?.item_id,
                        wastage_quantity: failed?.quantity,
                        sold_quantity: data.details.sold_quantity || 0,
                        reason_code: data.details.reason_code,
                        error: data.error,
                        message: data.message,
                    });
                } else {
                    const isNoWastageRecorded = wasteReason?.reason_label === "No Wastage Recorded";
                    const hasShortages = !isNoWastageRecorded && data && (
                        (Array.isArray(data.insufficientIngredients) && data.insufficientIngredients.length > 0) ||
                        (Array.isArray(data.details) && data.details.length > 0)
                    );

                    if (hasShortages) {
                        // The shortage modal reads error.data. With the batch
                        // endpoint the shortages are already SUMMED across every
                        // item, so two dishes sharing an ingredient are reported
                        // once, with the combined requirement.
                        const failedIndex = data.failed_item_index;
                        const failed = failedIndex != null ? batchItems[failedIndex] : null;
                        const nameOf = (row) =>
                            row && rowsByItemId.get(`${row.item_type}:${row.item_id}`)?.actualItem?.name;
                        const enhanced = new Error('Failed to record wastage: missing ingredients');
                        enhanced.data = data;
                        enhanced.status = batchError.status;
                        enhanced.menuItemId = failed?.item_id;
                        enhanced.itemType = failed?.item_type;
                        // The batch pre-flight sums every item's requirement, so its 400
                        // names no single row. Say what the shortage is actually against
                        // rather than falling back to the "this item" placeholder.
                        enhanced.itemName = nameOf(failed)
                            || (batchItems.length === 1 ? nameOf(batchItems[0]) : null)
                            || (batchItems.length > 1 ? 'this submission' : null);
                        throw enhanced;
                    }

                    if (isNoWastageRecorded) {
                        console.log('⚠️ Skipping ingredient shortage check for "No Wastage Recorded" reason');
                    } else {
                        const enhanced = new Error(
                            `Failed to record wastage: ${data?.error || batchError.message || 'Unknown error'}`
                        );
                        enhanced.data = data;
                        enhanced.status = batchError.status;
                        throw enhanced;
                    }
                }
            }

            if (batchResponse && batchResponse.success) {
                setProcessingProgress(30);
                setProcessingMessage('Recording waste items...');

                const recorded = Array.isArray(batchResponse.data) ? batchResponse.data : [];
                recorded.forEach((row, index) => {
                    const source = batchItems[index];
                    const original = source && rowsByItemId.get(`${source.item_type}:${source.item_id}`);
                    results.push({
                        success: true,
                        data: row,
                        _submitted_menu_item_id: original?.item?.menu_item_id,
                    });
                    totalEstimatedCost += parseFloat(row.estimated_cost || source?.estimated_cost_impact || 0);
                });

                // The server reports what it could not do rather than swallowing
                // it (I7) — a missing approval row, a photo that did not attach,
                // a cache it could not clear.
                if (Array.isArray(batchResponse.warnings) && batchResponse.warnings.length > 0) {
                    console.warn('⚠️ [SUBMIT] Recorded with warnings:', batchResponse.warnings);
                }

                // Banquet Wastage: the success modal names the function, the count and
                // the recorded total, and lists the server's banquet warnings.
                if (banquetFields.banquet_event_id) {
                    setBanquetOutcome({
                        message: banquetSuccessMessage({
                            functionName: chosenBanquetEvent?.functionName,
                            itemCount: batchItems.length,
                            totalCost: sumEstimatedCost(recorded),
                        }),
                        warnings: banquetWarningMessages(batchResponse.warnings),
                    });
                }
            }

            
            if (tsrErrors.length > 0) {
                console.log('🎨 Showing TSR Error modal with all affected items:', tsrErrors);
                
                
                const firstError = tsrErrors[0];
                let title = 'Bill Error Not Allowed';
                let message = `${tsrErrors.length} item(s) cannot be recorded with Bill Error reason.`;
                
               
                const allSameType = tsrErrors.every(e => e.reason_code === firstError.reason_code);
                if (allSameType) {
                    if (firstError.reason_code === 'TSR_NOT_FOUND') {
                        title = 'No Sales Record Found';
                        message = firstError.message;
                    } else if (firstError.reason_code === 'ITEM_NOT_IN_TSR') {
                        title = 'Items Not in Sales Record';
                        message = `The following items were not sold on the selected date and cannot use Bill Error reason.`;
                    } else if (firstError.reason_code === 'QUANTITY_EXCEEDS_TSR') {
                        title = 'Wastage Exceeds Sales';
                        message = `The wastage quantities exceed the sold quantities for these items.`;
                    }
                }
                
                setTSRErrorData({
                    title: title,
                    message: message,
                    items: tsrErrors, 
                    allItems: tsrErrors 
                });
                setShowTSRErrorModal(true);
                setError(null);
                setProcessing(false);
                setProcessingProgress(0);
                setProcessingMessage('');
                setIsSubmitting(false);
                clearTimeout(safetyTimeout);
                return; 
            }

            console.log(`✅ All ${results.length} wastage records submitted successfully`);
            console.log('📊 Summary:', { totalItems: items.length, totalEstimatedCost: totalEstimatedCost.toFixed(2) });
            setProcessingProgress(70);
            setProcessingMessage('Waste records created successfully...');

            
            if (results.length === 0) {
                throw new Error('No wastage records were created');
            }

            const summaryResult = {
                success: true,
                data: {
                    total_items: items.length,
                    total_estimated_cost: totalEstimatedCost.toFixed(2),
                    stock_out_id: results[0]?.data?.stock_out_id
                }
            };

            const result = summaryResult;

            if (result.success) {
                
                let targetStockOutId = result.data.stock_out_id;
                if (evidenceTargetMenuItemId) {
                    const match = results.find(r => r._submitted_menu_item_id?.toString() === evidenceTargetMenuItemId?.toString());
                    if (match?.data?.stock_out_id) targetStockOutId = match.data.stock_out_id;
                }

                if (photos.length > 0 && targetStockOutId) {
                    console.log('📸 Uploading photos...', { count: photos.length, targetStockOutId });
                    setProcessingProgress(85);
                    setProcessingMessage('Uploading photos...');
                    try {
                        const photoUploadResult = await uploadWastagePhotos(photos, targetStockOutId);
                        console.log('📸 Photo upload result:', photoUploadResult);
                        if (!photoUploadResult?.success) {
                            throw new Error(photoUploadResult?.error || 'Photo upload failed');
                        }
                        const photoMsg = `\n${t('waste.successMessage')}`;
                        setSuccessMessage(photoMsg);
                    } catch (photoError) {
                        console.error('📸 Photo upload error:', photoError);
                        
                        if (requiresEvidencePhoto) {
                            setError('Image upload failed. Please try uploading again. The waste record was created but requires a photo.');
                            setIsSubmitting(false);
                            
                            setProcessing(false);
                            setProcessingProgress(0);
                            setProcessingMessage('');
                            return;
                        } else {
                            setSuccessMessage(t('waste.successMessage'));
                        }
                    }
                } else {
                    console.log('📸 No photos to upload or no target stock_out_id');
                    setSuccessMessage(t('waste.successMessage'));
                }

                console.log('🎉 Waste submission complete!');
                // T1.8b — every item in the set is recorded; the resume ledger has done
                // its job. Cleared here and ONLY here (plus explicit form resets) so a
                // partial failure keeps its memory.
                resetSubmissionLedger();
                setProcessingProgress(100);
                setProcessingMessage('Complete!');
                
                // Dispatch custom event to clear daily operations lock immediately
                window.dispatchEvent(new CustomEvent('daily-operations:refresh'));
                
                // Show success modal after brief delay
                setTimeout(() => {
                    setProcessing(false);
                    setProcessingProgress(0);
                    setProcessingMessage('');
                    setSuccessModalKind('recorded'); // T1.9
                    setShowSuccessModal(true);
                }, 1000);
                
                // Clear all form data automatically
                setWastedItems({});
                setNotes('');
                setPhotos([]);
                setWasteReason(null);
                // Only clear recordedBy for owners; staff keep their auto-assignment
                if (isCurrentUserOwner) {
                    setRecordedBy(null);
                }
                setActiveCategory('All');
                setSearchTerm('');
                setThresholdCheckResults({});
                const businessId = localStorage.getItem('businessId') || 'unknown';
                localStorage.removeItem(`stockOutDraft_${businessId}`);
                
                // Clear database draft
                clearDraftDatabase();
                
                console.log('✅ Form cleared completely');

                // Refresh raw items data to reflect updated quantities
                fetchRawItems();

                // 🔥 FIX: Refresh available plates after wastage submission
                loadAvailablePlates();

                // Fire an important notification to the user that the stock-out report was submitted
                // Run this in the background without blocking the UI
                console.log('🔔 Sending notification (non-blocking)...');
                Promise.resolve().then(async () => {
                    try {
                        const businessId = UnitMappingService.getBusinessId();
                        // Notify the currently signed-in user
                        const userId = (() => {
                            try {
                                const token = window.localStorage.getItem('authToken');
                                if (token && token.split('.').length === 3) {
                                    const payload = JSON.parse(atob(token.split('.')[1]));
                                    const id = Number(payload?.userId ?? payload?.user_id ?? payload?.id);
                                    if (!Number.isNaN(id) && id > 0) return id;
                                }
                                const userStr = window.localStorage.getItem('user');
                                if (userStr) {
                                    const u = JSON.parse(userStr);
                                    const id = Number(u?.userId ?? u?.user_id ?? u?.id);
                                    if (!Number.isNaN(id) && id > 0) return id;
                                }
                            } catch (_) {}
                            return 1;
                        })();
                        const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
                        await http.post('/notifications/waste/report-submitted', {
                            businessId,
                            userId,
                            date: today,
                            totalItems: result.data.total_items,
                            totalValue: Number(result.data.total_estimated_cost || 0)
                        });
                        console.log('🔔 Notification sent successfully');
                        // Broadcast a refresh event so the Notifications UI updates immediately
                        try { if (typeof window !== 'undefined') window.dispatchEvent(new Event('refreshNotifications')); } catch (_) {}
                    } catch (notifErr) {
                        console.warn('Failed to send critical report-submitted notification:', notifErr?.message || notifErr);
                    }
                }).catch(err => console.warn('Notification background task error:', err));
            } else {
                // Handle specific error cases
                if (result.error && result.error.includes('Insufficient stock')) {
                    setError(`Insufficient stock: ${result.error}`);
                } else {
                    throw new Error(result.error || result.details || 'Failed to record waste');
                }
            }
        } catch (error) {
            console.error('❌ ================ WASTAGE SUBMISSION ERROR ================');
            console.error('❌ Error object:', error);
            console.error('❌ Error name:', error?.name);
            console.error('❌ Error message:', error?.message);
            console.error('❌ Error status:', error?.status);
            console.error('❌ Error data:', error?.data);
            console.error('❌ Error stack:', error?.stack);
            console.error('❌ ========================================================');
            
            // Also log to alert in development mode
            if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
                console.warn('🚨 DEV MODE: Error details logged above ☝️');
            }
            
            let errorMessage;
            
            // Parse error data for better user messages
            const errorData = error?.data;
            
            // ✅ PRIORITY CHECK: TSR Validation Error - Show beautiful modal FIRST
            if (errorData && errorData.details && 
                (errorData.details.reason_code === 'TSR_NOT_FOUND' || 
                 errorData.details.reason_code === 'ITEM_NOT_IN_TSR' ||
                 errorData.details.reason_code === 'QUANTITY_EXCEEDS_TSR')) {
                console.log('🎨 TSR Validation Error detected, showing modal:', errorData);
                
                setTSRErrorData({
                    title: errorData.error || 'Bill Error Not Allowed',
                    message: errorData.message || 'Failed to validate TSR data',
                    details: errorData.details
                });
                setShowTSRErrorModal(true);
                setError(null); // Clear text error since we're showing modal
                setProcessing(false);
                setProcessingProgress(0);
                setProcessingMessage('');
                setIsSubmitting(false);
                return; // Exit early
            }
            
            // Banquet Wastage refusals carry a `code` the generic branch below never
            // reads; they get the §12 messages. PERIOD_RECONCILED / a closing count in
            // progress are mapped too while the banquet reason is selected. Ordinary
            // reasons keep their existing handling.
            if (banquetReasonSelected || isBanquetRefusalCode(errorData)) {
                const refusal = banquetRefusalMessage(errorData, { event: chosenBanquetEvent });
                if (refusal) {
                    setError(refusal);
                    if (errorData?.code === 'BANQUET_EVENT_NOT_FOUND') {
                        // The event was reverted. Unchoose it and reload the list; the
                        // whole batch was refused, so the retry gets a fresh key.
                        submissionKeyRef.current = null;
                        setBanquetEventId(null);
                        setBanquetEventsReload((n) => n + 1);
                    }
                    if (errorData?.code === 'BANQUET_ITEM_NOT_IN_FUNCTION') {
                        // The function's dishes changed since the list loaded (it was
                        // reverted and re-entered). Reload it; the clean-up then removes
                        // what it no longer serves. The batch was refused whole, so the
                        // retry gets a fresh key.
                        submissionKeyRef.current = null;
                        setBanquetEventsReload((n) => n + 1);
                    }
                    setProcessing(false);
                    setProcessingProgress(0);
                    setProcessingMessage('');
                    return; // the finally block still runs
                }
            }

            if (error && error.name === 'TypeError' && typeof error.message === 'string' && error.message.includes('fetch')) {
                errorMessage = 'Network error. Please check your connection.';
                setError(errorMessage);
            } else {
                // Parse error data for better user messages
                const errorData = error?.data;
                
                console.log('🔍 Error handling - checking error type:', {
                    hasErrorData: !!errorData,
                    hasInsufficientIngredients: !!(errorData?.insufficientIngredients),
                    hasDetails: !!(errorData?.details),
                    errorMessage: error.message,
                    errorData: errorData,
                    fullError: error
                });
                
                // ✅ CHECK 1: Insufficient ingredients error - SHOW MODAL
                // Check both error.data.insufficientIngredients and error.message for ingredient shortage
                console.log('🔍 Checking if this is an insufficient ingredients error:', {
                    hasInsufficientIngredientsFlag: !!(errorData && errorData.insufficientIngredients),
                    hasMissingIngredientsInMessage: !!(error.message && error.message.includes('missing ingredients')),
                    willShowModal: !!((errorData && errorData.insufficientIngredients) || (error.message && error.message.includes('missing ingredients')))
                });
                
                if ((errorData && errorData.insufficientIngredients) || 
                    (error.message && error.message.includes('missing ingredients'))) {
                    
                    // Get the item name from the error message
                    // Backend format: "Cannot waste 2 Virgin Mojito - missing ingredients:"
                    const itemNameMatch = error.message?.match(/Cannot waste \d+\s+(.+?)\s+-\s+missing/);
                    const itemName = error.itemName
                        || (itemNameMatch ? itemNameMatch[1].trim() : 'this item');

                    // Extract ingredient details - USE 'details' not 'insufficientIngredients'
                    // Backend returns: 
                    //   - insufficientIngredients: array of strings (for display)
                    //   - details: array of objects with {name, required, available, shortage, unit}
                    let missingIngredients = errorData?.details || [];
                    
                    console.log('🔍 Extracted missing ingredients:', {
                        fromDetails: errorData?.details,
                        fromInsufficientIngredients: errorData?.insufficientIngredients,
                        finalValue: missingIngredients,
                        isArray: Array.isArray(missingIngredients),
                        length: missingIngredients?.length
                    });
                    
                    // If no ingredient details, it means the item has no recipe configured
                    if (!missingIngredients || missingIngredients.length === 0) {
                        console.log('⚠️ No ingredient details found - item may not have a recipe configured');
                        setError(
                            `Cannot waste "${itemName}":\n\n` +
                            `❌ This item has no recipe configured.\n\n` +
                            `To waste menu items, you must first:\n` +
                            `1. Go to Menu Management\n` +
                            `2. Configure ingredients for "${itemName}"\n` +
                            `3. Then try wasting again\n\n` +
                            `OR\n\n` +
                            `You can waste raw ingredients directly from the Inventory page.`
                        );
                        setIsSubmitting(false);
                        return;
                    }
                    
                    console.log('🔍 Raw error data:', errorData);
                    console.log('🔍 Missing ingredients array:', missingIngredients);
                    
                    // ✅ GET MENU ITEM ID FROM ERROR OBJECT (set during submission)
                    const failedMenuItemId = error.menuItemId || null;
                    
                    console.log('✅ Ingredient shortage detected! Showing modal:', {
                        itemName,
                        failedMenuItemId,
                        itemType: error.itemType,
                        missingIngredients,
                        errorData,
                        isArray: Array.isArray(missingIngredients)
                    });
                    
                    // Show professional modal instead of text error
                    // Ensure missingIngredients is always an array
                    setShortageData({
                        itemName: itemName,
                        menuItemId: failedMenuItemId,
                        missingIngredients: Array.isArray(missingIngredients) ? missingIngredients : []
                    });
                    setSelectedMissingItems({}); // Clear stale selections when a new failed item triggers shortage modal
                    setShowShortageModal(true);
                    setError(null); // Clear text error since we're showing modal
                    setIsSubmitting(false);
                    // ✅ Close processing modal when showing ingredient shortage modal
                    setProcessing(false);
                    setProcessingProgress(0);
                    setProcessingMessage('');
                    return; // Exit early to prevent showing text error
                }
                // ✅ CHECK 2: Frontend fallback reason error
                else if (errorData && (errorData.error?.includes('Frontend fallback reason') || errorData.error?.includes('fe_'))) {
                    errorMessage = `❌ Invalid wastage reason selected.\n\n` +
                                 `You selected a temporary placeholder reason (fe_*) which is not valid.\n\n` +
                                 `➡️ SOLUTION:\n` +
                                 `1. Press Ctrl+Shift+R (Windows) or Cmd+Shift+R (Mac) to refresh\n` +
                                 `2. Select a real wastage reason from the dropdown\n` +
                                 `3. Try again\n\n` +
                                 `If problem persists, add wastage reasons in Settings page.`;
                    setError(errorMessage);
                }
                // ✅ CHECK 3: Item not found error (deleted or sync issue)
                else if (error.message && error.message.includes('Item not found')) {
                    // Extract item details from error message
                    const itemMatch = error.message.match(/Item not found: (\w+) with ID (\d+)/);
                    const itemType = itemMatch ? itemMatch[1] : 'Item';
                    const itemId = itemMatch ? itemMatch[2] : 'unknown';
                    
                    errorMessage = `❌ Item Not Found (ID: ${itemId})\n\n` +
                                 `The ${itemType.toLowerCase()} you're trying to waste doesn't exist in the database.\n\n` +
                                 `This usually happens when:\n` +
                                 `• The item was recently deleted\n` +
                                 `• The data needs to be refreshed\n\n` +
                                 `➡️ SOLUTION:\n` +
                                 `1. Press F5 or pull down to refresh the page\n` +
                                 `2. Re-select the items you want to waste\n` +
                                 `3. Try submitting again\n\n` +
                                 `If this item shouldn't have been deleted, contact your administrator.`;
                    setError(errorMessage);
                }
                // ✅ CHECK 4: Generic error - show simple message without JSON dump
                else {
                    // Extract clean error message without JSON details or embedded objects
                    let cleanMessage = error.message || 'Unknown error';
                    
                    // Remove any embedded JSON or Details sections
                    cleanMessage = cleanMessage.split(' (Details:')[0];
                    cleanMessage = cleanMessage.split(' {"success"')[0];
                    
                    const status = error?.status ? ` (HTTP ${error.status})` : '';
                    errorMessage = `Failed to record waste: ${cleanMessage}${status}`;
                    setError(errorMessage);
                }
            }
            
            // Reset processing state on error
            setProcessing(false);
            setProcessingProgress(0);
            setProcessingMessage('');
            console.error('❌ Error handling complete');
        } finally {
            console.log('🏁 Finally block: Resetting isSubmitting to false');
            clearTimeout(safetyTimeout); // Clear the safety timeout
            submitAbortRef.current = null; // T1.8c
            setIsSubmitting(false);
        }
    };

    // Only require wasteReason for Waste tab, not Usage
    // Allow confirmation without items for "No Wastage Recorded" and "No wastage recorded"
    const isNonBillingError = wasteReason?.reason_label === "No Wastage Recorded";
    const isNoWastageRecorded = wasteReason?.reason_label === "No wastage recorded";
    const isConfirmDisabled = (activeSubTab === 'Waste' && !wasteReason) || (totalItems === 0 && !isNoWastageRecorded && !isNonBillingError) || (requiresEvidencePhoto && photos.length === 0);

    // Helper to compute digit places for Counter based on the number of digits
    const getPlaces = (num) => {
        const n = Math.max(0, Math.floor(Math.abs(Number(num) || 0)));
        const len = Math.max(1, Math.floor(Math.log10(n || 1)) + 1);
        const places = [];
        for (let i = len - 1; i >= 0; i--) places.push(10 ** i);
        return places;
    };

    const sortedWasteReasons = useMemo(() => {
        const uniqueReasons = wasteReasons.filter(
            (reason, index, self) => index === self.findIndex(r => r.reason_id === reason.reason_id)
        );
        const normalizeLabel = (label) => (label || '').toLowerCase();
        const isNoWastage = (reason) => normalizeLabel(reason.reason_label) === 'no wastage recorded';
        const isBillErrors = (reason) => normalizeLabel(reason.reason_label) === 'bill errors';

        return [...uniqueReasons].sort((a, b) => {
            if (isNoWastage(a) && !isNoWastage(b)) return -1;
            if (!isNoWastage(a) && isNoWastage(b)) return 1;
            if (isBillErrors(a) && !isBillErrors(b)) return 1;
            if (!isBillErrors(a) && isBillErrors(b)) return -1;
            return 0;
        });
    }, [wasteReasons]);

    return (
        <div className="stock-out-container" style={isLoading ? { overflow: 'hidden', maxHeight: '100vh' } : {}}>
            <div className="stock-out-page">
                <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    multiple
                />

                {/* Error Message */}
                {error && (
                    <div style={{
                        backgroundColor: '#f8d7da',
                        color: '#721c24',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        border: '1px solid #f5c6cb',
                        whiteSpace: 'pre-wrap',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        maxWidth: '100%',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word'
                    }}>
                        {error}
                    </div>
                )}

                {/* Success Message is now shown only in the modal */}

                {isLoading ? (
                    <div className="stockout-loading-center">
                        <div className="stockout-loading-brand">
                            <img
                                src="/invexis-logo.png"
                                alt="Invexis"
                                loading="eager"
                                fetchpriority="high"
                                className="stockout-loading-logo"
                            />
                            <div className="stockout-loading-ring"></div>
                        </div>
                        <p className="stockout-loading-text">{t('common.loading')}</p>
                    </div>
                ) : (
                    <>
                        <StandardScrollStyles />
                        <div className="waste-subheader no-gap-container">
                            <div className="stock-tabs" style={{ display: 'flex', width: '100%' }}>
                                <button
                                    className={`tab-btn ${activeSubTab === 'Usage' ? 'active' : ''}`}
                                    onClick={() => !submissionLock.isActive && setActiveSubTab('Usage')}
                                    aria-pressed={activeSubTab === 'Usage'}
                                    style={{ 
                                        flex: 1,
                                        opacity: submissionLock.isActive ? 0.5 : 1,
                                        cursor: submissionLock.isActive ? 'not-allowed' : 'pointer'
                                    }}
                                    disabled={submissionLock.isActive}
                                >
                                    {t('stockOut.usage')}
                                </button>
                                <button
                                    className={`tab-btn ${activeSubTab === 'Waste' ? 'active' : ''}`}
                                    onClick={() => !submissionLock.isActive && setActiveSubTab('Waste')}
                                    aria-pressed={activeSubTab === 'Waste'}
                                    style={{ 
                                        flex: 1,
                                        opacity: submissionLock.isActive ? 0.5 : 1,
                                        cursor: submissionLock.isActive ? 'not-allowed' : 'pointer'
                                    }}
                                    disabled={submissionLock.isActive}
                                >
                                    {t('stockOut.waste')}
                                </button>
                            </div>
                        </div>

                        {/* Render Usage component when Usage tab is active */}
                        {activeSubTab === 'Usage' && <Usage onItemSelected={startSubmissionLock} submissionLock={submissionLock} />}

                        {/* Render Waste form when Waste tab is active */}
                        {activeSubTab === 'Waste' && (
                            <>
                                {/* Wastage Reasons Dropdown Box */}
                                <div 
                                    className="wastage-reasons-dropdown-container no-gap-container" 
                                    style={{
                                        marginBottom: '8px',
                                        position: 'relative',
                                        marginTop: '8px',
                                    }}
                                >
                                    <div 
                                        className="wastage-reason-selector-box"
                                        onClick={() => setReasonModalOpen(!isReasonModalOpen)}
                                        style={{
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '12px',
                                            padding: '0.9rem 1rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            transition: 'none',
                                            outline: 'none'
                                        }}
                                    >
                                        <div style={{ flex: 1, textAlign: 'left' }}>
                                            {wasteReason ? (
                                                <div style={{
                                                    fontSize: '16px',
                                                    color: '#000',
                                                    textAlign: 'left'
                                                }}>
                                                    <span style={{fontWeight: '600'}}>Reason:</span> {translateWasteReason(wasteReason.reason_label)}
                                                </div>
                                            ) : (
                                                <div style={{
                                                    fontSize: '14px',
                                                    color: '#9ca3af',
                                                    fontWeight: '500',
                                                    textAlign: 'left'
                                                }}>
                                                    Select Wastage Reason
                                                </div>
                                            )}
                                        </div>
                                        <svg 
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                transition: 'transform 0.2s ease',
                                                transform: isReasonModalOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                color: '#6b7280'
                                            }}
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>

                                    {/* Dropdown List */}
                                    {isReasonModalOpen && (
                                        <div 
                                            className="wastage-reasons-dropdown"
                                            style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: '0',
                                                right: '0',
                                                marginTop: '8px',
                                                backgroundColor: '#ffffff',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '12px',
                                                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                                                maxHeight: '400px',
                                                overflowY: 'auto',
                                                zIndex: 1000
                                            }}
                                        >
                                            {wasteReasons
                                                .filter((reason, index, self) =>
                                                    index === self.findIndex(r => r.reason_id === reason.reason_id)
                                                )
                                                .map(r => (
                                                    <div
                                                        key={r.reason_id}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            // The server marks a reason is_disabled for THIS date —
                                                            // Bill Error on a day with no TSR. Refusing the click here
                                                            // is what turns a 400 at submit into a visible "not on
                                                            // this day".
                                                            if (r.is_disabled) return;
                                                            setWasteReason(r);
                                                            setReasonModalOpen(false);
                                                        }}
                                                        title={r.is_disabled ? (r.disabled_reason || 'Not available for this date') : undefined}
                                                        style={{
                                                            padding: '12px 16px',
                                                            cursor: r.is_disabled ? 'not-allowed' : 'pointer',
                                                            opacity: r.is_disabled ? 0.45 : 1,
                                                            borderBottom: '1px solid #f3f4f6',
                                                            transition: 'background-color 0.15s ease'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (r.is_disabled) return;
                                                            e.currentTarget.style.backgroundColor = '#f9fafb';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = '#ffffff';
                                                        }}
                                                    >
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between'
                                                        }}>
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{
                                                                    fontSize: '14px',
                                                                    fontWeight: '600',
                                                                    color: '#111827'
                                                                }}>
                                                                    {translateWasteReason(r.reason_label)}
                                                                </div>

                                                            </div>
                                                            {wasteReason?.reason_id === r.reason_id && (
                                                                <svg 
                                                                    style={{
                                                                        width: '20px',
                                                                        height: '20px',
                                                                        color: '#3b82f6'
                                                                    }}
                                                                    fill="currentColor" 
                                                                    viewBox="0 0 20 20"
                                                                >
                                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                            {wasteReasons.length === 0 && (
                                                <div style={{
                                                    padding: '24px 16px',
                                                    textAlign: 'center',
                                                    color: '#6b7280',
                                                    fontSize: '14px'
                                                }}>
                                                    No wastage reasons configured. Please add wastage reasons in Settings.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Wastage Date Card - rendered unconditionally above search bar, exactly as in the usage section.
                                    A "No wastage recorded" entry hides the item search, but it still
                                    has to be filed against a day. */}
                                <div 
                                    className="wastage-date-container no-gap-container"
                                    style={{
                                        marginBottom: '8px',
                                        position: 'relative'
                                    }}
                                >
                                    <WastageDatePicker
                                        value={operationDate}
                                        onChange={handleUserPickedDate}
                                        reconciledThrough={reconciledThrough}
                                        minDate={banquetDateWindow.minDate}
                                        maxDate={banquetDateWindow.maxDate}
                                        disabled={dateIsFixed}
                                        disabledHint={`Recording wastage for ${recoveryDate} — the date is set by the report you came from.`}
                                        label={t('stockOut.wastage_date') || "Wastage Date"}
                                    />
                                </div>

                                {/* The form chose a BACKLOG day on its own. Said out loud, because a
                                    silent default is how today's wastage ends up filed against
                                    yesterday — the mirror image of the bug this fixes. */}
                                {autoBacklogDate && autoBacklogDate === operationDate && !dateIsFixed && (
                                    <div
                                        role="status"
                                        style={{
                                            fontSize: '0.8rem', lineHeight: 1.45, color: '#92400e',
                                            background: '#fffbeb', border: '1px solid #fde68a',
                                            borderRadius: '8px', padding: '8px 12px', margin: '0 0 8px 0'
                                        }}
                                    >
                                        Recording for <strong>{autoBacklogDate.split('-').reverse().join('-')}</strong> — that
                                        day&apos;s sales report is still open. If this wastage is for today, change the date above.
                                    </div>
                                )}

                                {/* Banquet Wastage: the required event this wastage belongs to. */}
                                {banquetReasonSelected && (
                                    <BanquetEventPicker
                                        status={banquetEvents.status}
                                        events={banquetEvents.events}
                                        range={banquetEvents.range}
                                        error={banquetEvents.error}
                                        value={banquetEventId}
                                        onChange={handleBanquetEventChange}
                                        wastageDate={operationDate}
                                    />
                                )}

                                {/* What the chosen function served — the only dishes it can waste
                                    (their ingredients and preparations are offered too). */}
                                {banquetScopeKeys && chosenBanquetEvent && functionDishesNote(chosenBanquetEvent) && (
                                    <div
                                        role="note"
                                        style={{
                                            fontSize: '0.8rem', lineHeight: 1.45, color: '#374151',
                                            background: '#f8fafc', border: '1px solid #e5e7eb',
                                            borderRadius: '8px', padding: '8px 12px', margin: '0 0 8px 0'
                                        }}
                                    >
                                        {functionDishesNote(chosenBanquetEvent)}
                                    </div>
                                )}
                                {banquetRemovedNotice && (
                                    <div
                                        role="status"
                                        style={{
                                            fontSize: '0.8rem', lineHeight: 1.45, color: '#92400e',
                                            background: '#fffbeb', border: '1px solid #fde68a',
                                            borderRadius: '8px', padding: '8px 12px', margin: '0 0 8px 0'
                                        }}
                                    >
                                        {banquetRemovedNotice}
                                    </div>
                                )}

                                {/* Search bar - taking full width cleanly in both mobile and desktop view.
                                    Hidden for Banquet Wastage until a function is picked. */}
                                {wasteReason?.reason_label !== "No wastage recorded" && !banquetAwaitingFunction && (
                                    <div 
                                        className="wastage-search-container no-gap-container"
                                        style={{
                                            marginBottom: '10px',
                                            width: '100%',
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        <StandardSearch
                                            value={searchTerm}
                                            onChange={setSearchTerm}
                                            onClear={clearSearch}
                                            placeholder={t('stockOut.search_placeholder')}
                                            inputRef={searchInputRef}
                                            style={{ marginBottom: 0, width: '100%', boxSizing: 'border-box' }}
                                        />
                                    </div>
                                )}

                                {wasteReason?.reason_label !== "No wastage recorded" && (
                                    <>
                                        <div
                                            className="category-chips-viewall-wrap"
                                            style={{ position: 'relative', width: '100%', display: banquetAwaitingFunction ? 'none' : undefined }}
                                        >
                                            <div ref={categoryChipsRef} className="category-pills">
                                                {/* All button */}
                                                <button 
                                                    key="All" 
                                                    className={activeCategory === 'All' ? 'active' : ''} 
                                                    onClick={() => {
                                                        console.log(`🔍 [WASTAGE] Category clicked: "All"`);
                                                        setActiveCategory('All');
                                                    }}
                                                    style={{
                                                        fontWeight: activeCategory === 'All' ? '600' : '400',
                                                        backgroundColor: activeCategory === 'All' ? '#000' : 'transparent',
                                                        color: activeCategory === 'All' ? '#fff' : '#000'
                                                    }}
                                                >
                                                    {translateCategory('All')}
                                                </button>

                                                {/* Dish / Master Categories */}
                                                {chipDishOptions.map(cat => (
                                                    <button 
                                                        key={cat} 
                                                        className={activeCategory === cat ? 'active' : ''} 
                                                        onClick={() => {
                                                            console.log(`🔍 [WASTAGE] Category clicked: "${cat}"`);
                                                            setActiveCategory(cat);
                                                        }}
                                                        style={{
                                                            fontWeight: activeCategory === cat ? '600' : '400',
                                                            backgroundColor: activeCategory === cat ? '#000' : 'transparent',
                                                            color: activeCategory === cat ? '#fff' : '#000'
                                                        }}
                                                    >
                                                        {translateCategory(cat)}
                                                        {activeCategory === cat && (
                                                            <span style={{ marginLeft: '6px', fontSize: '11px', opacity: 0.8 }}>
                                                                ({scopedDishes.filter(item => {
                                                                    const key = useMasterCategories ? 'master_category' : 'category';
                                                                    const itemCat = item[key] || (useMasterCategories ? 'Uncategorized' : '');
                                                                    return itemCat === cat;
                                                                }).length})
                                                            </span>
                                                        )}
                                                    </button>
                                                ))}

                                                {/* Extra categories that ALWAYS appear */}
                                                {chipExtraCategories.map(cat => (
                                                    <button
                                                        key={cat}
                                                        className={activeCategory === cat ? 'active' : ''} 
                                                        onClick={() => {
                                                            console.log(`🔍 [WASTAGE] Category clicked: "${cat}"`);
                                                            setActiveCategory(cat);
                                                        }}
                                                        style={{
                                                            fontWeight: activeCategory === cat ? '600' : '400',
                                                            backgroundColor: activeCategory === cat ? '#000' : 'transparent',
                                                            color: activeCategory === cat ? '#fff' : '#000'
                                                        }}
                                                    >
                                                        {translateCategory(cat)}
                                                        {activeCategory === cat && (
                                                            <span style={{ marginLeft: '6px', fontSize: '11px', opacity: 0.8 }}>
                                                                ({cat === 'Raw Items'
                                                                    ? scopedRawItems.length
                                                                    : cat === NON_CONSUMABLE_CATEGORY
                                                                    ? scopedRawItems.filter(item => item.isNonConsumable).length
                                                                    : cat === 'Preparations'
                                                                    ? scopedPreparationItems.length
                                                                    : 0})
                                                            </span>
                                                        )}
                                                    </button>
                                                ))}

                                                {/* View all / Show less toggle */}
                                                {hasCategoryChipOverflow && !banquetScopeKeys && (
                                                    <button
                                                        type="button"
                                                        data-category-toggle="true"
                                                        onClick={() => setShowAllCategoryChips(prev => !prev)}
                                                        style={{
                                                            backgroundColor: '#fff',
                                                            color: '#111827',
                                                            border: '1px solid #d1d5db',
                                                            fontWeight: '500',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        {showAllCategoryChips ? 'Show less' : 'View all'}
                                                    </button>
                                                )}
                                            </div>

                                            <div
                                                ref={categoryMeasureRef}
                                                className="category-pills category-chips-measure"
                                                aria-hidden="true"
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    visibility: 'hidden',
                                                    pointerEvents: 'none',
                                                    zIndex: -1,
                                                    margin: 0,
                                                    padding: 0,
                                                    display: 'flex',
                                                    gap: '8px',
                                                    flexWrap: 'wrap'
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    data-measure-all="true"
                                                    tabIndex={-1}
                                                    style={{ padding: '8px 16px', fontSize: '14px', fontFamily: "'Poppins', sans-serif" }}
                                                >
                                                    {translateCategory('All')}
                                                </button>
                                                {dishCategories.map(cat => (
                                                    <button
                                                        key={`measure-dish-${cat}`}
                                                        type="button"
                                                        data-measure-dish="true"
                                                        tabIndex={-1}
                                                        style={{ padding: '8px 16px', fontSize: '14px', fontFamily: "'Poppins', sans-serif" }}
                                                    >
                                                        {translateCategory(cat)}
                                                    </button>
                                                ))}
                                                {extraCategories.map(cat => (
                                                    <button
                                                        key={`measure-extra-${cat}`}
                                                        type="button"
                                                        data-measure-extra="true"
                                                        tabIndex={-1}
                                                        style={{ padding: '8px 16px', fontSize: '14px', fontFamily: "'Poppins', sans-serif" }}
                                                    >
                                                        {translateCategory(cat)}
                                                    </button>
                                                ))}
                                                <button
                                                    type="button"
                                                    data-measure-view-all="true"
                                                    tabIndex={-1}
                                                    style={{ padding: '8px 16px', fontSize: '14px', fontFamily: "'Poppins', sans-serif" }}
                                                >
                                                    View all
                                                </button>
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            {banquetAwaitingFunction ? (
                                                // Banquet Wastage: the function comes first; only what it
                                                // served can be picked once it is chosen.
                                                <div className="items-loading-card" role="status">
                                                    <p className="items-loading-text">{PICK_FUNCTION_FIRST}</p>
                                                </div>
                                            ) : banquetScopeKeys && banquetScopeKeys.size === 0 ? (
                                                <div className="items-loading-card" role="status">
                                                    <p className="items-loading-text">{emptyFunctionMessage(chosenBanquetEvent)}</p>
                                                </div>
                                            ) : isItemsLoading ? (
                                                <div className="items-loading-card">
                                                    <div className="items-loading-spinner" />
                                                    <p className="items-loading-text">Loading items...</p>
                                                    <p className="items-loading-subtext">Fetching menu items & costs</p>
                                                </div>
                                            ) : (
                                            <div className="item-grid-scroll-wrap">
                                            <div
                                                ref={itemGridRef}
                                                className={`item-grid ${isItemGridScrolling ? 'is-scrolling' : ''}`}
                                                onScroll={handleItemGridScroll}
                                            >
                                                {displayedItems.map((item, index) => {
                                                    const platesInfo = availablePlates[item.id];
                                                    const availablePlatesCount = platesInfo?.available_plates || 0;
                                                    
                                                    // Debug log for first few items
                                                    if (index < 3) {
                                                        console.log(`🔍 [PLATES RENDER] Item: ${item.name}, ID: ${item.id}, Category: ${item.category}, PlatesInfo:`, platesInfo, 'Count:', availablePlatesCount);
                                                    }
                                                    
                                                    return (
                                                    <div className="dish-card" key={`${item.id}-${item.category}-${index}`} style={{ position: 'relative' }}>
                                                        {/* Available Plates Badge - only for menu items (not raw items or preparations) */}
                                                        {item.category !== 'Raw Items' && item.category !== 'Preparations' && platesInfo && (
                                                            <button type="button" onClick={() => openPlateDetailModal(item)} style={{
                                                                position: 'absolute',
                                                                top: '6.5px',
                                                                right: '9.5px',
                                                                backgroundColor: '#ffffff',
                                                                color: '#6b7280',
                                                                padding: '2px 4px',
                                                                borderRadius: '12px',
                                                                fontSize: '11px',
                                                                fontWeight: '700',
                                                                zIndex: 10,
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                                whiteSpace: 'nowrap',
                                                                border: '1px solid #e5e7eb',
                                                                cursor: 'pointer'
                                                            }}>
                                                                est. {availablePlatesCount} {availablePlatesCount === 1 ? 'plate' : 'plates'}
                                                            </button>
                                                        )}
                                                        {item.category === 'Raw Items' ? (
                                                            <div className="raw-item-placeholder" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <span style={{ fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>Raw Items</span>
                                                            </div>
                                                        ) : item.category === 'Preparations' ? (
                                                            <div className="raw-item-placeholder" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <span style={{ fontSize: '13px', fontWeight: 600, color: '#6b7280' }}>Preparation</span>
                                                            </div>
                                                        ) : (
                                                            <ImageWithFallback
                                                                src={item.img}
                                                                alt={item.name}
                                                                fallback={item.fallback_img}
                                                                placeholder={item.placeholder_img}
                                                            />
                                                        )}
                                                        <p className="dish-name" style={defaultTextSizes}>
                                                            {searchTerm
                                                                ? highlightMatch(item.name, searchTerm)
                                                                : item.name
                                                            }
                                                        </p>
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 8 }}>
                                                            {item.category === 'Raw Items' ? (
                                                                <>
                                                                    <span style={{ color: '#111', fontWeight: 700, fontSize: 16, marginBottom: 2, textAlign: 'center' }}>
                                                                        ₹{isNonBillingError ? '0.00' : (parseFloat(item.price) || 0).toFixed(2)}/{item.unit}
                                                                    </span>
                                                                    {wastedItems[item.id] > 0 && wastedItems[item.id] > (item.quantity || 0) && (
                                                                        <span className="negative-stock-warning" style={{ color: '#ef4444', fontWeight: 600, fontSize: 12, marginTop: 2, textAlign: 'center' }}>
                                                                            Negative stock warning
                                                                        </span>
                                                                    )}
                                                                </>
                                                            ) : item.category === 'Preparations' ? (
                                                                <>
                                                                    <span style={{ color: '#111', fontWeight: 600, fontSize: 13, marginBottom: 2, textAlign: 'center' }}>
                                                                        {item.totalWeight} {item.unitSymbol}/batch
                                                                    </span>
                                                                    {wastedItems[item.id] > 0 && checkPrepShortages(item, wastedItems[item.id]).length > 0 && (
                                                                        <span className="negative-stock-warning" style={{ color: '#ef4444', fontWeight: 600, fontSize: 12, marginTop: 2, textAlign: 'center' }}>
                                                                            Negative stock warning
                                                                        </span>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {(() => {
                                                                        const platesInfoForItem = availablePlates[item.id];
                                                                        const hasZeroPlates = platesInfoForItem && Number(platesInfoForItem.available_plates || 0) === 0;
                                                                        const hideManufacturingCost = isNonBillingError || item.insufficientStock || hasZeroPlates;
                                                                        if ((wastedItems[item.id] || 0) >= 1) {
                                                                            return (
                                                                                <>
                                                                                    <span className="manu-cost-label" style={{ color: '#888', fontWeight: 700, fontSize: 14, marginBottom: 2 }}>Manufacturing Cost</span>
                                                                                    <span className="manu-cost-value" style={{ color: '#111', fontWeight: 700, fontSize: 16, marginBottom: 2, textAlign: 'center' }}>
                                                                                        ₹{hideManufacturingCost ? '0.00' : ((parseFloat(item.price) || 0) * (wastedItems[item.id] || 0)).toFixed(2)}
                                                                                    </span>
                                                                                </>
                                                                            );
                                                                        }
                                                                        return (
                                                                            <span style={{ color: '#000000', fontWeight: 600, fontSize: 13, marginBottom: 2, textAlign: 'center' }}>
                                                                                ₹{hideManufacturingCost ? '0.00' : (parseFloat(item.price) || 0).toFixed(2)}/plate
                                                                            </span>
                                                                        );
                                                                    })()}
                                                                    {wastedItems[item.id] > 0 && wastedItems[item.id] > (availablePlates[item.id]?.available_plates || 0) && (
                                                                        <span className="negative-stock-warning" style={{ color: '#ef4444', fontWeight: 600, fontSize: 12, marginTop: 2, textAlign: 'center' }}>
                                                                            Negative stock warning
                                                                        </span>
                                                                    )}
                                                                    {item.hasRecipe && !item.isComplete && !item.insufficientStock && !isNonBillingError && (
                                                                        <span className="manu-cost-warning" style={{ color: '#ef4444', fontWeight: 600, fontSize: 12, marginTop: 2, textAlign: 'center' }}>
                                                                            Recipe Incomplete
                                                                        </span>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                        <div className="quantity-stepper">
                                                            {(item.category === 'Preparations' || item.category === 'Raw Items') && !isPieceSteppedItem(item) ? (
                                                                 <>
                                                                     <button
                                                                         style={{ border: '1px solid rgb(209, 213, 219)' }}
                                                                         onClick={() => {
                                                                             const currentQty = wastedItems[item.id] || 0;
                                                                             if (currentQty <= 0) return;
                                                                             // Remove entirely on minus
                                                                             setWastedItems(prev => {
                                                                                 const { [item.id]: _, ...rest } = prev;
                                                                                 return rest;
                                                                             });
                                                                         }}
                                                                         disabled={!wastedItems[item.id] || wastedItems[item.id] <= 0}
                                                                     >
                                                                         <FiMinus />
                                                                     </button>
                                                                     <div
                                                                         onClick={() => openPrepQuantityModal(item)}
                                                                         style={{
                                                                             width: '60px',
                                                                             textAlign: 'center',
                                                                             fontSize: '14px',
                                                                             fontWeight: 600,
                                                                             cursor: 'pointer',
                                                                             color: wastedItems[item.id] ? '#111' : '#9ca3af',
                                                                             lineHeight: '32px',
                                                                             userSelect: 'none'
                                                                         }}
                                                                     >
                                                                         {wastedItems[item.id] ? formatPrepDisplayQty(item) : `0`}
                                                                     </div>
                                                                     <button
                                                                         style={{ border: '1px solid rgb(209, 213, 219)' }}
                                                                         onClick={() => openPrepQuantityModal(item)}
                                                                     >
                                                                         <FiPlus />
                                                                     </button>
                                                                 </>
                                                             ) : (
                                                                <>
                                                            <button
                                                                style={{ border: '1px solid rgb(209, 213, 219)', opacity: (item.category !== 'Raw Items' && !item.isComplete && item.price > 0) ? 0.4 : 1 }}
                                                                onClick={() => handleQuantityChange(item.id, -1)}
                                                                disabled={!wastedItems[item.id] || wastedItems[item.id] <= 0 || (item.category !== 'Raw Items' && !item.isComplete && item.price > 0)}
                                                                title={item.category !== 'Raw Items' && !item.isComplete && item.price > 0 ? 'Add ingredient cost data in inventory to enable' : ''}
                                                            >
                                                                <FiMinus />
                                                            </button>
                                                            <input
                                                                type="number"
                                                                className="quantity-input"
                                                                value={wastedItems[item.id] || ''}
                                                                placeholder="0"
                                                                disabled={item.category !== 'Raw Items' && !item.isComplete && item.price > 0}
                                                                title={item.category !== 'Raw Items' && !item.isComplete && item.price > 0 ? 'Add ingredient cost data in inventory to enable' : ''}
                                                                onFocus={(e) => {
                                                                    // Select all text when focused so typing replaces the value
                                                                    e.target.select();
                                                                }}
                                                                onChange={(e) => {
                                                                    const value = parseInt(e.target.value) || 0;
                                                                    if (value >= 0) {
                                                                        setWastedItems(prev => {
                                                                            const currentCount = prev[item.id] || 0;
                                                                            
                                                                            // No submission lock in Wastage tab (lock only applies to Usage component)
                                                                            
                                                                            if (value === 0) {
                                                                                const { [item.id]: _, ...rest } = prev;
                                                                                return rest;
                                                                            }
                                                                            return { ...prev, [item.id]: value };
                                                                        });
                                                                    }
                                                                }}
                                                                min="0"
                                                                style={{
                                                                    width: '60px',
                                                                    textAlign: 'center',
                                                                    border:'none',
                                                                    fontSize: '16px',
                                                                    fontWeight: 'inherit',
                                                                    color: 'currentColor',
                                                                    backgroundColor: 'transparent',
                                                                    appearance: 'none',
                                                                    MozAppearance: 'textfield',
                                                                    WebkitAppearance: 'none',
                                                                    boxShadow: 'none',
                                                                    opacity: (item.category !== 'Raw Items' && !item.isComplete && item.price > 0) ? 0.4 : 1,
                                                                    cursor: (item.category !== 'Raw Items' && !item.isComplete && item.price > 0) ? 'not-allowed' : 'text'
                                                                }}
                                                            />
                                                            <button 
                                                                style={{ border: '1px solid rgb(209, 213, 219)', opacity: (item.category !== 'Raw Items' && !item.isComplete && item.price > 0) ? 0.4 : 1 }} 
                                                                onClick={() => handleQuantityChange(item.id, 1)}
                                                                disabled={item.category !== 'Raw Items' && !item.isComplete && item.price > 0}
                                                                title={item.category !== 'Raw Items' && !item.isComplete && item.price > 0 ? 'Add ingredient cost data in inventory to enable' : ''}
                                                            >
                                                                <FiPlus />
                                                            </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                            {itemGridThumb.hasOverflow && (
                                                <div className={`item-grid-scrollbar-indicator ${isItemGridScrolling ? 'visible' : ''}`} aria-hidden="true">
                                                    <div
                                                        className="item-grid-scrollbar-thumb"
                                                        style={{
                                                            height: `${itemGridThumb.height}px`,
                                                            transform: `translateY(${itemGridThumb.top}px)`
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            </div>
                                            )}
                                        </div>
                                        {/* ✅ New wrapper div for the card */}
                                        <div className="summary-notes-card">
                                            <div className="waste-summary">
                                                <span style={{ color: "#000" }}>Total Items:</span>
                                                <span style={{ color: "#000", fontSize: "16px", fontWeight: "600" }}>{totalItems}</span>
                                            </div>
                                        </div>

                                        {/* Additional Notes label with Limit button on same line */}
                                        <div style={{ marginTop: '16px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                            <label className="form-label-main" style={{ margin: 0, whiteSpace: 'nowrap' }}>Additional Notes (Optional)</label>
                                            <button
                                                onClick={() => {
                                                    console.log('Limit button clicked, goTo:', goTo);
                                                    if (goTo) {
                                                        goTo('wastage-thresholds');
                                                    } else {
                                                        console.error('goTo function not available');
                                                    }
                                                }}
                                                className="thresholds-btn"
                                                style={{
                                                    padding: '10px 14px',
                                                    backgroundColor: '#000000',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    fontSize: '14px',
                                                    fontWeight: '500',
                                                    color: '#fff',
                                                    cursor: 'pointer',
                                                    whiteSpace: 'nowrap',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                Limit
                                            </button>
                                        </div>

                                        {/* Notes textarea */}
                                        <div className="notes-group">
                                            <textarea placeholder="Add Notes" value={notes} onChange={e => setNotes(e.target.value)} maxLength={350}></textarea>
                                        </div>

                                        {/* Single row: Recorded by, Select Staff, and Camera (only when photo required) */}
                                        <div className="recorder-and-photo-section" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                                <label className="form-label-main" style={{ margin: 0, whiteSpace: 'nowrap' }}>Recorded by: {recordedBy?.name || ''}</label>
                                                {/* Only show Select Staff button for Owner/Admin users.
                                                    Staff members with their own credentials are auto-assigned. */}
                                                {isCurrentUserOwner && (
                                                    <button className="recorder-select-btn" onClick={() => setStaffModalOpen(true)} style={{ whiteSpace: 'nowrap' }}>
                                                        <FiUser /> {recordedBy ? 'Change Staff' : 'Select Staff'}
                                                    </button>
                                                )}
                                            </div>
                                            {/* Camera button only shown when threshold exceeded (photo required) */}
                                            {requiresEvidencePhoto && (
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                    <button className="photo-upload-box" onClick={handleCameraClick}>
                                                        <FiCamera />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Photo requirement banner removed as requested */}
                                {photos.length > 0 && (
                                    <div className="photo-previews" style={{ marginBottom: '8px' }}>
                                        {photos.map((photo, index) => (
                                            <div key={index} className="preview-image-container">
                                                <img src={photo.url} alt={`Waste preview ${index + 1}`} />
                                                <button onClick={() => handleRemovePhoto(index)} className="remove-photo-btn"><FiX /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* --- UPDATED: Added onClick handler to the Draft button --- */}
                                <div className="form-actions" style={{ marginTop: '8px', marginBottom: '0', paddingBottom: '0' }}>
                                    {/* T1.9 — saving an empty form wiped the previously stored draft,
                                        because POST /wastage/draft replaces that day's Draft rows
                                        before re-inserting. Disabled when there is nothing to save.
                                        Hidden for Banquet Wastage, which is never a draft. */}
                                    {!banquetReasonSelected && (
                                    <button
                                        className="draft-btn"
                                        onClick={handleSaveDraft}
                                        disabled={totalItems === 0}
                                        title={totalItems === 0 ? 'Add at least one item before saving a draft' : undefined}
                                        style={totalItems === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                                    >
                                        Save as Draft
                                    </button>
                                    )}
                                    <button
                                        className={`submit-btn waste-confirm ${requiresEvidencePhoto && photos.length === 0 ? 'photo-required' : ''}`}
                                        disabled={(activeSubTab === 'Waste' && !wasteReason) || (totalItems === 0 && wasteReason?.reason_label !== "No wastage recorded" && wasteReason?.reason_label !== "No Wastage Recorded") || !submissionLock.canSubmit || (banquetReasonSelected && !chosenBanquetEvent)}
                                        onClick={() => {
                                            if (!submissionLock.canSubmit) {
                                                setError(`Please wait ${submissionLock.secondsRemaining} seconds before submitting.`);
                                                return;
                                            }
                                            if (!recordedBy) {
                                                // Only open staff modal for owners; staff are auto-assigned
                                                if (isCurrentUserOwner) {
                                                    setStaffModalOpen(true);
                                                } else {
                                                    setError('Staff assignment error. Please refresh the page.');
                                                }
                                                return;
                                            }
                                            // If evidence required but not yet added, show a small popup first
                                            if (requiresEvidencePhoto && photos.length === 0) {
                                                setShowPhotoNudge(true);
                                                setPhotoButtonClicked(true); // Mark as clicked when opening nudge
                                                return;
                                            }
                                            // Build confirmation items snapshot to ensure names render
                                            try {
                                                const normalizeId = (v) => `${v}`;
                                                const itemsArr = Object.entries(wastedItems).map(([itemId, quantity]) => {
                                                    const wid = normalizeId(itemId);
                                                    let name = getItemNameById(wid) || 'Unknown Item';
                                                    return {
                                                        id: wid,
                                                        name: name,
                                                        quantity: quantity || 0
                                                    };
                                                });
                                                setConfirmItems(itemsArr);
                                            } catch (err) {
                                                setConfirmItems([]);
                                            }
                                            // Open confirmation modal before submitting
                                            setConfirmModalOpen(true);
                                        }}
                                        title={
                                            !submissionLock.canSubmit 
                                                ? `Please wait ${submissionLock.secondsRemaining} seconds`
                                                : requiresEvidencePhoto && photos.length === 0 
                                                ? t('waste.addPhotoInstruction')
                                                : 'Confirm wastage record'
                                        }
                                    >
                                        {!submissionLock.canSubmit 
                                            ? `Wait ${submissionLock.secondsRemaining}s`
                                            : requiresEvidencePhoto && photos.length === 0 
                                            ? (t('waste.photoRequired') !== 'waste.photoRequired' ? t('waste.photoRequired') : '📸 Photo Required')
                                            : (t('waste.confirmWaste') !== 'waste.confirmWaste' ? t('waste.confirmWaste') : 'Confirm Wastage')}
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Modals - shown for both Usage and Waste tabs */}
                        {isReasonModalOpen && (
                            <Modal onClose={() => setReasonModalOpen(false)}>
                                <div className="stock-out-modal-list">
                                    <h3>{t('waste.selectWasteReason')}</h3>
                                    <div>
                                        {sortedWasteReasons
                                            .map(r => (
                                                <button
                                                    key={r.reason_id}
                                                    className={wasteReason?.reason_id === r.reason_id ? 'active' : ''}
                                                    // Same date gating as the inline dropdown. Both surfaces pick a
                                                    // reason, so both have to honour is_disabled — otherwise the
                                                    // fallback modal is a way around the gate.
                                                    disabled={Boolean(r.is_disabled)}
                                                    title={r.is_disabled ? (r.disabled_reason || 'Not available for this date') : undefined}
                                                    style={r.is_disabled ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                                                    onClick={() => {
                                                        if (r.is_disabled) return;
                                                        setWasteReason(r);
                                                        setReasonModalOpen(false);
                                                    }}
                                                >
                                                    <h4>{translateWasteReason(r.reason_label)}</h4>
                                                </button>
                                            ))
                                        }
                                    </div>
                                </div>
                            </Modal>
                        )}
                        {/* Staff selection modal - only shown for Owner/Admin users */}
                        {isStaffModalOpen && isCurrentUserOwner && (
                            <Modal onClose={() => setStaffModalOpen(false)}>
                                <div className="stock-out-modal-list">
                                    <h3>{t('waste.selectStaffMember')}</h3>
                                    <div>
                                        {staffMembers.map(s => (
                                            <button key={s.user_id || s.id} onClick={() => { setRecordedBy(s); setStaffModalOpen(false); }}>
                                                <h4>{s.name}</h4>
                                                <p>{s.role_name || s.role || ''}</p>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </Modal>
                        )}
                        {/* Preparation Quantity Modal */}
                        {prepQuantityModal.open && prepQuantityModal.item && (
                            <Modal 
                                onClose={() => { setPrepQuantityModal({ open: false, item: null, quantity: '', selectedUnit: '' }); setPrepUnitDropdownOpen(false); }}
                                overlayClassName="confirm-modal-overlay"
                                contentClassName="confirm-modal-shell prep-quantity-modal-content"
                            >
                                <div className="confirm-modal" style={{ margin: '0', padding: '0', width: '100%', boxSizing: 'border-box' }}>
                                    <h3 className="confirm-title" style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: 700, textAlign: 'center' }}>
                                        {prepQuantityModal.item.name}
                                    </h3>
                                    {prepQuantityModal.item.isPreparation ? (
                                        <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>
                                            Batch size: {prepQuantityModal.item.totalWeight} {prepQuantityModal.item.unitSymbol}
                                            {prepQuantityModal.item.components?.length > 0 && ` · ${prepQuantityModal.item.components.length} ingredient${prepQuantityModal.item.components.length !== 1 ? 's' : ''}`}
                                        </p>
                                    ) : (
                                        <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>
                                            Standard Unit: {prepQuantityModal.item.unit}
                                        </p>
                                    )}
                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#374151', textAlign: 'center' }}>
                                        How much do you want to waste?
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'stretch', gap: '8px', marginBottom: '20px' }}>
                                        <input
                                            type="number"
                                            autoFocus
                                            value={prepQuantityModal.quantity}
                                            onChange={(e) => setPrepQuantityModal(prev => ({ ...prev, quantity: e.target.value }))}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handlePrepQuantityConfirm(); }}
                                            placeholder="0"
                                            min="0"
                                            step="any"
                                            style={{
                                                flex: 1,
                                                padding: '10px 12px',
                                                fontSize: '18px',
                                                fontWeight: 600,
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                textAlign: 'center',
                                                outline: 'none',
                                                transition: 'border-color 0.2s',
                                                boxSizing: 'border-box'
                                            }}
                                            onFocus={(e) => { e.target.select(); }}
                                        />
                                        {getAvailableUnits(prepQuantityModal.item.unitSymbol).length > 1 ? (
                                            <div style={{ position: 'relative', minWidth: '80px' }}>
                                                <div
                                                    onClick={() => setPrepUnitDropdownOpen(!prepUnitDropdownOpen)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px',
                                                        padding: '10px 12px',
                                                        fontSize: '16px',
                                                        fontWeight: 600,
                                                        border: '2px solid #e5e7eb',
                                                        borderRadius: '8px',
                                                        backgroundColor: '#fff',
                                                        color: '#374151',
                                                        cursor: 'pointer',
                                                        userSelect: 'none',
                                                        height: '100%',
                                                        boxSizing: 'border-box'
                                                    }}
                                                >
                                                    <span>{prepQuantityModal.selectedUnit}</span>
                                                    <span style={{ fontSize: '10px', color: '#6b7280' }}>▼</span>
                                                </div>
                                                {prepUnitDropdownOpen && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        bottom: '100%',
                                                        right: 0,
                                                        width: '140px',
                                                        marginBottom: '4px',
                                                        backgroundColor: '#ffffff',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '8px',
                                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                                        zIndex: 1000,
                                                        overflow: 'hidden'
                                                    }}>
                                                        {getAvailableUnits(prepQuantityModal.item.unitSymbol).map(u => (
                                                            <div
                                                                key={u}
                                                                onClick={() => {
                                                                    const newUnit = u;
                                                                    const oldUnit = prepQuantityModal.selectedUnit;
                                                                    const currentVal = parseFloat(prepQuantityModal.quantity) || 0;
                                                                    let newVal = '';
                                                                    if (currentVal > 0) {
                                                                        const baseVal = convertToBaseUnit(currentVal, oldUnit, prepQuantityModal.item.unitSymbol);
                                                                        const converted = convertFromBaseUnit(baseVal, newUnit, prepQuantityModal.item.unitSymbol);
                                                                        newVal = String(parseFloat(converted.toFixed(4)));
                                                                    }
 
                                                                    // Keep the real amount constant, just re-express it in the
                                                                    // newly selected unit (g ↔ kg, ml ↔ L).
                                                                    setPrepQuantityModal(prev => ({
                                                                        ...prev,
                                                                        selectedUnit: newUnit,
                                                                        quantity: newVal
                                                                    }));
                                                                    setPrepUnitDropdownOpen(false);
                                                                }}
                                                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                                                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
                                                                style={{
                                                                    padding: '10px 12px',
                                                                    fontSize: '14px',
                                                                    fontWeight: 600,
                                                                    color: '#374151',
                                                                    cursor: 'pointer',
                                                                    textAlign: 'center',
                                                                    backgroundColor: '#ffffff',
                                                                    transition: 'background-color 0.15s ease'
                                                                }}
                                                            >
                                                                {u === 'kg' || u === 'Kg' ? 'Kilograms (kg)' : u === 'g' ? 'Grams (g)' : u === 'L' || u === 'l' ? 'Liters (L)' : u === 'ml' ? 'Milliliters (ml)' : u}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '10px 16px',
                                                fontSize: '16px',
                                                fontWeight: 600,
                                                color: '#374151',
                                                minWidth: '40px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                backgroundColor: '#f9fafb',
                                                boxSizing: 'border-box'
                                            }}>
                                                {prepQuantityModal.item.unitSymbol}
                                            </div>
                                        )}
                                    </div>
                                    {prepQuantityModal.item.components?.length > 0 && parseFloat(prepQuantityModal.quantity) > 0 && (
                                        <div style={{ marginBottom: '16px', padding: '10px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                            <p style={{ margin: '0 0 6px 0', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                                                Components that will be deducted
                                            </p>
                                            {prepQuantityModal.item.components.map((comp, idx) => {
                                                const enteredQty = parseFloat(prepQuantityModal.quantity) || 0;
                                                // Convert entered quantity to base unit first
                                                const baseQty = convertToBaseUnit(enteredQty, prepQuantityModal.selectedUnit, prepQuantityModal.item.unitSymbol);
                                                const batchSize = prepQuantityModal.item.totalWeight || 1;
                                                const ratio = baseQty / batchSize;
                                                const compQty = (comp.weight || 0) * ratio;
                                                return (
                                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '13px' }}>
                                                        <span style={{ color: '#374151' }}>{comp.item_name}</span>
                                                        <span style={{ fontWeight: 600, color: '#111' }}>{formatQuantity(compQty, comp.unit_name)}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <div className="actions" style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                                        <button
                                            onClick={() => {
                                                setPrepQuantityModal({ open: false, item: null, quantity: '', selectedUnit: '' });
                                                setPrepUnitDropdownOpen(false);
                                            }}
                                            className="cancel-btn"
                                            style={{ flex: 1, margin: 0 }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={() => {
                                                handlePrepQuantityConfirm();
                                                setPrepUnitDropdownOpen(false);
                                            }}
                                            disabled={!prepQuantityModal.quantity || parseFloat(prepQuantityModal.quantity) <= 0}
                                            className="confirm-btn-final"
                                            style={{
                                                flex: 1, margin: 0,
                                                backgroundColor: (!prepQuantityModal.quantity || parseFloat(prepQuantityModal.quantity) <= 0) ? '#d1d5db' : '#111',
                                                color: (!prepQuantityModal.quantity || parseFloat(prepQuantityModal.quantity) <= 0) ? '#9ca3af' : '#fff',
                                                cursor: (!prepQuantityModal.quantity || parseFloat(prepQuantityModal.quantity) <= 0) ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            Confirm
                                        </button>
                                    </div>
                                </div>
                            </Modal>
                        )}
                        {/* Confirmation Modal */}
                        {isConfirmModalOpen && (
                            <Modal 
                                onClose={() => setConfirmModalOpen(false)}
                                overlayClassName="confirm-modal-overlay"
                                contentClassName="confirm-modal-shell"
                            >
                                <div className="confirm-modal">
                                    <h3 className="confirm-title">
                                        {(t('waste.confirmWasteRecord') !== 'waste.confirmWasteRecord') ? t('waste.confirmWasteRecord') : 'Confirm Waste Record'}
                                    </h3>
                                    
                                    {/* Items List - Hide when "No Wastage Recorded" is selected */}
                                    {!isNoWastageRecorded && !isNonBillingError && (
                                        <div className="items-card">
                                            {(confirmItems && confirmItems.length > 0
                                                ? confirmItems
                                                : Object.entries(wastedItems).map(([itemId, quantity]) => {
                                                    const wid = `${itemId}`;
                                                    const name = getItemNameById(wid) || 'Unknown Item';
                                                    return { id: wid, name, quantity };
                                                })
                                                                            ).map(({ id, name, quantity }, index) => {
                                                const prepItem = id.startsWith('prep_') ? preparationItems.find(p => p.id === id) : null;
                                                const prepDisplayUnit = prepItem ? (prepDisplayUnits[id] || getDefaultInputUnit(prepItem.unitSymbol)) : null;
                                                const prepDisplayQty = prepItem && prepDisplayUnit ? parseFloat(convertFromBaseUnit(quantity, prepDisplayUnit, prepItem.unitSymbol).toFixed(4)) : quantity;
                                                return (
                                                    <div key={id + ':' + index} className="item-row">
                                                        <span className="item-name">{name}</span>
                                                        <span className="item-qty">
                                                            {prepItem ? `${prepDisplayQty} ${prepDisplayUnit}` : `x ${quantity}`}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Total Items */}
                                    <div className="meta-row">
                                        <span className="kv-label">Total Items:</span>
                                        <span className="meta-value">{totalItems}</span>
                                    </div>


                                    {/* Reason */}
                                    <div className="meta-row">
                                        <span className="kv-label">Reason:</span>
                                        <span className="meta-value">{translateWasteReason(wasteReason?.reason_label) || wasteReason?.reason_label || 'Not selected'}</span>
                                    </div>

                                    {/* Banquet Wastage: which banquet it is recorded against */}
                                    {chosenBanquetEvent && (
                                        <div className="meta-row">
                                            <span className="kv-label">Banquet:</span>
                                            <span className="meta-value">{chosenBanquetEvent.functionName} · {formatShortDay(chosenBanquetEvent.eventDate)}</span>
                                        </div>
                                    )}

                                    {/* Recorded By */}
                                    <div className="meta-row">
                                        <span className="kv-label">Recorded by:</span>
                                        <span className="meta-value">{recordedBy?.name || ''}</span>
                                    </div>

                                    {/* Buttons */}
                                    <div className="actions">
                                        <button 
                                            onClick={() => setConfirmModalOpen(false)}
                                            className="cancel-btn"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleConfirmWithPiecePacks}
                                            className="confirm-btn-final"
                                            disabled={piecePackChecking}
                                        >
                                            {piecePackChecking ? 'Checking…' : 'Confirm'}
                                        </button>
                                    </div>
                                </div>
                            </Modal>
                        )}
                        {/* PIECE GUARD on the way out: pieces read back as packets, using the
                            Stock In answer, before anything is recorded. */}
                        {piecePackReview && (
                            <Modal
                                onClose={() => setPiecePackReview(null)}
                                overlayClassName="confirm-modal-overlay"
                                contentClassName="confirm-modal-shell"
                            >
                                <div className="confirm-modal piece-pack-review">
                                    <h3 className="confirm-title">Check pieces and packets</h3>
                                    <p className="piece-pack-intro">
                                        {piecePackReview.length === 1 ? 'This item is' : 'These items are'} counted in pieces.
                                        Going by your Stock In:
                                    </p>
                                    <div className="items-card">
                                        {piecePackReview.map(line => (
                                            <div key={line.id} className="piece-pack-line">
                                                <div className="piece-pack-name">{line.name}</div>
                                                <div className="piece-pack-sentence">{packetsSentence(line)}</div>
                                                <div className="piece-pack-basis">1 packet = {line.piecesPerPack} pieces</div>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="piece-pack-question">Is this the wastage you mean to record?</p>
                                    <div className="actions">
                                        <button
                                            onClick={() => setPiecePackReview(null)}
                                            className="cancel-btn"
                                        >
                                            No, change it
                                        </button>
                                        <button
                                            onClick={() => {
                                                setPiecePackReview(null);
                                                handleSubmit();
                                            }}
                                            className="confirm-btn-final"
                                        >
                                            Yes, record
                                        </button>
                                    </div>
                                </div>
                            </Modal>
                        )}
                        {showPhotoNudge && (
                            <Modal 
                                onClose={() => setShowPhotoNudge(false)}
                                overlayClassName="confirm-modal-overlay"
                                contentClassName="confirm-modal-shell"
                            >
                                <div className="confirm-modal photo-required-modal">
                                    <h3>{t('waste.photoEvidenceRequired')}</h3>
                                    <p style={{ 
                                        margin: '12px 0', 
                                        color: '#000000', 
                                        fontWeight: 600, 
                                        fontSize: '15px',
                                        textAlign: 'center'
                                    }}>
                                        Wastage Limit Exceeded
                                    </p>
                                    <ul style={{ textAlign: 'center', marginTop: '8px', marginBottom: '12px', paddingLeft: '20px', paddingTop:'0.5rem', maxHeight: '100px', overflowY: 'auto' }}>
                                        {Object.entries(thresholdCheckResults)
                                            .filter(([, result]) => result.photoRequired)

                                            .map(([id, result]) => {
                                                const item = menuItemsWithRecipeCost.find(i => i.id?.toString() === id?.toString());
                                                return item ? (
                                                    <li key={id} style={{ marginBottom: '1rem', fontSize: '14px', textAlign: 'left' }}>
                                                        <strong style={{ marginBottom: '2px', display: 'inline-block' }}>{item.name}</strong>: {result.enteredQuantity} plates<br/>
                                                        <span style={{ color: '#dc3545', fontSize: '13px' }}>(limit : {result.thresholdQuantity} plates)</span>
                                                    </li>
                                                ) : null;
                                            })
                                        }
                                    </ul>
                                    <div className="confirm-actions">
                                        <button className="cancel-btn" onClick={() => setShowPhotoNudge(false)}>{t('waste.cancel')}</button>
                                        <button
                                            className="confirm-btn-final"
                                            onClick={() => {
                                                setShowPhotoNudge(false);
                                                // Open file selector; after user picks a file, open confirm modal automatically
                                                const onNextChange = (e) => {
                                                    // file input change is already handled by handleFileSelect
                                                    // just open confirm modal after a brief tick
                                                    setTimeout(() => setConfirmModalOpen(true), 50);
                                                    if (fileInputRef.current) {
                                                        fileInputRef.current.removeEventListener('change', onNextChange);
                                                    }
                                                };
                                                if (fileInputRef.current) {
                                                    fileInputRef.current.addEventListener('change', onNextChange, { once: true });
                                                    fileInputRef.current.click();
                                                }
                                            }}
                                        >
                                            {t('waste.clickCameraIcon')}
                                        </button>
                                    </div>
                                </div>
                            </Modal>
                        )}
                        {plateDetailModal.open && (
                            <Modal onClose={closePlateDetailModal} contentClassName="plate-breakdown-modal-shell">
                                <div className="plate-breakdown-modal">
                                    <div className="plate-breakdown-header">
                                        <div className="plate-breakdown-header-row">
                                            <h3 className="plate-breakdown-title">As per plate costing</h3>
                                            <button
                                                type="button"
                                                className="plate-breakdown-close-btn"
                                                onClick={closePlateDetailModal}
                                                aria-label="Close plate cost breakdown"
                                            >
                                                <FiX size={20} />
                                            </button>
                                        </div>
                                        <p className="plate-breakdown-item-name">{plateDetailModal.item?.name || 'Menu item'}</p>
                                    </div>

                                    <div className="plate-breakdown-body">
                                        {plateDetailModal.loading ? (
                                            <div className="plate-breakdown-loading">Loading plate details...</div>
                                        ) : plateDetailModal.error ? (
                                            <div className="plate-breakdown-error">
                                                {plateDetailModal.error}
                                            </div>
                                        ) : (() => {
                                            // STEP C.5 — DISPLAY PARITY.
                                            //
                                            // This used to re-sum `breakdown` client-side into
                                            // `computedBatchCost` and render THAT, while the dish card
                                            // rendered the server's `manufacturing_cost`. The two came
                                            // from two different server-side engines, so the card and
                                            // this modal could show different costs for the same dish —
                                            // the exact bug STEP C exists to kill.
                                            //
                                            // The server now returns ONE `cost_total`, produced in the
                                            // same pass as these rows and asserted equal to their sum
                                            // (services/costEngine.js). The modal renders it verbatim.
                                            // Do not re-derive it here.
                                            const brkRows = plateDetailModal.breakdown || [];
                                            const summary = plateDetailModal.summary || {};
                                            // I4 — 'uncosted' means there is no cost, not a cost of zero.
                                            const isUncosted = summary.cost_status === 'uncosted';
                                            // `manufacturing_cost` is the pre-STEP-C field name, kept so
                                            // the modal still reads correctly against a server that has
                                            // not been redeployed yet.
                                            const batchCost = Number(summary.cost_total ?? summary.manufacturing_cost ?? 0);
                                            return (
                                            <>
                                                <div className="plate-breakdown-summary-grid">
                                                    <div className="plate-breakdown-summary-card">
                                                        <div className="plate-breakdown-summary-label">Selling price</div>
                                                        <div className="plate-breakdown-summary-value">₹{parseFloat(Number(plateDetailModal.summary?.selling_price || 0).toFixed(2))}</div>
                                                    </div>
                                                    <div className="plate-breakdown-summary-card">
                                                        <div className="plate-breakdown-summary-label">Batch cost</div>
                                                        <div className="plate-breakdown-summary-value">
                                                            {isUncosted ? 'Not costed' : `₹${parseFloat(batchCost.toFixed(2))}`}
                                                        </div>
                                                    </div>
                                                    <div className="plate-breakdown-summary-card">
                                                        <div className="plate-breakdown-summary-label">Available plates</div>
                                                        <div className="plate-breakdown-summary-value">{Number(plateDetailModal.platesInfo?.available_plates || 0).toFixed(0)}</div>
                                                    </div>
                                                </div>

                                                {((isUncosted || summary.partially_priced) && brkRows.length > 0) && (
                                                    <div className="plate-breakdown-notice">
                                                        {summary.zero_cost_reason
                                                            || (isUncosted
                                                                ? 'This dish cannot be costed from the current data.'
                                                                : 'Some ingredients were priced at their last known cost.')}
                                                    </div>
                                                )}

                                                <div className="plate-breakdown-table">
                                                    <div className="plate-breakdown-table-inner">
                                                        {/* Table header */}
                                                        <div className="plate-breakdown-table-row plate-breakdown-table-head">
                                                            <span>Ingredient</span>
                                                            <span className="plate-breakdown-cell-right">Per Plate Qty</span>
                                                            <span className="plate-breakdown-cell-right">Current Stock</span>
                                                            <span className="plate-breakdown-cell-right">Per Plate Cost</span>
                                                            <span className="plate-breakdown-cell-total">Total Value</span>
                                                        </div>
                                                    {(() => {
                                                        const brkRows = plateDetailModal.breakdown || [];
                                                        const savedPreps = brkRows.filter(r => r.section === 'preparation' || r.from_preparation);
                                                        const compItems = brkRows.filter(r => r.section === 'complimentary' || r.is_complimentary);
                                                        const directIngs = brkRows.filter(r => !savedPreps.includes(r) && !compItems.includes(r));
                                                        const hasItems = savedPreps.length > 0 || compItems.length > 0 || directIngs.length > 0;

                                                        const renderGroup = (rows, title) => {
                                                            if (rows.length === 0) return null;
                                                            return (
                                                                <>
                                                                    <div className="plate-breakdown-section-header">
                                                                        {title}
                                                                    </div>
                                                                    {rows.map((row, index) => {
                                                                        // ─── Smart price formatter ────────────────────────────────────────────
                                                                        const fmtPrice = (val) => {
                                                                            const n = Number(val);
                                                                            if (n === 0) return '₹0';
                                                                            if (n >= 100) return `₹${Math.round(n)}`;
                                                                            if (n >= 1) {
                                                                                return `₹${parseFloat(n.toFixed(2))}`;
                                                                            }
                                                                            if (n < 0.01) return `₹${parseFloat(n.toFixed(4))}`;
                                                                            return `₹${parseFloat(n.toFixed(4))}`;
                                                                        };

                                                                        const perPlateQty   = Number(row.quantity || 0);
                                                                        const currentStock  = Number(row.current_stock || 0);
                                                                        // `unit_cost: null` means NEVER PRICED. It is not ₹0 — rendering
                                                                        // it as ₹0 tells the user the ingredient is free, when what the
                                                                        // server said is that it has no price at all. The server flags
                                                                        // this on the row as price_basis, so read that rather than
                                                                        // inferring it from a falsy number.
                                                                        const isUnpriced    = row.unit_cost === null || row.unit_cost === undefined
                                                                                              || row.price_basis === 'unpriced';
                                                                        const unitCost      = isUnpriced ? null : Number(row.unit_cost);
                                                                        const perPlateCost  = Number(row.total_cost || 0);
                                                                        // Stock value is stock × unit cost. It used to be derived as
                                                                        // (stock / perPlateQty) × perPlateCost — algebraically the same,
                                                                        // but it divides by a per-plate quantity that can be a fraction
                                                                        // of a gram and multiplies the row's rounding error back up by
                                                                        // that ratio, so a half-paisa became rupees on a well-stocked
                                                                        // ingredient.
                                                                        const totalStockValue = isUnpriced ? null : currentStock * unitCost;
                                                                        const isLow = currentStock < perPlateQty;

                                                                        const formattedPerPlate = formatValueWithUnit(perPlateQty, row.unit);
                                                                        const formattedStock = formatValueWithUnit(currentStock, row.unit);

                                                                        return (
                                                                            <div
                                                                                key={`${row.ingredient_id || index}-${row.section || ''}-${index}`}
                                                                                className={`plate-breakdown-table-row plate-breakdown-table-body-row ${isLow ? 'is-low' : ''}`}
                                                                            >
                                                                                <span className="plate-breakdown-ingredient">
                                                                                    {row.ingredient_name || '-'}
                                                                                    {isLow && (
                                                                                        <span className="plate-breakdown-low-badge">LOW</span>
                                                                                    )}
                                                                                </span>

                                                                                <span className="plate-breakdown-qty">
                                                                                    {formattedPerPlate.qty}
                                                                                    {formattedPerPlate.unit && <span className="plate-breakdown-unit">{formattedPerPlate.unit}</span>}
                                                                                </span>

                                                                                <span className={`plate-breakdown-stock ${isLow ? 'is-low' : ''}`}>
                                                                                    {formattedStock.qty}
                                                                                    {formattedStock.unit && <span className="plate-breakdown-unit">{formattedStock.unit}</span>}
                                                                                </span>

                                                                                <span className="plate-breakdown-cost">
                                                                                    {isUnpriced ? 'Not priced' : fmtPrice(perPlateCost)}
                                                                                </span>

                                                                                <span className="plate-breakdown-total plate-breakdown-cell-total">
                                                                                    {isUnpriced ? '—' : fmtPrice(totalStockValue)}
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </>
                                                            );
                                                        };

                                                        if (!hasItems) {
                                                            return (
                                                                <div className="plate-breakdown-empty">
                                                                    No ingredient breakdown returned for this item.
                                                                </div>
                                                            );
                                                        }

                                                        return (
                                                            <>
                                                                {renderGroup(directIngs, "Direct Ingredients")}
                                                                {renderGroup(savedPreps, "Saved Preparations")}
                                                                {renderGroup(compItems, "Complimentary Items")}
                                                            </>
                                                        );
                                                    })()}
                                                    </div>
                                                </div>
                                                            </>
                                                        );
                                                    })()
                                                }
                                    </div>
                                </div>
                            </Modal>
                        )}
                        {/* Success Modal with green animation tick - Removed per user request */}
                        {/* <SuccessModal
                            show={showSuccessModal}
                            message={successMessage}
                            onClose={() => setShowSuccessModal(false)}
                        /> */}
                        
                        {/* Ingredient Shortage Modal */}
                        <IngredientShortageModal
                            show={showShortageModal}
                            data={shortageData}
                            onClose={() => {
                                setShowShortageModal(false);
                                setShortageData(null);
                                setSelectedMissingItems({});
                            }}
                            onAddStock={handleConfirmNegativeStock}

                            onRemoveFromRecipe={handleRemoveFromRecipe}
                            selectedItems={selectedMissingItems}
                            onToggleItem={handleToggleMissingItem}
                        />
                        
                        {/* TSR Validation Error Modal */}
                        <TSRValidationErrorModal
                            show={showTSRErrorModal}
                            data={tsrErrorData}
                            onClose={() => {
                                setShowTSRErrorModal(false);
                                setTSRErrorData(null);
                            }}
                        />
                        
                        {/* Processing Modal */}
                        {processing && (
                            <div style={{
                                position: 'fixed',
                                inset: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 9999,
                                padding: '10px'
                            }}>
                                <div style={{
                                    backgroundColor: 'white',
                                    borderRadius: '12px',
                                    padding: '40px',
                                    maxWidth: '400px',
                                    width: '90%',
                                    textAlign: 'center',
                                    boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                                }}>
                                    <div style={{
                                        width: '50px',
                                        height: '50px',
                                        border: '4px solid #f3f3f3',
                                        borderTop: '4px solid #000',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                        margin: '0 auto 20px'
                                    }}></div>
                                    
                                    <h3 style={{ margin: '0 0 10px 0', fontSize: '21px', color: '#333', whiteSpace: 'nowrap' }}>
                                        Processing Waste Record
                                    </h3>
                                    
                                    <p style={{
                                        fontSize: '14px',
                                        color: '#666',
                                        marginBottom: '20px',
                                        minHeight: '20px'
                                    }}>
                                        {processingMessage}
                                    </p>
                                    
                                    <div style={{
                                        width: '100%',
                                        height: '8px',
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                        marginBottom: '10px'
                                    }}>
                                        <div style={{ 
                                            width: `${processingProgress}%`,
                                            height: '100%',
                                            backgroundColor: '#000',
                                            transition: 'width 0.3s ease'
                                        }}></div>
                                    </div>
                                    
                                    <p style={{ fontSize: '14px', fontWeight: '600', color: '#000', margin: 0 }}>
                                        {processingProgress.toFixed(0)}% Complete
                                    </p>
                                </div>
                            </div>
                        )}
                        
                        {/* Success Modal */}
                        {showSuccessModal && (
                            <div style={{
                                position: 'fixed',
                                inset: 0,
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 9999,
                                padding: '10px'
                            }}>
                                <div style={{
                                    width: 'min(380px, 90vw)',
                                    background: '#fff',
                                    borderRadius: 12,
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                    padding: '32px 24px',
                                    textAlign: 'center'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                                        <div style={{
                                            width: '60px',
                                            height: '60px',
                                            borderRadius: '50%',
                                            border: '3px solid #22c55e',
                                            backgroundColor: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <Check size={36} color="#22c55e" strokeWidth={3} />
                                        </div>
                                    </div>
                                    <div style={{
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        marginBottom: '16px',
                                        color: '#000000'
                                    }}>
                                        {/* T1.9 — the title told the user their waste was RECORDED even
                                            when all they had done was save a draft. */}
                                        {successModalKind === 'draft'
                                            ? (successMessage || 'Draft saved — not yet recorded')
                                            : (banquetOutcome ? banquetOutcome.message : 'Waste Recorded Successfully')}
                                    </div>
                                    {/* Banquet Wastage: the server's soft warnings (never blocking). */}
                                    {successModalKind !== 'draft' && banquetOutcome && banquetOutcome.warnings.length > 0 && (
                                        <ul
                                            aria-label="Banquet warnings"
                                            style={{
                                                textAlign: 'left', fontSize: '13px', lineHeight: 1.45, color: '#92400e',
                                                background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px',
                                                padding: '8px 12px 8px 28px', margin: '0 0 16px 0'
                                            }}
                                        >
                                            {banquetOutcome.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                        </ul>
                                    )}
                                    {successModalKind === 'draft' && (
                                        <div style={{
                                            fontSize: '13px',
                                            color: '#6b7280',
                                            marginBottom: '16px',
                                            lineHeight: 1.5
                                        }}>
                                            Nothing has been deducted from stock yet. Press Confirm on this
                                            screen to record it.
                                        </div>
                                    )}
                                    <button
                                        onClick={async () => {
                                            // T1.9 — a draft is not a recording. Ticking the daily wastage
                                            // checkpoint here marked the day's wastage duty done on the
                                            // strength of an unconfirmed draft, so the operations screen
                                            // stopped chasing wastage that was never recorded. The
                                            // checkpoint now belongs to the confirmed path only.
                                            if (successModalKind === 'draft') {
                                                setShowSuccessModal(false);
                                                return;
                                            }

                                            // Mark wastage checkpoint as complete in daily operations
                                            try {
                                                const token = localStorage.getItem('token');
                                                const businessId = localStorage.getItem('businessId');
                                                const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                                                
                                                // Check if this is "No wastage recorded" case
                                                const isNoWastage = wasteReason?.reason_label === "No wastage recorded";
                                                
                                                await fetch(`${API_URL}/operations/record-wastage`, {
                                                    method: 'POST',
                                                    headers: {
                                                        'Authorization': `Bearer ${token}`,
                                                        'x-business-id': businessId,
                                                        'Content-Type': 'application/json'
                                                    },
                                                    body: JSON.stringify({
                                                        isNoWastage: isNoWastage,
                                                        operation_date: operationDate || undefined
                                                    })
                                                }).catch((err) => console.warn('Record wastage checkpoint error:', err));
                                            } catch (err) {
                                                console.error('Failed to mark wastage checkpoint:', err);
                                            }
                                            
                                            setShowSuccessModal(false);
                                            
                                            if (recoveryDate && goTo) {
                                                console.log('🔄 Recovery mode: Navigating directly to TSR page after wastage...');
                                                goTo('todays-sales-report');
                                                return;
                                            }

                                            // Refresh plates after closing success modal
                                            await loadAvailablePlates().catch(err => {
                                                console.error('Error refreshing plates after modal close:', err);
                                            });
                                        }}
                                        style={{
                                            padding: '10px 24px',
                                            backgroundColor: '#000000',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        OK
                                    </button>
                                </div>
                            </div>
                        )}

                        {messageModal.open && (
                            <Modal
                                overlayClassName="stock-out-msg-overlay"
                                contentStyle={{
                                    width: 'calc(100vw - 64px)',
                                    maxWidth: '340px',
                                    minWidth: 'unset',
                                    minHeight: '220px',
                                    padding: '0',
                                    borderRadius: '16px',
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                }}
                                onClose={() => {
                                    const onOk = messageModal.onOk;
                                    closeMessageModal();
                                    if (typeof onOk === 'function') onOk();
                                }}
                            >
                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    gap: '16px',
                                    padding: '36px 28px 32px 28px',
                                }}>
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#111' }}>
                                        {messageModal.title || 'Notice'}
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '14px', color: '#555', lineHeight: 1.7 }}>
                                        {messageModal.message}
                                    </p>
                                    <button
                                        onClick={() => {
                                            const onOk = messageModal.onOk;
                                            closeMessageModal();
                                            if (typeof onOk === 'function') onOk();
                                        }}
                                        style={{
                                            marginTop: '6px',
                                            padding: '10px 0',
                                            backgroundColor: '#000',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '10px',
                                            fontSize: '15px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            width: '120px',
                                        }}
                                    >
                                        OK
                                    </button>
                                </div>
                            </Modal>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default StockOutForm;
